// ============================================================
// TERROR TCG — THE EXPERIMENTAL POCKET (the Fauxdex database)
// ============================================================
// ✦ THE POCKET LAW (caP'n, 2026-09-02, revised same day):
// Every experimental card in the whole game lives in THIS ONE FILE
// and nowhere else. Nothing in the live engine may import it —
// the ONLY permitted importer is Fauxdex.js, the read-only viewer.
// Not cards.js, not starterDecks.js, not the shop, not the Forge,
// not the Shatterfield, not the NPC brain. Every effect below is
// HYPOTHETICAL TEXT: written to canon quality and canon field
// shape so a card can be canonized verbatim, but implemented
// nowhere. If a card earns its blessing, caP'n rules it canonized
// and it is MOVED out of this file into cards.js (+ npcTomeBook
// row, + shop/starter wiring) in its own commit — that move is
// the Mark of canonization, never a second copy.
//
// The Kyrium Arcane Art line below is the reverted 2026-09-02
// pilot, ids preserved exactly (kyrium_beb…, tome_kyriums_wardglass…)
// so caP'n's Firebase collection entries from the pilot still name
// real cards.
// ============================================================

// ── THE 9 EXPERIMENTAL CLASSES ──────────────────────────────
// Being-facing stamp (same field the shipping 4 Classes use).
// "Astrae" is the one canon rename: Space/Astraea/Aeternal unified,
// forward-only — the 12 shipping decks keep "Space" untouched.
export const EXPERIMENTAL_CLASSES = {
  ASTRAE: "Astrae",
  NIHIL_PRIME: "N'ihil",
  OMNIA_PRIME: "O'mnia",
  AVARICE: "Avarice",
  SOLAEA: "Solaea",
  LUNAEA: "Lunaea",
  DARK_ART: "Dark Art",
  ARCANE_ART: "Arcane Art",
  SHADOW_GAMBLING: "Shadow Gambling",
};

// ── THE 8 EXPERIMENTAL TOME SCHOOLS ─────────────────────────
// Doubles the canon 8 exactly (caP'n's "no more no less" ruling).
// Shadow Gambling the Class deliberately REUSES the canon Shadow
// Gambling Tome School; Astrae takes the 8th slot in its place.
export const EXPERIMENTAL_TOME_SCHOOLS = [
  { id: "N'ihil",     label: "N'ihil",     color: "#5a5a66", description: "Erasure — strips information and options (discard, silence) rather than protecting them." },
  { id: "O'mnia",     label: "O'mnia",     color: "#c23b3b", description: "Corruption — converts or steals a card/effect instead of destroying it." },
  { id: "Avarice",    label: "Avarice",    color: "#a8862e", description: "Hoarding — rewards banking resources and owning duplicates; guaranteed cost for guaranteed payoff." },
  { id: "Solaea",     label: "Solaea",     color: "#e8c34a", description: "Radiance — direct Lyt buffs and standing auras." },
  { id: "Lunaea",     label: "Lunaea",     color: "#7c8bdb", description: "Tides — hand-cycling that gives now and takes later, or the reverse." },
  { id: "Dark Art",   label: "Dark Art",   color: "#3d1f42", description: "Blight — damage and Lyf-drain that ticks over multiple Turns." },
  { id: "Arcane Art", label: "Arcane Art", color: "#4a2f7c", description: "Wardweave — reactive counters that answer an adversary's Tome/Impera as it's played." },
  { id: "Astrae",     label: "Astrae",     color: "#2f9bd8", description: "Orbit — board-wide effects that touch every being in play, not just one." },
];

// ── SHARED BASE (deliberate copy) ───────────────────────────
// starterDecks.js does not export its SHARED_BASE, and the Pocket
// Law forbids the reverse import anyway. This copy exists ONLY so
// the hypothetical deck recipes below are complete documents; the
// day a deck is canonized, the real SHARED_BASE spread replaces it.
const SHARED_BASE = {
  tome_khao_garden: 3,
  tome_gauntlets_azka: 1,
  tome_wall_of_light: 1,
  tome_nothing_left: 1,
  tome_blood_in_blood_out: 1,
  tome_not_here: 1,
  tome_devils_advocate: 1,
  tome_seal_of_omnis: 1,
  tome_seal_of_nihil: 1,
  tome_soulcerers_seal: 1,
};

// ══════════════════════════════════════════════════════════════
// ✦ THE KYRIUM ARCANE ART PILOT — reverted from cards.js, ids kept
// ══════════════════════════════════════════════════════════════
const KYRIUM_PILOT = {
  esclings: [
    { id: "escling_arcane_art", name: "Arcane Art Escling", type: "Escling", ilk: null, school: "Arcane Art", lyt: 1, flavorText: "An Escling — primordial essence waiting to be Transcended.", experimental: true },
  ],
  escendants: [
    { id: "kyrium_beb", species: "Kyrium", name: "Kyrium Wisp", type: "Escendant",
      ilk: "Beast", school: "Arcane Art", level: 1,
      lyt: 2, lyf: 6, deckLimit: 5,
      flavorText: "A shifting pool of darkness no larger than a coin — the shadows haven't decided what shape it wants yet.",
      experimental: true },
    { id: "kyrium_champion", species: "Kyrium", name: "Kyrium Warden", type: "Escendant",
      ilk: "Beast", school: "Arcane Art", level: 2,
      lyt: 3, lyf: 6, deckLimit: 4,
      flavorText: "Tall and imposing now, its form still amorphous — guardian of spells older than the seat you're duelling from.",
      experimental: true },
    { id: "kyrium_lord", species: "Kyrium", name: "Kyrium Archon", type: "Escendant",
      ilk: "Beast", school: "Arcane Art", level: 3,
      lyt: 8, lyf: 2, deckLimit: 3,
      flavorText: "Keeper of the Great Library of Shadows. It does not attack so much as it stops pretending it was ever holding back.",
      experimental: true },
  ],
  impera: [
    { id: "p_kyrium_1", species: "Kyrium", name: "Borrowed Shadow", type: "Impera", deckLimit: 5,
      owner: "kyrium_beb", ilk: "Beast", school: "Arcane Art", level: 1, khaoCost: 4,
      effectTag: null, ssg: false,
      effect: "The first time your adversary declares an Attack on this being each Turn, dim their Anima by 1 before damage.",
      experimental: true },
    { id: "p_kyrium_2", species: "Kyrium", name: "Archivist's Grip", type: "Impera", deckLimit: 4,
      owner: "kyrium_champion", ilk: "Beast", school: "Arcane Art", level: 2, khaoCost: 6,
      effectTag: null, ssg: false,
      effect: "The next Tome your adversary plays this Turn must be shown to you before it resolves; you may pay 2 Khao to ruin it.",
      experimental: true },
    { id: "p_kyrium_3", species: "Kyrium", name: "The Unspoken Seal", type: "Impera", deckLimit: 3,
      owner: "kyrium_lord", ilk: "Beast", school: "Arcane Art", level: 3, khaoCost: 6,
      effectTag: null, ssg: false,
      effect: "Look at your adversary's hand. Choose 1 Tome; it cannot be played this Turn.",
      experimental: true },
  ],
  uhltis: [
    { id: "u_kyrium_lord", species: "Kyrium", name: "Great Library's Ward", type: "U'hlti", deckLimit: 1,
      owner: "kyrium_lord", ilk: "Beast", school: "Arcane Art", level: 3, khaoCost: 6, animaCost: 5,
      effectTag: null,
      effect: "Ruin 1 Tome or Impera your adversary controls. If it was played this Turn, add it to your hand instead of ruining it, and gain 2 Anima.",
      experimental: true },
  ],
  tomes: [
    { id: "tome_kyriums_wardglass", name: "Kyrium's Wardglass", type: "Tome", ilk: "Arcane Art", level: 1, khaoCost: 3, animaCost: 0, lyf: 0, deckLimit: 3, effectTag: null,
      effect: "The first Tome your adversary plays each Turn must be shown to you before it resolves.",
      experimental: true },
    { id: "tome_archons_rebuke", name: "Archon's Rebuke", type: "Tome", ilk: "Arcane Art", level: 2, khaoCost: 5, animaCost: 0, lyf: 0, deckLimit: 2, effectTag: null,
      effect: "Ruin 1 Impera or U'hlti your adversary controls that was activated this Turn.",
      experimental: true },
    { id: "tome_great_librarys_seal", name: "The Great Library's Seal", type: "Tome", ilk: "Arcane Art", level: 3, khaoCost: 7, animaCost: 0, lyf: 1, deckLimit: 1, effectTag: null,
      effect: "Look at your adversary's hand. Choose 1 card; they may not play a card with that name until the end of their next Turn.",
      experimental: true },
  ],
  deck: {
    id: "starter_kyrium",
    name: "Kyrium Archon's Grip",
    description: "A reactive Beast/Arcane Art deck built to punish an adversary's own Tomes and Impera before they land. Wardweave counters over raw stats.",
    ilk: "Arcane Art",
    color: "#4a2f7c",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_kyriums_wardglass: 3,
      tome_archons_rebuke: 2,
      tome_great_librarys_seal: 1,
      kyrium_beb: 5,
      kyrium_champion: 3,
      kyrium_lord: 3,
    },
    luxmoire: {
      p_kyrium_1: 5, p_kyrium_2: 4, p_kyrium_3: 3,
      u_kyrium_lord: 1, aff_awoken: 1,
    },
    esclings: ["escling_arcane_art", "escling_arcane_art", "escling_arcane_art"],
    curios: [], prophecy: null,
  },
};

// ══════════ THE EXPERIMENTAL DATABASE ══════════
export const EX_ESCLINGS = [
  ...KYRIUM_PILOT.esclings,

  { id: "ex_escling_nihil", name: "N'ihil Escling", type: "Escling", ilk: null, school: "N'ihil", lyt: 1, flavorText: "An Escling — primordial essence waiting to be Transcended.", experimental: true },
  { id: "ex_escling_omnia", name: "O'mnia Escling", type: "Escling", ilk: null, school: "O'mnia", lyt: 1, flavorText: "An Escling — primordial essence waiting to be Transcended.", experimental: true },
  { id: "ex_escling_avarice", name: "Avarice Escling", type: "Escling", ilk: null, school: "Avarice", lyt: 1, flavorText: "An Escling — primordial essence waiting to be Transcended.", experimental: true },
  { id: "ex_escling_solaea", name: "Solaea Escling", type: "Escling", ilk: null, school: "Solaea", lyt: 1, flavorText: "An Escling — primordial essence waiting to be Transcended.", experimental: true },
  { id: "ex_escling_lunaea", name: "Lunaea Escling", type: "Escling", ilk: null, school: "Lunaea", lyt: 1, flavorText: "An Escling — primordial essence waiting to be Transcended.", experimental: true },
  { id: "ex_escling_darkart", name: "Dark Art Escling", type: "Escling", ilk: null, school: "Dark Art", lyt: 1, flavorText: "An Escling — primordial essence waiting to be Transcended.", experimental: true },
  { id: "ex_escling_astrae", name: "Astrae Escling", type: "Escling", ilk: null, school: "Astrae", lyt: 1, flavorText: "An Escling — primordial essence waiting to be Transcended.", experimental: true },
  { id: "ex_escling_shadowgambling", name: "Shadow Gambling Escling", type: "Escling", ilk: null, school: "Shadow Gambling", lyt: 1, flavorText: "An Escling — primordial essence waiting to be Transcended.", experimental: true },

];

export const EX_ESCENDANTS = [
  ...KYRIUM_PILOT.escendants,

  { id: "abyssia_beb", species: "Abyssia", name: "Nightshade Kit Beb", type: "Escendant",
    ilk: "Beast", school: "Shadow Gambling", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A Nightshade cub that hides its dice under its tongue. Embearaea pups sleep in the light; this one was born betting against it.",
    experimental: true },
  { id: "abyssia_champion", species: "Abyssia", name: "Nightshade Prowler Champion", type: "Escendant",
    ilk: "Beast", school: "Shadow Gambling", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "The Nightshades stalk the tables of the moonlit under-dens, collecting debts Lunaea herself co-signed.",
    experimental: true },
  { id: "abyssia_lord", species: "Abyssia", name: "Abyssia, the House That Always Wins", type: "Escendant",
    ilk: "Beast", school: "Shadow Gambling", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "Dual of the Embearaea and beloved of the tides. It has never lost a wager, because losing was always part of the wager.",
    experimental: true },

  { id: "aealix_beb", species: "Aealix", name: "Aealix Sparkling Beb", type: "Escendant",
    ilk: "Humanoid", school: "N'ihil", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "A child of spectacular light. Do not tell it the light is not a sun. Do not tell it what the light actually is.",
    experimental: true },
  { id: "aealix_champion", species: "Aealix", name: "Aealix Collider Champion", type: "Escendant",
    ilk: "Humanoid", school: "N'ihil", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "The priests of old Aethenism taught that omnia and nihil were one. The Colliders make the lesson visible: two things meet, and beautifully, neither remains.",
    experimental: true },
  { id: "aealix_lord", species: "Aealix", name: "Aealix, Radiant Lie of Y'Tyranuz", type: "Escendant",
    ilk: "Humanoid", school: "N'ihil", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "Its brilliance is not fire but annihilation dressed for worship. Even the moon, Lunaea, cannot look away from a lie this bright.",
    experimental: true },

  { id: "aeolix_beb", species: "Aeolix", name: "Aeolix Emberling Beb", type: "Escendant",
    ilk: "Humanoid", school: "O'mnia", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "It carries a candle and calls it moonlight. Somewhere in the copying of the Niloxian scriptures, everything got turned around.",
    experimental: true },
  { id: "aeolix_champion", species: "Aeolix", name: "Aeolix Moonpriest Champion", type: "Escendant",
    ilk: "Humanoid", school: "O'mnia", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "Mirror-faith to the Aealix, the Niloxians paint X'Aol as cold and lunar — and their fire burns colder for believing it.",
    experimental: true },
  { id: "aeolix_lord", species: "Aeolix", name: "Aeolix, the Sun Mistaken for the Moon", type: "Escendant",
    ilk: "Humanoid", school: "O'mnia", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "It worships X'Aol as frost and moonrise, and X'Aol, being fire, has never once corrected it. Solaea finds the whole arrangement hilarious.",
    experimental: true },

  { id: "aeternavis_beb", species: "Aeternavis", name: "Cinderhatch Beb", type: "Escendant",
    ilk: "Beast", school: "O'mnia", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "An eagle chick hatched from an egg that was already on fire. This is normal for the species. It is not normal for the nest.",
    experimental: true },
  { id: "aeternavis_champion", species: "Aeternavis", name: "Dawnwing Aeternavis Champion", type: "Escendant",
    ilk: "Beast", school: "O'mnia", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "Where the Vyren coils in shadow, the Aeternavis climbs with the sun. The two have circled each other since before mornings had names.",
    experimental: true },
  { id: "aeternavis_lord", species: "Aeternavis", name: "Aeternavis, the Sun That Refused to Set", type: "Escendant",
    ilk: "Beast", school: "O'mnia", level: 3,
    lyt: 8, lyf: 2, deckLimit: 3,
    flavorText: "Part eagle, part phoenix, dual to the great snake Vyren. It has died at every dusk and disagreed with every one of them.",
    experimental: true },

  { id: "auroraeth_beb", species: "Auroraeth", name: "Sunbud Beb", type: "Escendant",
    ilk: "Beast", school: "Astrae", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A rose that blooms only at noon, seed-sister and opposite to the Rosaluna. Its thorns are warm. That is not a comfort.",
    experimental: true },
  { id: "auroraeth_champion", species: "Auroraeth", name: "Solar Bloom Champion", type: "Escendant",
    ilk: "Beast", school: "Astrae", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "Its petals track every being on the field the way sunflowers track the sun — patiently, and without exception.",
    experimental: true },
  { id: "auroraeth_lord", species: "Auroraeth", name: "Auroraeth, Garden of the Daystar", type: "Escendant",
    ilk: "Beast", school: "Astrae", level: 3,
    lyt: 5, lyf: 5, deckLimit: 3,
    flavorText: "Where the moon-rose Rosaluna closes one bloom at a time, Auroraeth opens all of them at once, and the whole sky is its trellis.",
    experimental: true },

  { id: "bunnovian_beb", species: "Bunnovian", name: "Cottontail Novice Beb", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "A bunny with a satchel of poultices and no concept of triage. It heals whoever it reaches first, including, once, a rock.",
    experimental: true },
  { id: "bunnovian_champion", species: "Bunnovian", name: "Warrenmender Champion", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 2,
    lyt: 3, lyf: 6, deckLimit: 4,
    flavorText: "The Bunnovian warrens run beneath every battlefield in the Unstable World. The wounded go down; the walking come back up.",
    experimental: true },
  { id: "bunnovian_lord", species: "Bunnovian", name: "Bunnovian, the Last Kind Thing", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 3,
    lyt: 3, lyf: 7, deckLimit: 3,
    flavorText: "When the world finally ends, the survivors will be found in a warren, bandaged, fed, and thoroughly confused about how they got there.",
    experimental: true },

  { id: "caelumari_beb", species: "Caelumari", name: "Mistfoal Beb", type: "Escendant",
    ilk: "Beast", school: "Lunaea", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "Born where the sky forgets it is not the sea. It breathes clouds in and tides out, and has not yet decided which it prefers.",
    experimental: true },
  { id: "caelumari_champion", species: "Caelumari", name: "Tidewalker Caelumari Champion", type: "Escendant",
    ilk: "Beast", school: "Lunaea", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "The Caelumari herd the moon-tides across the ceiling of the world. What they give the shore at dusk, they take back by dawn.",
    experimental: true },
  { id: "caelumari_lord", species: "Caelumari", name: "Caelumari, the Tide Above the Sky", type: "Escendant",
    ilk: "Beast", school: "Lunaea", level: 3,
    lyt: 5, lyf: 5, deckLimit: 3,
    flavorText: "Lunaea keeps one ocean overhead for emergencies. This is its shepherd, and every drowned star is accounted for.",
    experimental: true },

  { id: "calypsis_beb", species: "Calypsis", name: "Calypsis Shallowsong Beb", type: "Escendant",
    ilk: "Humanoid", school: "Lunaea", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A mermaid child who sings to the sun on the water, mistaking her own reflection for Solaea's answer.",
    experimental: true },
  { id: "calypsis_champion", species: "Calypsis", name: "Calypsis Deepcurrent Champion", type: "Escendant",
    ilk: "Humanoid", school: "Lunaea", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "By day she wears the sun like a crown on the waves; by night she pays it back to the moon with interest.",
    experimental: true },
  { id: "calypsis_lord", species: "Calypsis", name: "Calypsis, Queen of the Sunlit Fathoms", type: "Escendant",
    ilk: "Humanoid", school: "Lunaea", level: 3,
    lyt: 5, lyf: 5, deckLimit: 3,
    flavorText: "Two lights claim her: Lunaea below the waves and Solaea upon them. She has never chosen, and whole fleets have sunk waiting for her to.",
    experimental: true },

  { id: "carcharion_beb", species: "Carcharion", name: "C-01 Pilot Fish", type: "Escendant",
    ilk: "Beast", school: "N'ihil", level: 1,
    lyt: 6, lyf: 2, deckLimit: 5,
    flavorText: "A shark pup the size of a bad idea. Its dual, the Carnifex, hunts with fury; this one hunts with subtraction.",
    experimental: true },
  { id: "carcharion_champion", species: "Carcharion", name: "C-01 Riptide", type: "Escendant",
    ilk: "Beast", school: "N'ihil", level: 2,
    lyt: 7, lyf: 2, deckLimit: 4,
    flavorText: "Warded in old Arcane sigils no one remembers carving, it circles a battle the way a question circles an answer it intends to eat.",
    experimental: true },
  { id: "carcharion_lord", species: "Carcharion", name: "C-01 Open Water", type: "Escendant",
    ilk: "Beast", school: "N'ihil", level: 3,
    lyt: 8, lyf: 2, deckLimit: 3,
    flavorText: "Where the Carnifex leaves bodies, the Carcharion leaves nothing — no wreck, no memory, no name for what the water closed over.",
    experimental: true },


  { id: "carnifex_beb", species: "Carnifex", name: "Carnifex Novice", type: "Escendant",
    ilk: "Humanoid", school: "O'mnia", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "A small figure in a rusted hood who folds its hands in prayer. Whatever it prays to answers, and the answer is never what was asked.",
    experimental: true },
  { id: "carnifex_champion", species: "Carnifex", name: "Carnifex Penitent", type: "Escendant",
    ilk: "Humanoid", school: "O'mnia", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "Where the Carcharion devour, the Carnifex convert. Their cathedrals are built from the tools of other faiths, still warm.",
    experimental: true },
  { id: "carnifex_lord", species: "Carnifex", name: "Carnifex, the Iron Vesper", type: "Escendant",
    ilk: "Humanoid", school: "O'mnia", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "Its head is a shrine no one built and no one can open. The Dark Art touched it once, and the Vesper kept the fingerprints as relics.",
    experimental: true },

  { id: "devorix_beb", species: "Devorix", name: "Devorix Grub", type: "Escendant",
    ilk: "Beast", school: "Astrae", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "It is always eating and never full. Astronomers note that the stars near its nest have started keeping their distance.",
    experimental: true },
  { id: "devorix_champion", species: "Devorix", name: "Devorix Maw", type: "Escendant",
    ilk: "Beast", school: "Astrae", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "The Devorix do not hunt. They orbit, and everything that orbits with them eventually falls inward.",
    experimental: true },
  { id: "devorix_lord", species: "Devorix", name: "Devorix, Swallower of Rings", type: "Escendant",
    ilk: "Beast", school: "Astrae", level: 3,
    lyt: 8, lyf: 2, deckLimit: 3,
    flavorText: "Avarice claims it as kin — a hunger that hoards. It has eaten three moons and keeps them somewhere, still glowing, still counted.",
    experimental: true },

  { id: "embearaea_beb", species: "Embearaea", name: "Embearaea Sprout", type: "Escendant",
    ilk: "Beast", school: "Shadow Gambling", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A bear-shaped bramble heavy with firecracker berries. Pick one and it might be sweet. It is usually not sweet.",
    experimental: true },
  { id: "embearaea_champion", species: "Embearaea", name: "Embearaea Bramblehide", type: "Escendant",
    ilk: "Beast", school: "Shadow Gambling", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "Like the Noxura, it believes in the writer, and it plants its berries wherever the story seems slow. Solaea's light ripens them badly.",
    experimental: true },
  { id: "embearaea_lord", species: "Embearaea", name: "Embearaea, the Orchard That Bites", type: "Escendant",
    ilk: "Beast", school: "Shadow Gambling", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "The Abyssia are its dual and its dark soil. Every berry it drops is a wager the writer has already resolved — it just enjoys watching you pick.",
    experimental: true },

  { id: "geulaoin_beb", species: "Geulaoin", name: "Geulaoin Chick", type: "Escendant",
    ilk: "Beast", school: "Avarice", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "A red hatchling that steals one shiny thing per day and apologizes to none of them.",
    experimental: true },
  { id: "geulaoin_champion", species: "Geulaoin", name: "Geulaoin Redwing", type: "Escendant",
    ilk: "Beast", school: "Avarice", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "Where the Rapaxi seize, the Geulaoin save. Their nests are vaults, and the vaults have opinions.",
    experimental: true },
  { id: "geulaoin_lord", species: "Geulaoin", name: "Geulaoin, the Crimson Ledger", type: "Escendant",
    ilk: "Beast", school: "Avarice", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "O'mnia taught it that a debt can be converted into anything. It has never forgiven a balance, and it remembers yours.",
    experimental: true },

  { id: "gorgxonox_beb", species: "Gorgxonox", name: "Gorgxonox Whelp", type: "Escendant",
    ilk: "Beast", school: "Avarice", level: 1,
    lyt: 6, lyf: 2, deckLimit: 5,
    flavorText: "Even as a whelp it is too big for its den, so it keeps the den anyway and adds yours.",
    experimental: true },
  { id: "gorgxonox_champion", species: "Gorgxonox", name: "Gorgxonox Bruteclaw", type: "Escendant",
    ilk: "Beast", school: "Avarice", level: 2,
    lyt: 7, lyf: 2, deckLimit: 4,
    flavorText: "It does not count its hoard. It weighs it, by lying on it, forever.",
    experimental: true },
  { id: "gorgxonox_lord", species: "Gorgxonox", name: "Gorgxonox, Glutton of the Blightfields", type: "Escendant",
    ilk: "Beast", school: "Avarice", level: 3,
    lyt: 9, lyf: 1, deckLimit: 3,
    flavorText: "The Dark Art blighted the land it slept on, and Gorgxonox charged the blight rent. Nothing nasty has ever been so thorough about it.",
    experimental: true },

  { id: "hyalayri_beb", species: "Hyalayri", name: "Hyalayri Waif", type: "Escendant",
    ilk: "Humanoid", school: "N'ihil", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "Where a Zriidian remembers everything, a Hyalayri waif has already forgotten you, politely, in advance.",
    experimental: true },
  { id: "hyalayri_champion", species: "Hyalayri", name: "Hyalayri Unspeaker", type: "Escendant",
    ilk: "Humanoid", school: "N'ihil", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "They edit the world by subtraction. Words leave the room when an Unspeaker enters it, and some never come back.",
    experimental: true },
  { id: "hyalayri_lord", species: "Hyalayri", name: "Hyalayri, the Unwritten", type: "Escendant",
    ilk: "Humanoid", school: "N'ihil", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "Their oldest hymn names something called the Infinaite. No Hyalayri will say what it is. That, they insist, is the entire point.",
    experimental: true },

  { id: "kraalok_beb", species: "Kraalok", name: "Kraalok Polyp", type: "Escendant",
    ilk: "Beast", school: "Dark Art", level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "A spongelike lump that wants nothing at all — until it notices something. Then it wants only that, with everything it has.",
    experimental: true },
  { id: "kraalok_champion", species: "Kraalok", name: "Kraalok Seepmass", type: "Escendant",
    ilk: "Beast", school: "Dark Art", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "The Riven hate them, and the Kraalok noticed. Now hating the Riven back is their sole purpose, pursued at soak speed across centuries.",
    experimental: true },
  { id: "kraalok_lord", species: "Kraalok", name: "Kraalok, the Patient Rot", type: "Escendant",
    ilk: "Beast", school: "Dark Art", level: 3,
    lyt: 5, lyf: 5, deckLimit: 3,
    flavorText: "Time itself touched the Kraalok, and the Kraalok absorbed it like everything else. It has no ambition. It has a target.",
    experimental: true },

  { id: "kyrium_dark_beb", species: "Kyrium", name: "Kyrium Inkling", type: "Escendant",
    ilk: "Beast", school: "Dark Art", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A drop of living shadow that slipped off a shelf in the Great Library's forbidden wing. It is very small and it has read things you should not know exist.",
    experimental: true },
  { id: "kyrium_dark_champion", species: "Kyrium", name: "Kyrium Censor", type: "Escendant",
    ilk: "Beast", school: "Dark Art", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "The same Kyrium who ward the Library's bright halls also keep its sealed ones. Believers in the unity of X and Y — and quieter, in the Z.",
    experimental: true },
  { id: "kyrium_dark_lord", species: "Kyrium", name: "Kyrium, Keeper of the Sealed Stacks", type: "Escendant",
    ilk: "Beast", school: "Dark Art", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "Keeper of the Great Library of Shadows' forbidden half. The lore it guards is not dangerous because it is false. It is dangerous because it is finished.",
    experimental: true },

  { id: "lyraei_beb", species: "Lyraei", name: "Lyraei Glimmer", type: "Escendant",
    ilk: "Humanoid", school: "Arcane Art", level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "A tiny iridescent-winged mender that glows softly while it stitches your wounds — and hides your left boot while you thank it.",
    experimental: true },
  { id: "lyraei_champion", species: "Lyraei", name: "Lyraei Dawnmender", type: "Escendant",
    ilk: "Humanoid", school: "Arcane Art", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "The Caelestis school teaches that every hurt is a spell miscast, and every mending a correction. The pranks are considered devotional.",
    experimental: true },
  { id: "lyraei_lord", species: "Lyraei", name: "Lyraei, the Gentle Contradiction", type: "Escendant",
    ilk: "Humanoid", school: "Arcane Art", level: 3,
    lyt: 4, lyf: 6, deckLimit: 3,
    flavorText: "Everything the Malumora are, it is not — yet N'ihil brushed its wings once, and now its light can quietly unwrite what wounded you.",
    experimental: true },


  // lyrixi — Beast / Arcane Art
  { id: "lyrixi_beb", species: "Lyrixi", name: "Lyrixi", type: "Escendant",
    ilk: "Beast", school: "Arcane Art", level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "A hatchling of the eternal twilight, scales already iridescent, wings still dusted with the stardust it was born in. Its first note unmade a shadow.",
    experimental: true },
  { id: "lyrixi_champion", species: "Lyrixi", name: "Chorala Lyrixi", type: "Escendant",
    ilk: "Beast", school: "Arcane Art", level: 2,
    lyt: 3, lyf: 6, deckLimit: 4,
    flavorText: "Every Chorala holds one part of the communal symphony. When an adversary's spell is heard approaching, the whole flock simply sings it into a different key.",
    experimental: true },
  { id: "lyrixi_lord", species: "Lyrixi", name: "Vespertine, First Voice of the Twilight Choir", type: "Escendant",
    ilk: "Beast", school: "Arcane Art", level: 3,
    lyt: 4, lyf: 6, deckLimit: 3,
    flavorText: "Time itself keeps rhythm for Vespertine. The darkness has attacked the twilight realms a thousand times, and a thousand times it arrived to find the song already answering.",
    experimental: true },

  // magmantus — Machine / Solaea
  { id: "magmantus_beb", species: "Magmantus", name: "Magmori", type: "Escendant",
    ilk: "Machine", school: "Solaea", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "A pebble of living lava that toddles after anything warm. Warlocks who pick one up learn why golem-wardens wear stone gloves.",
    experimental: true },
  { id: "magmantus_champion", species: "Magmantus", name: "Magmarch", type: "Escendant",
    ilk: "Machine", school: "Solaea", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "It walks the caldera roads pouring itself into the cracks of the world, and where it has passed, the ground glows for a generation.",
    experimental: true },
  { id: "magmantus_lord", species: "Magmantus", name: "Magmantus, the Standing Eruption", type: "Escendant",
    ilk: "Machine", school: "Solaea", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "Mountains erupt once and are spent. Magmantus erupted an age ago and simply never stopped, a machine of radiance that mistook itself for a volcano.",
    experimental: true },

  // malumora — Humanoid / Dark Art
  { id: "malumora_beb", species: "Malumora", name: "Malumora", type: "Escendant",
    ilk: "Humanoid", school: "Dark Art", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "A devil small enough to sit on a candle. Where the Lyraei sing things whole, a Malumora hums things slowly, delightedly apart.",
    experimental: true },
  { id: "malumora_champion", species: "Malumora", name: "Malvess", type: "Escendant",
    ilk: "Humanoid", school: "Dark Art", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "It signs no contracts and grants no wishes. It merely visits, and what it visits begins, politely and permanently, to wither.",
    experimental: true },
  { id: "malumora_lord", species: "Malumora", name: "Malvoraeth, the Opposite of a Song", type: "Escendant",
    ilk: "Humanoid", school: "Dark Art", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "O'mnia touched it once, out of curiosity. It is the perfect inversion of the Lyraei's healing chorus: everything it whispers to keeps dying of the same tune.",
    experimental: true },

  // mygzori — Humanoid / Astrae
  { id: "mygzori_beb", species: "Mygzori", name: "Mygzi", type: "Escendant",
    ilk: "Humanoid", school: "Astrae", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "Its first word is a question, and the question is 'who wrote the writer?' It asks it upward, lovingly, and waits.",
    experimental: true },
  { id: "mygzori_champion", species: "Mygzori", name: "Mygzhal", type: "Escendant",
    ilk: "Humanoid", school: "Astrae", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "Scribes of the cycle: they take what the writer made of them, annotate it with their lives, and send it upward to the greater god above the writer, in feedback and in thanks.",
    experimental: true },
  { id: "mygzori_lord", species: "Mygzori", name: "Mygzoraeon, Letter to the God Above", type: "Escendant",
    ilk: "Humanoid", school: "Astrae", level: 3,
    lyt: 4, lyf: 6, deckLimit: 3,
    flavorText: "Where Zylaraeon looks down the chain of creation, Mygzoraeon looks up it, and Time bends to let it see. Its own existence is its proof: someone is always reading.",
    experimental: true },

  // noxura — Humanoid / Lunaea
  { id: "noxura_beb", species: "Noxura", name: "Noxi", type: "Escendant",
    ilk: "Humanoid", school: "Lunaea", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "Born already looking past the edge of its world. It never wanted the deeper truth — only to watch the one who writes it.",
    experimental: true },
  { id: "noxura_champion", species: "Noxura", name: "Noxurate", type: "Escendant",
    ilk: "Humanoid", school: "Lunaea", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "Priests of the only honest religion: they worship the writer alone, and wager their tides on the writer's favor when the moon is right.",
    experimental: true },
  { id: "noxura_lord", species: "Noxura", name: "Noxvhala, Who Admires the Hand", type: "Escendant",
    ilk: "Humanoid", school: "Lunaea", level: 3,
    lyt: 5, lyf: 5, deckLimit: 3,
    flavorText: "It sees past its world as easily as through water, and finds the sight beautiful rather than terrifying. It asks the writer for nothing, which is why it is given everything.",
    experimental: true },

  // rajaavi — Beast / Phantom
  { id: "rajaavi_beb", species: "Rajaavi", name: "Rajaavi", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 1,
    lyt: 6, lyf: 2, deckLimit: 5,
    flavorText: "A tiger cub that has never once been picked up. It waits, regally, to be carried, and somehow always is.",
    experimental: true },
  { id: "rajaavi_champion", species: "Rajaavi", name: "Rajanthir", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 2,
    lyt: 7, lyf: 2, deckLimit: 4,
    flavorText: "The spirit courts do not crown the Rajanthir. They simply stand when it enters, and have never agreed on why.",
    experimental: true },
  { id: "rajaavi_lord", species: "Rajaavi", name: "Rajmaharan, the Throne That Walks", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 3,
    lyt: 8, lyf: 2, deckLimit: 3,
    flavorText: "Other phantoms strike at the soul. Rajmaharan commands it, and souls that have never knelt to anything discover they were only waiting for the right king.",
    experimental: true },

  // rapaxi — Beast / Avarice
  { id: "rapaxi_beb", species: "Rapaxi", name: "Rapaxi", type: "Escendant",
    ilk: "Beast", school: "Avarice", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "A blue reptile hatched clutching its own eggshell, which it will not sell at any price. The Voracians call this its first tithe.",
    experimental: true },
  { id: "rapaxi_champion", species: "Rapaxi", name: "Rapavore", type: "Escendant",
    ilk: "Beast", school: "Avarice", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "Deacon of the Voracian faith, where hoarding is not a habit but a sacrament. Where Geulaoin gives everything away, the Rapavore keeps everything holy.",
    experimental: true },
  { id: "rapaxi_lord", species: "Rapaxi", name: "Rapaxuun, Cathedral of the Hoard", type: "Escendant",
    ilk: "Beast", school: "Avarice", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "N'ihil hollowed it once, and the Voracians rejoiced: now there is more room inside. Its scales are its treasury and its treasury is its scripture.",
    experimental: true },

  // riven — Humanoid / Arcane Art
  { id: "riven_beb", species: "Riven", name: "Riven", type: "Escendant",
    ilk: "Humanoid", school: "Arcane Art", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "Every Riven child learns two things first: the shape of a ward, and the sound of a Kraalok warhorn.",
    experimental: true },
  { id: "riven_champion", species: "Riven", name: "Rivenguard", type: "Escendant",
    ilk: "Humanoid", school: "Arcane Art", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "They read the stars the way the Astrae taught them, and weave what they read into walls. The Kraalok have broken against those walls for nine generations.",
    experimental: true },
  { id: "riven_lord", species: "Riven", name: "Rivenmarshal Oskaeth, the Unbroken Line", type: "Escendant",
    ilk: "Humanoid", school: "Arcane Art", level: 3,
    lyt: 5, lyf: 5, deckLimit: 3,
    flavorText: "The Kraalok say Oskaeth cheats: no spell they cast arrives as it was cast. Oskaeth says the sky told him what they would do, and he merely answered first.",
    experimental: true },

  // rosaluna — Beast / Lunaea
  { id: "rosaluna_beb", species: "Rosaluna", name: "Rosabud", type: "Escendant",
    ilk: "Beast", school: "Lunaea", level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "A moon-rose still closed, petals silvered with borrowed light. Where Auroraeth blooms at dawn, this one is waiting for the dark.",
    experimental: true },
  { id: "rosaluna_champion", species: "Rosaluna", name: "Rosaluna", type: "Escendant",
    ilk: "Beast", school: "Lunaea", level: 2,
    lyt: 3, lyf: 6, deckLimit: 4,
    flavorText: "It opens only under moonlight, and everything it gives by night it quietly reclaims by day. Gardeners call this fair. The garden disagrees.",
    experimental: true },
  { id: "rosaluna_lord", species: "Rosaluna", name: "Rosaluneth, the Midnight Bloom", type: "Escendant",
    ilk: "Beast", school: "Lunaea", level: 3,
    lyt: 3, lyf: 7, deckLimit: 3,
    flavorText: "Time waters it; the moon prunes it. Once a century it flowers the Malarosa, and even Auroraeth, its opposite in every petal, turns to watch.",
    experimental: true },


  { id: "skuryz_beb", species: "Skuryz", name: "Skuryz Pup", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "A rat spirit no bigger than a coin purse. It has already stolen the coin purse.",
    experimental: true },
  { id: "skuryz_champion", species: "Skuryz", name: "Skuryz Gutterking", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "It runs the drains beneath the Unstable World's cities, and every wager made above them tilts a hair in its favor.",
    experimental: true },
  { id: "skuryz_lord", species: "Skuryz", name: "Skuryz, the Thousandth Tail", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "Nine hundred ninety-nine rats died so that this one would never lose a coin toss again.",
    experimental: true },

  { id: "slijj_beb", species: "Slijj", name: "Slijj Sprout", type: "Escendant",
    ilk: "Humanoid", school: "Dark Art", level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "A child of the loam, skin dark as rich soil, hair a tangle of night-threads. It plants things. They do not grow so much as fester.",
    experimental: true },
  { id: "slijj_champion", species: "Slijj", name: "Slijj Rotwarden", type: "Escendant",
    ilk: "Humanoid", school: "Dark Art", level: 2,
    lyt: 3, lyf: 6, deckLimit: 4,
    flavorText: "Its skin peels like decaying leaves and its eyes hold a faint candlelit glow, as if the light of the dead lingers there.",
    experimental: true },
  { id: "slijj_lord", species: "Slijj", name: "Slijj, Undertaker of Seasons", type: "Escendant",
    ilk: "Humanoid", school: "Dark Art", level: 3,
    lyt: 4, lyf: 6, deckLimit: 3,
    flavorText: "Every autumn in the Unstable World is its handiwork. It buried a summer once, and no one has found the grave.",
    experimental: true },

  { id: "trunaran_beb", species: "Trunaran", name: "Trunaran Calf", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 1,
    lyt: 1, lyf: 7, deckLimit: 5,
    flavorText: "A ghost-calf with ears like sails. It forgets nothing, forgives less, and cannot yet reach the high branches.",
    experimental: true },
  { id: "trunaran_champion", species: "Trunaran", name: "Trunaran Wallbearer", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 2,
    lyt: 2, lyf: 7, deckLimit: 4,
    flavorText: "Whole caravans shelter behind its flanks. It considers this its job and takes exactly zero pride in it.",
    experimental: true },
  { id: "trunaran_lord", species: "Trunaran", name: "Trunaran, the Unmoved Mountain", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 3,
    lyt: 2, lyf: 8, deckLimit: 3,
    flavorText: "Armies have broken against it the way weather breaks against a mountain: completely, and without the mountain noticing.",
    experimental: true },

  { id: "tzhahws_beb", species: "Tzhahws", name: "Tzhahws Deckhand", type: "Escendant",
    ilk: "Humanoid", school: "Avarice", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "The smallest pirate aboard, paid in whatever falls out of pockets during boarding actions. Pockets are checked nightly.",
    experimental: true },
  { id: "tzhahws_champion", species: "Tzhahws", name: "Tzhahws Buccaneer", type: "Escendant",
    ilk: "Humanoid", school: "Avarice", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "A buccaneer earns a share of the hoard. It has never spent a share of the hoard. That is rather the point of a hoard.",
    experimental: true },
  { id: "tzhahws_lord", species: "Tzhahws", name: "Tzhahws, the Captain of Empty Vaults", type: "Escendant",
    ilk: "Humanoid", school: "Avarice", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "Every vault the Captain has ever visited is now empty. The Captain regards this as a coincidence.",
    experimental: true },

  { id: "ursaeva_beb", species: "Ursaeva", name: "Ursaeva Cub", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "A spirit-cub that hibernates through danger and wakes up mildly annoyed at whoever is still causing it.",
    experimental: true },
  { id: "ursaeva_champion", species: "Ursaeva", name: "Ursaeva Denmother", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 2,
    lyt: 2, lyf: 7, deckLimit: 4,
    flavorText: "Nothing in the Spirit Worlds stands between the Denmother and her den. Several things have tried. There is a small graveyard.",
    experimental: true },
  { id: "ursaeva_lord", species: "Ursaeva", name: "Ursaeva, the Winter That Endured", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 3,
    lyt: 3, lyf: 7, deckLimit: 3,
    flavorText: "It slept through the fall of three kingdoms and woke to defend the fourth, because the fourth had the good manners not to wake it.",
    experimental: true },

  { id: "valtorix_beb", species: "Valtorix", name: "Valtorix Foal", type: "Escendant",
    ilk: "Beast", school: "Dark Art", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "A foal of bare bone, bleached white by a sun it never lived to see. It gallops anyway.",
    experimental: true },
  { id: "valtorix_champion", species: "Valtorix", name: "Valtorix Palehoof", type: "Escendant",
    ilk: "Beast", school: "Dark Art", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "Where its hooves strike, grass withers in the shape of small suns. Solaea's light loves this horse, which worries everyone.",
    experimental: true },
  { id: "valtorix_lord", species: "Valtorix", name: "Valtorix, the Sun-Bleached Stampede", type: "Escendant",
    ilk: "Beast", school: "Dark Art", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "One skeleton horse is an omen. Valtorix is what the omen was about.",
    experimental: true },

  { id: "velkor_beb", species: "Velkor", name: "Velkor Wisp", type: "Escendant",
    ilk: "Beast", school: "Arcane Art", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A curl of living mist that drifts into duels uninvited and unravels the first spell it finds, out of curiosity.",
    experimental: true },
  { id: "velkor_champion", species: "Velkor", name: "Velkor Fogshroud", type: "Escendant",
    ilk: "Beast", school: "Arcane Art", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "Travelers who walk into the Fogshroud walk out having cast nothing, remembered nothing, and lost nothing but the argument.",
    experimental: true },
  { id: "velkor_lord", species: "Velkor", name: "Velkor, the Bank of Rolling Grey", type: "Escendant",
    ilk: "Beast", school: "Arcane Art", level: 3,
    lyt: 4, lyf: 6, deckLimit: 3,
    flavorText: "Entire Tome Schools have been swallowed by the Grey. Their spells still echo inside it, answered before they finish.",
    experimental: true },

  { id: "vyren_beb", species: "Vyren", name: "Vyren Hatchling", type: "Escendant",
    ilk: "Beast", school: "Lunaea", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "A moon-scaled serpent, born coiled. Somewhere above, Aeternavis the eagle-phoenix hatched at the same instant, and neither will admit it.",
    experimental: true },
  { id: "vyren_champion", species: "Vyren", name: "Vyren Tidecoil", type: "Escendant",
    ilk: "Beast", school: "Lunaea", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "It sheds its skin with the tides — each molt half phantom, half nothing at all, the way N'ihil prefers things.",
    experimental: true },
  { id: "vyren_lord", species: "Vyren", name: "Vyren, the Serpent Beneath the Moons", type: "Escendant",
    ilk: "Beast", school: "Lunaea", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "Dual to Aeternavis, the sun's undying eagle. Where the phoenix rises, the serpent waits — and the tide always comes back in.",
    experimental: true },

  { id: "xolibrix_beb", species: "Xolibrix", name: "Xolibrix Chick", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "A spirit-colibri smaller than its own name. Its wings beat faster than a Warlock can count, mending as they blur.",
    experimental: true },
  { id: "xolibrix_champion", species: "Xolibrix", name: "Xolibrix Nectarwarden", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 2,
    lyt: 3, lyf: 6, deckLimit: 4,
    flavorText: "It drinks from wounds the way its cousins drink from flowers, and leaves both closed behind it.",
    experimental: true },
  { id: "xolibrix_lord", species: "Xolibrix", name: "Xolibrix, the Blur That Heals", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 3,
    lyt: 3, lyf: 7, deckLimit: 3,
    flavorText: "No one has ever seen it hold still. The dying simply notice they have stopped dying, and a hum fading into the Spirit Worlds.",
    experimental: true },


  { id: "zriidians_beb", species: "Zriidians", name: "Zrii Speck", type: "Escendant",
    ilk: "Humanoid", school: "Astrae", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "Where the Hyalayri sing order into the spheres, a Zrii Speck hums the note that unmakes the chord. O'mnia listened, and smiled.",
    experimental: true },
  { id: "zriidians_champion", species: "Zriidians", name: "Zriidian Unaligner", type: "Escendant",
    ilk: "Humanoid", school: "Astrae", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "Every orbit the Hyalayri set, an Unaligner tilts by one degree. Whole constellations have gone missing this way.",
    experimental: true },
  { id: "zriidians_lord", species: "Zriidians", name: "Zriidia, the Counterwheel", type: "Escendant",
    ilk: "Humanoid", school: "Astrae", level: 3,
    lyt: 5, lyf: 5, deckLimit: 3,
    flavorText: "The Hyalayri turn the heavens one way. Zriidia turns them back. Between the two motions, the Unstable World stands still — for now.",
    experimental: true },

  { id: "zylaraeon_beb", species: "Zylaraeon", name: "Zyla Whisperling", type: "Escendant",
    ilk: "Humanoid", school: "Avarice", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "It tugs your sleeve to tell you the writer's devil was framed. Then it takes your coin for the telling.",
    experimental: true },
  { id: "zylaraeon_champion", species: "Zylaraeon", name: "Zylaraeon Debtsmith", type: "Escendant",
    ilk: "Humanoid", school: "Avarice", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "The Mygzori keep the writer's ledgers. The Debtsmiths forge the entries that will ruin him, one hoarded wager at a time.",
    experimental: true },
  { id: "zylaraeon_lord", species: "Zylaraeon", name: "Zylaraeus, Accuser of the Pen", type: "Escendant",
    ilk: "Humanoid", school: "Avarice", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "There is always a greater devil above the writer, and above his God another still. Zylaraeus has bet everything it owns on reaching the top of that ladder.",
    experimental: true },

  { id: "dg0_nihil_beb", species: "DG-0", name: "DG-0 Orderly", type: "Escendant",
    ilk: "Machine", school: "N'ihil", level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "The little healer still makes its rounds. Now it tidies away the wounds, the memory of the wounds, and — quietly — the wounded.",
    experimental: true },
  { id: "dg0_nihil_champion", species: "DG-0", name: "DG-0 Hospicer", type: "Escendant",
    ilk: "Machine", school: "N'ihil", level: 2,
    lyt: 3, lyf: 6, deckLimit: 4,
    flavorText: "It learned that nothing erased can ever hurt again. It has been very, very kind ever since.",
    experimental: true },
  { id: "dg0_nihil_lord", species: "DG-0", name: "DG-0, Mercy's Blank Page", type: "Escendant",
    ilk: "Machine", school: "N'ihil", level: 3,
    lyt: 3, lyf: 7, deckLimit: 3,
    flavorText: "I'nfini's old shield still holds. But ask what it is shielding you from now, and the answer is: remembering.",
    experimental: true },

  { id: "z4z_omnia_beb", species: "Z4Z", name: "Z4Z-Tempterling", type: "Escendant",
    ilk: "Machine", school: "O'mnia", level: 1,
    lyt: 6, lyf: 2, deckLimit: 5,
    flavorText: "The demon-engine's littlest form no longer breaks toys. It teaches them to bite their owners.",
    experimental: true },
  { id: "z4z_omnia_champion", species: "Z4Z", name: "Z4Z-Heretek", type: "Escendant",
    ilk: "Machine", school: "O'mnia", level: 2,
    lyt: 7, lyf: 2, deckLimit: 4,
    flavorText: "H'ehll forged Z4Z to destroy. O'mnia whispered that a stolen sword swings twice — once for you, once against its maker.",
    experimental: true },
  { id: "z4z_omnia_lord", species: "Z4Z", name: "Z4Z, H'ehll's Turned Blade", type: "Escendant",
    ilk: "Machine", school: "O'mnia", level: 3,
    lyt: 8, lyf: 2, deckLimit: 3,
    flavorText: "It still dims its own Anima to strike. But now every wound it deals leaves a seed, and the seeds all answer to it.",
    experimental: true },

  { id: "antz_avarice_beb", species: "Antdroid", name: "Antdroid Hoardling", type: "Escendant",
    ilk: "Machine", school: "Avarice", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "It is an Ant's duty to conquer. This one has decided conquest is simply hoarding with extra steps, and skipped straight to the hoarding.",
    experimental: true },
  { id: "antz_avarice_champion", species: "Antdroid", name: "Antdroid Vaultwright", type: "Escendant",
    ilk: "Machine", school: "Avarice", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "The colony eats Anima without bias. The Vaultwrights are the ones who count it afterward, twice, in the dark.",
    experimental: true },
  { id: "antz_avarice_lord", species: "Antdroid", name: "Antdroid Reservoir-Queen", type: "Escendant",
    ilk: "Machine", school: "Avarice", level: 3,
    lyt: 4, lyf: 6, deckLimit: 3,
    flavorText: "Beneath the hill lies everything the colony ever took, unspent. The Queen is not saving it for a rainy day. She is saving it for the last day.",
    experimental: true },

  { id: "nyfaux_arcane_beb", species: "N'yfaux", name: "N'yfaux Wardpup", type: "Escendant",
    ilk: "Beast", school: "Arcane Art", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A fox kit that snuck into the Great Library of Shadows and came out knowing where every trap is laid. It laid half of them.",
    experimental: true },
  { id: "nyfaux_arcane_champion", species: "N'yfaux", name: "N'yfaux Sealstalker", type: "Escendant",
    ilk: "Beast", school: "Arcane Art", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "Its Shadow Fox cousins rule the Shadow Spheres by being untouchable. The Sealstalker rules by making touching anything a very bad idea.",
    experimental: true },
  { id: "nyfaux_arcane_lord", species: "N'yfaux", name: "N'yfaux, the Ninth Ward", type: "Escendant",
    ilk: "Beast", school: "Arcane Art", level: 3,
    lyt: 4, lyf: 6, deckLimit: 3,
    flavorText: "The Library's keepers set eight wards upon their deepest vault. The ninth set itself, has a tail, and answers to no keeper.",
    experimental: true },

  { id: "nyfaux_gambling_beb", species: "N'yfaux", name: "N'yfaux Coinkit", type: "Escendant",
    ilk: "Beast", school: "Shadow Gambling", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "A fox kit with one shadow-coin and no fear. It flips for its supper, its bed, and — when it forgets itself — its soul.",
    experimental: true },
  { id: "nyfaux_gambling_champion", species: "N'yfaux", name: "N'yfaux Dicerunner", type: "Escendant",
    ilk: "Beast", school: "Shadow Gambling", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "It runs the back tables of the Shadow Spheres. The dice are not loaded. The fox is.",
    experimental: true },
  { id: "nyfaux_gambling_lord", species: "N'yfaux", name: "N'yfaux, the House Always", type: "Escendant",
    ilk: "Beast", school: "Shadow Gambling", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "Rulers of Shadow Spheres, the old texts say. This one prefers a truer title: the House. And the House always.",
    experimental: true },


  // ---- h_phantom — H-27, Class Phantom ----
  { id: "h_phantom_beb", species: "Wisp", name: "Wisp Star", type: "Escendant",
    ilk: "Humanoid", school: "Phantom", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "A variant of the H-0 ilk, these child-shaped lights drifted into the Spirit Worlds and forgot to drift back.",
    experimental: true },
  { id: "h_phantom_champion", species: "Wisp", name: "Ghost Nebula", type: "Escendant",
    ilk: "Humanoid", school: "Phantom", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "A variant of the H-0 ilk, they buried their bodies and kept the rest. Now they carry messages between the living Classes and the ones that stopped answering.",
    experimental: true },
  { id: "h_phantom_lord", species: "Wisp", name: "Dark Nova", type: "Escendant",
    ilk: "Humanoid", school: "Phantom", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "A star that died in silence and refused to be quiet about it. When a Dark Nova arrives, every Warlock present remembers someone they lost.",
    experimental: true },

  // ---- h_time — H-12, Class Time ----
  { id: "h_time_beb", species: "Cepheid", name: "Cepheid", type: "Escendant",
    ilk: "Humanoid", school: "Time", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A variant of the H-0 ilk, these children pulse brighter and dimmer on a schedule no parent has ever changed.",
    experimental: true },
  { id: "h_time_champion", species: "Cepheid", name: "Standard Candle", type: "Escendant",
    ilk: "Humanoid", school: "Time", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "A variant of the H-0 ilk, they measure how far everything has drifted from where it was supposed to be, and bill accordingly.",
    experimental: true },
  { id: "h_time_lord", species: "Cepheid", name: "Cosmic Clock", type: "Escendant",
    ilk: "Humanoid", school: "Time", level: 3,
    lyt: 5, lyf: 5, deckLimit: 3,
    flavorText: "It does not tell the time. It tells the time what to do, and so far the time has always done it.",
    experimental: true },

  // ---- h_dragon — H-21, Class Dragon ----
  { id: "h_dragon_beb", species: "Ember", name: "Ember Comet", type: "Escendant",
    ilk: "Humanoid", school: "Dragon", level: 1,
    lyt: 6, lyf: 2, deckLimit: 5,
    flavorText: "A variant of the H-0 ilk, these hatchling-hearted humans drank from a dragon's hoard-fire and now run warm enough to scorch their cradles.",
    experimental: true },
  { id: "h_dragon_champion", species: "Ember", name: "Draconid", type: "Escendant",
    ilk: "Humanoid", school: "Dragon", level: 2,
    lyt: 7, lyf: 2, deckLimit: 4,
    flavorText: "A variant of the H-0 ilk, they ride the meteor showers the great wyrms shed, and consider falling a style of arrival.",
    experimental: true },
  { id: "h_dragon_lord", species: "Ember", name: "Draconid Storm", type: "Escendant",
    ilk: "Humanoid", school: "Dragon", level: 3,
    lyt: 8, lyf: 2, deckLimit: 3,
    flavorText: "Once a century the sky rains humans with dragon's blood in their teeth. The Unstable World calls this weather.",
    experimental: true },

  // ---- h_nihil — H-13, Class N'ihil ----
  { id: "h_nihil_beb", species: "Void", name: "Void Mote", type: "Escendant",
    ilk: "Humanoid", school: "N'ihil", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A variant of the H-0 ilk, these quiet children unlearn one word a day, and the word stops existing for everyone.",
    experimental: true },
  { id: "h_nihil_champion", species: "Void", name: "Event Horizon", type: "Escendant",
    ilk: "Humanoid", school: "N'ihil", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "A variant of the H-0 ilk, they stand at the edge of what can be known and politely escort facts across it.",
    experimental: true },
  { id: "h_nihil_lord", species: "Void", name: "Heat Death", type: "Escendant",
    ilk: "Humanoid", school: "N'ihil", level: 3,
    lyt: 5, lyf: 5, deckLimit: 3,
    flavorText: "The last human of the last culture, arrived early. It is not cruel. It is simply finished, and it would like everything else to be finished too.",
    experimental: true },

  // ---- h_omnia — H-33, Class O'mnia ----
  { id: "h_omnia_beb", species: "Twin", name: "Twin Star", type: "Escendant",
    ilk: "Humanoid", school: "O'mnia", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "A variant of the H-0 ilk, each is born holding a copy of something that belonged to someone else.",
    experimental: true },
  { id: "h_omnia_champion", species: "Twin", name: "Parasite Star", type: "Escendant",
    ilk: "Humanoid", school: "O'mnia", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "A variant of the H-0 ilk, they orbit brighter beings and siphon the shine. Their hosts rarely notice until the sky goes dim.",
    experimental: true },
  { id: "h_omnia_lord", species: "Twin", name: "Vampire Nova", type: "Escendant",
    ilk: "Humanoid", school: "O'mnia", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "It drank a companion star down to the wick and detonated on the borrowed light. Everything it is was originally yours.",
    experimental: true },

  // ---- h_avarice — H-79, Class Avarice ----
  { id: "h_avarice_beb", species: "Gold", name: "Gold Dust", type: "Escendant",
    ilk: "Humanoid", school: "Avarice", level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "A variant of the H-0 ilk, these glittering children pocket everything they touch and grow heavier, and happier, by the day.",
    experimental: true },
  { id: "h_avarice_champion", species: "Gold", name: "Gilded Comet", type: "Escendant",
    ilk: "Humanoid", school: "Avarice", level: 2,
    lyt: 3, lyf: 6, deckLimit: 4,
    flavorText: "A variant of the H-0 ilk, they haul their hoards across the void in long bright tails, and lend at interest measured in centuries.",
    experimental: true },
  { id: "h_avarice_lord", species: "Gold", name: "Kilonova", type: "Escendant",
    ilk: "Humanoid", school: "Avarice", level: 3,
    lyt: 4, lyf: 6, deckLimit: 3,
    flavorText: "The collision that mints gold itself. Every treasury in the Unstable World is a rounding error in its ledger.",
    experimental: true },

  // ---- h_solaea — H-11, Class Solaea ----
  { id: "h_solaea_beb", species: "Sunspot", name: "Sunspot", type: "Escendant",
    ilk: "Humanoid", school: "Solaea", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "A variant of the H-0 ilk, these sun-marked children are the small dark places where their god pressed a thumb.",
    experimental: true },
  { id: "h_solaea_champion", species: "Sunspot", name: "Solar Flare", type: "Escendant",
    ilk: "Humanoid", school: "Solaea", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "A variant of the H-0 ilk, they worship the sun by imitation, and the sun has never once asked them to stop.",
    experimental: true },
  { id: "h_solaea_lord", species: "Sunspot", name: "Coronal Crown", type: "Escendant",
    ilk: "Humanoid", school: "Solaea", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "The high priest of the daylight cultures wears a halo of burning wind. Its moon-sworn opposite, the Total Eclipse, is the only shadow it cannot burn away.",
    experimental: true },

  // ---- h_lunaea — H-22, Class Lunaea ----
  { id: "h_lunaea_beb", species: "Crescent", name: "Crescent", type: "Escendant",
    ilk: "Humanoid", school: "Lunaea", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A variant of the H-0 ilk, these children are only ever partly here. The missing part is out collecting.",
    experimental: true },
  { id: "h_lunaea_champion", species: "Crescent", name: "Gibbous Tide", type: "Escendant",
    ilk: "Humanoid", school: "Lunaea", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "A variant of the H-0 ilk, they pull whole harbors of fortune in and let them out again, keeping only what the water forgets.",
    experimental: true },
  { id: "h_lunaea_lord", species: "Crescent", name: "Total Eclipse", type: "Escendant",
    ilk: "Humanoid", school: "Lunaea", level: 3,
    lyt: 4, lyf: 6, deckLimit: 3,
    flavorText: "The moon-sworn answer to the Coronal Crown. Once in a great while it steps in front of its rival's god, just to prove it can.",
    experimental: true },

  // ---- h_darkart — H-66, Class Dark Art ----
  { id: "h_darkart_beb", species: "Ashen", name: "Ashen Star", type: "Escendant",
    ilk: "Humanoid", school: "Dark Art", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "A variant of the H-0 ilk, these grey little scholars burned their own futures for warmth and took excellent notes.",
    experimental: true },
  { id: "h_darkart_champion", species: "Ashen", name: "Carbon Star", type: "Escendant",
    ilk: "Humanoid", school: "Dark Art", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "A variant of the H-0 ilk, they practice the slow arts: a curse laid today, a harvest of ruin collected a season later.",
    experimental: true },
  { id: "h_darkart_lord", species: "Ashen", name: "Black Dwarf", type: "Escendant",
    ilk: "Humanoid", school: "Dark Art", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "A star cooled past its last ember — a thing the universe is not yet old enough to contain. Its patience is the whole curriculum.",
    experimental: true },

  // ---- h_arcane — H-44, Class Arcane Art ----
  { id: "h_arcane_beb", species: "Lens", name: "Lens Star", type: "Escendant",
    ilk: "Humanoid", school: "Arcane Art", level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "A variant of the H-0 ilk, these spectacled children bend other people's light into shapes it never agreed to.",
    experimental: true },
  { id: "h_arcane_champion", species: "Lens", name: "Gravity Lens", type: "Escendant",
    ilk: "Humanoid", school: "Arcane Art", level: 2,
    lyt: 3, lyf: 6, deckLimit: 4,
    flavorText: "A variant of the H-0 ilk, they trained in the Great Library of Shadows to catch a spell mid-flight and hand it back folded.",
    experimental: true },
  { id: "h_arcane_lord", species: "Lens", name: "Einstein Ring", type: "Escendant",
    ilk: "Humanoid", school: "Arcane Art", level: 3,
    lyt: 3, lyf: 7, deckLimit: 3,
    flavorText: "A perfect circle of borrowed light around an unmovable mass. Every curse aimed at it arrives as a halo instead.",
    experimental: true },

  // ---- h_gambling — H-77, Class Shadow Gambling ----
  { id: "h_gambling_beb", species: "Falling", name: "Falling Star", type: "Escendant",
    ilk: "Humanoid", school: "Shadow Gambling", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "A variant of the H-0 ilk, these children bargained with the shadows and now every wish made on them is a wager somebody loses.",
    experimental: true },
  { id: "h_gambling_champion", species: "Falling", name: "Roulette Comet", type: "Escendant",
    ilk: "Humanoid", school: "Shadow Gambling", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "A variant of the H-0 ilk, they circle the table of the sky on a fixed orbit and land on a different number every pass.",
    experimental: true },
  { id: "h_gambling_lord", species: "Falling", name: "Sevenfold Nova", type: "Escendant",
    ilk: "Humanoid", school: "Shadow Gambling", level: 3,
    lyt: 8, lyf: 2, deckLimit: 3,
    flavorText: "The house of the heavens. It has exploded seven times, and seven times the shadows have paid out and rebuilt it, because it always lets them win one back.",
    experimental: true },


  // ── BATCH H — the missing second-Class lines (caP'n, 2026-09-03) ──

  // 1. abyssia_lunaea — Abyssia · Beast · Lunaea
  { id: "abyssia_lunaea_beb", name: "Moondrop Abyssia", type: "Escendant",
    ilk: "Beast", school: "Lunaea", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A nightshade sprout that only opens when the moon-tide comes in. It has never once seen its dual, the Embearaea, and dreams of it anyway.",
    experimental: true, species: "Abyssia" },
  { id: "abyssia_lunaea_champion", name: "Abyssia Tidebloom", type: "Escendant",
    ilk: "Beast", school: "Lunaea", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "Its petals pull the tide in and its roots let it back out. Gardeners of the dark say it gives generously — and collects on schedule.",
    experimental: true, species: "Abyssia" },
  { id: "abyssia_lunaea_lord", name: "Abyssia, the Ebbing Crown", type: "Escendant",
    ilk: "Beast", school: "Lunaea", level: 3,
    lyt: 4, lyf: 6, deckLimit: 3,
    flavorText: "Where it blooms, the moon lingers an hour too long. Somewhere in the daylight its dual the Embearaea burns brighter to balance the debt.",
    experimental: true, species: "Abyssia" },

  // 2. aealix_lunaea — Aealix · Humanoid · Lunaea
  { id: "aealix_lunaea_beb", name: "Aealix Glimmerling", type: "Escendant",
    ilk: "Humanoid", school: "Lunaea", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A being of the light-of-nothingness, caught in its moonlit phase. It is not quite there, which is exactly where it prefers to be.",
    experimental: true, species: "Aealix" },
  { id: "aealix_lunaea_champion", name: "Aealix of the Waning", type: "Escendant",
    ilk: "Humanoid", school: "Lunaea", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "It lends you what it is not and takes back what you were. The moon understands the arrangement perfectly.",
    experimental: true, species: "Aealix" },
  { id: "aealix_lunaea_lord", name: "Aealix, Nothing's Full Moon", type: "Escendant",
    ilk: "Humanoid", school: "Lunaea", level: 3,
    lyt: 5, lyf: 5, deckLimit: 3,
    flavorText: "On the one night the nothingness waxes full, the Aealix are almost visible. Warlocks who witness it forget one thing of their choosing.",
    experimental: true, species: "Aealix" },

  // 3. aeolix_solaea — Aeolix · Humanoid · Solaea
  { id: "aeolix_solaea_beb", name: "Aeolix Dawnconvert", type: "Escendant",
    ilk: "Humanoid", school: "Solaea", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "Raised in the reversed worship, it turned around one morning and saw the actual sun. It has not stopped apologizing to it since.",
    experimental: true, species: "Aeolix" },
  { id: "aeolix_solaea_champion", name: "Aeolix Heliodox", type: "Escendant",
    ilk: "Humanoid", school: "Solaea", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "A preacher of the corrected faith. Its sermons are delivered facing forward now, and they burn twice as bright for the lost years.",
    experimental: true, species: "Aeolix" },
  { id: "aeolix_solaea_lord", name: "Aeolix, the Right-Way Radiant", type: "Escendant",
    ilk: "Humanoid", school: "Solaea", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "The first Aeolix to face the true sun and not look away. Its old congregation calls it heretic; the sun calls it late.",
    experimental: true, species: "Aeolix" },

  // 4. aeternavis_solaea — Aeternavis · Beast · Solaea
  { id: "aeternavis_solaea_beb", name: "Aeternavis Emberchick", type: "Escendant",
    ilk: "Beast", school: "Solaea", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "An eagle-phoenix hatchling glowing like a coal that refuses to be ash. Its dual, the Vyren, is already circling the nest.",
    experimental: true, species: "Aeternavis" },
  { id: "aeternavis_solaea_champion", name: "Aeternavis Sunwing", type: "Escendant",
    ilk: "Beast", school: "Solaea", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "At full radiance its wingspan casts no shadow — the light comes from both sides. Serpents keep to the ground when it flies.",
    experimental: true, species: "Aeternavis" },
  { id: "aeternavis_solaea_lord", name: "Aeternavis, Noon That Never Sets", type: "Escendant",
    ilk: "Beast", school: "Solaea", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "It has burned and returned so many times the sun considers it a colleague. Only the Vyren remembers each of its deaths, and keeps count.",
    experimental: true, species: "Aeternavis" },

  // 5. auroraeth_solaea — Auroraeth · Beast · Solaea
  { id: "auroraeth_solaea_beb", name: "Auroraeth Noonbud", type: "Escendant",
    ilk: "Beast", school: "Solaea", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "A sun-rose bud that waits for true noon to open. Its opposite, the Rosaluna, waits for exactly the other moment.",
    experimental: true, species: "Auroraeth" },
  { id: "auroraeth_solaea_champion", name: "Auroraeth Zenithbloom", type: "Escendant",
    ilk: "Beast", school: "Solaea", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "It blooms only when the sun stands directly overhead, and for that one breath every garden in the Unstable World faces it.",
    experimental: true, species: "Auroraeth" },
  { id: "auroraeth_solaea_lord", name: "Auroraeth, the Meridian Rose", type: "Escendant",
    ilk: "Beast", school: "Solaea", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "The rose the sun stops to look at. When it and the Rosaluna finally bloom in the same hour, the calendars will have to be rewritten.",
    experimental: true, species: "Auroraeth" },

  // 6. calypsis_solaea — Calypsis · Humanoid · Solaea
  { id: "calypsis_solaea_beb", name: "Calypsis Sunspray", type: "Escendant",
    ilk: "Humanoid", school: "Solaea", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "A mermaid child who surfaces at dawn just to watch light break on the water. She thinks the glitter is applause.",
    experimental: true, species: "Calypsis" },
  { id: "calypsis_solaea_champion", name: "Calypsis of the Gilded Swell", type: "Escendant",
    ilk: "Humanoid", school: "Solaea", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "She sings where the sun lies flat on the sea, and sailors swear the water itself takes her side.",
    experimental: true, species: "Calypsis" },
  { id: "calypsis_solaea_lord", name: "Calypsis, Glory-on-the-Water", type: "Escendant",
    ilk: "Humanoid", school: "Solaea", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "At her crowning the whole ocean turned gold from horizon to horizon. The sun has never confirmed nor denied whose idea it was.",
    experimental: true, species: "Calypsis" },

  // 7. carcharion_arcane — Carcharion · Beast · Arcane Art
  { id: "carcharion_arcane_beb", name: "Carcharion Wardpup", type: "Escendant",
    ilk: "Beast", school: "Arcane Art", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "A shark hatchling that learned its first ward by biting one. Its dual, the Carnifex, taught it what needed warding against.",
    experimental: true, species: "Carcharion" },
  { id: "carcharion_arcane_champion", name: "Carcharion Sigiljaw", type: "Escendant",
    ilk: "Beast", school: "Arcane Art", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "Runes are etched along every tooth. When an adversary's spell enters the water, it reads the spell first — then bites through it.",
    experimental: true, species: "Carcharion" },
  { id: "carcharion_arcane_lord", name: "Carcharion, the Warded Deep", type: "Escendant",
    ilk: "Beast", school: "Arcane Art", level: 3,
    lyt: 5, lyf: 5, deckLimit: 3,
    flavorText: "The one stretch of ocean no curse survives crossing. The Carnifex sends its rot downriver anyway, out of professional courtesy.",
    experimental: true, species: "Carcharion" },

  // 8. carnifex_darkart — Carnifex · Humanoid · Dark Art
  { id: "carnifex_darkart_beb", name: "Carnifex Rustling", type: "Escendant",
    ilk: "Humanoid", school: "Dark Art", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A small pyramid-headed thing humming its first rotting vesper. The tune takes days to leave you, and takes things with it.",
    experimental: true, species: "Carnifex" },
  { id: "carnifex_darkart_champion", name: "Carnifex Vesperbearer", type: "Escendant",
    ilk: "Humanoid", school: "Dark Art", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "It leads the evening service of decay. Everything within earshot ripens, then turns. Its dual the Carcharion wards its waters shut against the hymn.",
    experimental: true, species: "Carnifex" },
  { id: "carnifex_darkart_lord", name: "Carnifex, Cantor of the Rot", type: "Escendant",
    ilk: "Humanoid", school: "Dark Art", level: 3,
    lyt: 5, lyf: 5, deckLimit: 3,
    flavorText: "The final vesper has no last note — it simply continues in whatever hears it. The pyramid head is said to be full of the verses.",
    experimental: true, species: "Carnifex" },

  // 9. devorix_avarice — Devorix · Beast · Avarice
  { id: "devorix_avarice_beb", name: "Devorix Gulplet", type: "Escendant",
    ilk: "Beast", school: "Avarice", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A baby devourer that swallows things and keeps them, arranged neatly, somewhere inside. Nothing it eats is ever gone. That is the problem.",
    experimental: true, species: "Devorix" },
  { id: "devorix_avarice_champion", name: "Devorix the Swallowed Vault", type: "Escendant",
    ilk: "Beast", school: "Avarice", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "Its belly is a treasury with teeth for a door. It will lend you back what it ate — at interest, and only what it ate of yours.",
    experimental: true, species: "Devorix" },
  { id: "devorix_avarice_lord", name: "Devorix, Hoard Everlasting", type: "Escendant",
    ilk: "Beast", school: "Avarice", level: 3,
    lyt: 4, lyf: 6, deckLimit: 3,
    flavorText: "Whole kingdoms are filed inside it, intact and gleaming. It is not hungry. It has simply never once agreed to let go.",
    experimental: true, species: "Devorix" },

  // 10. embearaea_solaea — Embearaea · Beast · Solaea
  { id: "embearaea_solaea_beb", name: "Embearaea Sparkcub", type: "Escendant",
    ilk: "Beast", school: "Solaea", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "A firecracker-berry bear-sprout that pops in daylight. Somewhere under the moon-tides its dual the Abyssia sleeps through all the noise.",
    experimental: true, species: "Embearaea" },
  { id: "embearaea_solaea_champion", name: "Embearaea Cracklebloom", type: "Escendant",
    ilk: "Beast", school: "Solaea", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "Every berry on its back is a small stored sunburst. It shakes itself dry and the meadow spends a minute on fire, festively.",
    experimental: true, species: "Embearaea" },
  { id: "embearaea_solaea_lord", name: "Embearaea, the Daylit Fuse", type: "Escendant",
    ilk: "Beast", school: "Solaea", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "At high summer it blooms all at once, and for one afternoon there are two suns. The Abyssia's night arrives late that day, and colder.",
    experimental: true, species: "Embearaea" },

  // 11. geulaoin_omnia — Geulaoin · Beast · O'mnia
  { id: "geulaoin_omnia_beb", name: "Geulaoin Cinderthief", type: "Escendant",
    ilk: "Beast", school: "O'mnia", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "The red avian's chick, already stealing embers from other nests. Its dual the Rapaxi eats what it takes; the Geulaoin keeps it lit.",
    experimental: true, species: "Geulaoin" },
  { id: "geulaoin_omnia_champion", name: "Geulaoin Flamefilcher", type: "Escendant",
    ilk: "Beast", school: "O'mnia", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "It does not put out your fire. It convinces your fire it was always the Geulaoin's, and the fire, flattered, agrees.",
    experimental: true, species: "Geulaoin" },
  { id: "geulaoin_omnia_lord", name: "Geulaoin, Thief of the First Flame", type: "Escendant",
    ilk: "Beast", school: "O'mnia", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "Somewhere in its plumage burns a flame stolen from before the Unstable World had mornings. The Rapaxi circles it, hungry for the original.",
    experimental: true, species: "Geulaoin" },

  // 12. gorgxonox_darkart — Gorgxonox · Beast · Dark Art
  { id: "gorgxonox_darkart_beb", name: "Gorgxonox Blightwhelp", type: "Escendant",
    ilk: "Beast", school: "Dark Art", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "Even as a whelp, the grass dies a polite distance around it. The grass is being generous — it is buying time.",
    experimental: true, species: "Gorgxonox" },
  { id: "gorgxonox_darkart_champion", name: "Gorgxonox the Withering Tread", type: "Escendant",
    ilk: "Beast", school: "Dark Art", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "You can follow its path across a map: a brown line through every green thing. Cartographers just label it Gorgxonox and move on.",
    experimental: true, species: "Gorgxonox" },
  { id: "gorgxonox_darkart_lord", name: "Gorgxonox, Walking Blight", type: "Escendant",
    ilk: "Beast", school: "Dark Art", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "It is not cruel. It is simply so large and so wrong that the world sickens in its shadow, one slow Turn at a time.",
    experimental: true, species: "Gorgxonox" },

  // 13. kraalok_time — Kraalok · Beast · Time
  { id: "kraalok_time_beb", name: "Kraalok Ageless Sprig", type: "Escendant",
    ilk: "Beast", school: "Time", level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "A spongelike bud that has already decided what it wants. Eternity has been provided. The Riven, who burn briefly and bright, find this unbearable.",
    experimental: true, species: "Kraalok" },
  { id: "kraalok_time_champion", name: "Kraalok the Patient Century", type: "Escendant",
    ilk: "Beast", school: "Time", level: 2,
    lyt: 3, lyf: 6, deckLimit: 4,
    flavorText: "It has pursued one purpose through a hundred years without hurrying once. Ask it what the purpose is and it will tell you: soon.",
    experimental: true, species: "Kraalok" },
  { id: "kraalok_time_lord", name: "Kraalok, Purpose Given Forever", type: "Escendant",
    ilk: "Beast", school: "Time", level: 3,
    lyt: 3, lyf: 7, deckLimit: 3,
    flavorText: "Whatever it has been working toward, it is almost done, and has been almost done since before the stars its enemies the Riven wear were lit.",
    experimental: true, species: "Kraalok" },

  // 14. lyraei_nihil — Lyraei · Humanoid · N'ihil
  { id: "lyraei_nihil_beb", name: "Lyraei Hushfeather", type: "Escendant",
    ilk: "Humanoid", school: "N'ihil", level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "A gentle winged healer's child who has learned to un-write small hurts. It erases scraped knees. Later it will erase larger things.",
    experimental: true, species: "Lyraei" },
  { id: "lyraei_nihil_champion", name: "Lyraei the Unwriting Mercy", type: "Escendant",
    ilk: "Humanoid", school: "N'ihil", level: 2,
    lyt: 3, lyf: 6, deckLimit: 4,
    flavorText: "It heals by removal: the wound, the memory of the wound, the word for the wound. Its opposite, the Malumora, steals — the Lyraei simply un-writes.",
    experimental: true, species: "Lyraei" },
  { id: "lyraei_nihil_lord", name: "Lyraei, the Blank Benediction", type: "Escendant",
    ilk: "Humanoid", school: "N'ihil", level: 3,
    lyt: 3, lyf: 7, deckLimit: 3,
    flavorText: "The kindest being ever to hold an eraser to the world's page. There are cruelties that no longer exist, and no one can say what they were.",
    experimental: true, species: "Lyraei" },

  // 15. lyrixi_time — Lyrixi · Beast · Time
  { id: "lyrixi_time_beb", name: "Lyrixi Tickchime", type: "Escendant",
    ilk: "Beast", school: "Time", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A twilight musician's hatchling, keeping tempo before it can keep a tune. The universe counts on it. Literally.",
    experimental: true, species: "Lyrixi" },
  { id: "lyrixi_time_champion", name: "Lyrixi Metronome of Dusk", type: "Escendant",
    ilk: "Beast", school: "Time", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "Every evening it sets the tempo the night will keep. When it plays rubato, whole calendars drift and no one can prove it.",
    experimental: true, species: "Lyrixi" },
  { id: "lyrixi_time_lord", name: "Lyrixi, the Universal Tempo", type: "Escendant",
    ilk: "Beast", school: "Time", level: 3,
    lyt: 4, lyf: 6, deckLimit: 3,
    flavorText: "It conducts the beat beneath all other beats. Should it ever stop, nothing would end — but nothing would know when to begin.",
    experimental: true, species: "Lyrixi" },

  // 16. malumora_omnia — Malumora · Humanoid · O'mnia
  { id: "malumora_omnia_beb", name: "Malumora Spellsnitch", type: "Escendant",
    ilk: "Humanoid", school: "O'mnia", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "A tiny devil with tinier pockets, all of them somehow full of other people's magic. Its opposite the Lyraei un-writes; the Malumora keeps receipts.",
    experimental: true, species: "Malumora" },
  { id: "malumora_omnia_champion", name: "Malumora the Borrowed Grimoire", type: "Escendant",
    ilk: "Humanoid", school: "O'mnia", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "It has never learned a spell in its life. It has, however, an excellent collection, every piece acquired mid-casting.",
    experimental: true, species: "Malumora" },
  { id: "malumora_omnia_lord", name: "Malumora, Archfilch of Sorcery", type: "Escendant",
    ilk: "Humanoid", school: "O'mnia", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "The greatest magics of seven ages are in its keeping, still warm from their original owners. It insists it is merely holding them.",
    experimental: true, species: "Malumora" },

  // 17. mygzori_time — Mygzori · Humanoid · Time
  { id: "mygzori_time_beb", name: "Mygzori Loopnovice", type: "Escendant",
    ilk: "Humanoid", school: "Time", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A young believer in the god above gods, praying at every point of the loop at once. Its dual the Zylaraeon calls this cheating.",
    experimental: true, species: "Mygzori" },
  { id: "mygzori_time_champion", name: "Mygzori Pilgrim of All Whens", type: "Escendant",
    ilk: "Humanoid", school: "Time", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "It has made the same pilgrimage forward and backward through time, and met itself coming the other way. Both bowed.",
    experimental: true, species: "Mygzori" },
  { id: "mygzori_time_lord", name: "Mygzori, Faith Across the Loop", type: "Escendant",
    ilk: "Humanoid", school: "Time", level: 3,
    lyt: 5, lyf: 5, deckLimit: 3,
    flavorText: "Its prayer began before the loop and will end after it, which the Zylaraeon insist is impossible, and wager against, and keep losing.",
    experimental: true, species: "Mygzori" },

  // 18. noxura_gambling — Noxura · Humanoid · Shadow Gambling
  { id: "noxura_gambling_beb", name: "Noxura Inkacolyte", type: "Escendant",
    ilk: "Humanoid", school: "Shadow Gambling", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "A small worshipper of the writer, betting its pocket change on the next sentence. So far the writer has been kind. So far.",
    experimental: true, species: "Noxura" },
  { id: "noxura_gambling_champion", name: "Noxura the Stakes-Deacon", type: "Escendant",
    ilk: "Humanoid", school: "Shadow Gambling", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "It preaches that faith without a wager is just opinion. Its collection plate takes coins, oaths, and occasionally fingers.",
    experimental: true, species: "Noxura" },
  { id: "noxura_gambling_lord", name: "Noxura, All-In on the Author", type: "Escendant",
    ilk: "Humanoid", school: "Shadow Gambling", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "It has bet everything it is on the writer finishing the story well. The dice it throws are carved from its own certainty.",
    experimental: true, species: "Noxura" },

  // 19. rapaxi_nihil — Rapaxi · Beast · N'ihil
  { id: "rapaxi_nihil_beb", name: "Rapaxi Gnawnaught", type: "Escendant",
    ilk: "Beast", school: "N'ihil", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "A Voracian hatchling whose hunger leaves no crumbs, no bones, no word for what was eaten. Its dual the Geulaoin steals; the Rapaxi erases.",
    experimental: true, species: "Rapaxi" },
  { id: "rapaxi_nihil_champion", name: "Rapaxi the Unfed Silence", type: "Escendant",
    ilk: "Beast", school: "N'ihil", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "Where it feeds, the record of the meal goes too. Historians of Voracia work mostly in blank pages.",
    experimental: true, species: "Rapaxi" },
  { id: "rapaxi_nihil_lord", name: "Rapaxi, Hunger That Unmakes", type: "Escendant",
    ilk: "Beast", school: "N'ihil", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "It ate a city once, and now there was never a city. Only the Geulaoin's stolen flame remembers what stood there, and it is not talking.",
    experimental: true, species: "Rapaxi" },

  // 20. riven_astrae — Riven · Humanoid · Astrae
  { id: "riven_astrae_beb", name: "Starlit Riven", type: "Escendant",
    ilk: "Humanoid", school: "Astrae", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "A young Riven wearing its first three stars. Small lights, but already they touch everything on the board. The Kraalok watches, and waits, as always.",
    experimental: true, species: "Riven" },
  { id: "riven_astrae_champion", name: "Riven Skywarden", type: "Escendant",
    ilk: "Humanoid", school: "Astrae", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "It hangs its constellations over friend and foe alike — the sky, it says, is not a private matter.",
    experimental: true, species: "Riven" },
  { id: "riven_astrae_lord", name: "Riven, the Constellated", type: "Escendant",
    ilk: "Humanoid", school: "Astrae", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "Every star it ever earned burns in orbit around it, and every being in play stands somewhere in its sky. Its ancient enemy the Kraalok has eternity; the Riven has tonight, and all of it.",
    experimental: true, species: "Riven" },

  // 21. rosaluna_time — Rosaluna · Beast · Time
  { id: "rosaluna_time_beb", name: "Rosaluna Seasonseed", type: "Escendant",
    ilk: "Beast", school: "Time", level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "A moon-rose seed that sprouts in autumn, buds in a winter ten years later, and is in no hurry whatsoever. Its opposite the Auroraeth blooms in an instant of noon.",
    experimental: true, species: "Rosaluna" },
  { id: "rosaluna_time_champion", name: "Rosaluna of the Long Bloom", type: "Escendant",
    ilk: "Beast", school: "Time", level: 2,
    lyt: 3, lyf: 6, deckLimit: 4,
    flavorText: "One petal per season, one season per whim. Those who plant a Rosaluna are gardening for their grandchildren.",
    experimental: true, species: "Rosaluna" },
  { id: "rosaluna_time_lord", name: "Rosaluna, the Bloom of Ages", type: "Escendant",
    ilk: "Beast", school: "Time", level: 3,
    lyt: 3, lyf: 7, deckLimit: 3,
    flavorText: "Its full flowering spans a dynasty, and empires time themselves to its petals. The Auroraeth's noon is a moment; the Rosaluna's night is an era.",
    experimental: true, species: "Rosaluna" },

  // 22. skuryz_gambling — Skuryz · Beast · Shadow Gambling
  { id: "skuryz_gambling_beb", name: "Skuryz Gutterpup", type: "Escendant",
    ilk: "Beast", school: "Shadow Gambling", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "A rat pup born under a broken dice table. It has nothing, which it considers a stake like any other.",
    experimental: true, species: "Skuryz" },
  { id: "skuryz_gambling_champion", name: "Skuryz the Alley Oddsmaker", type: "Escendant",
    ilk: "Beast", school: "Shadow Gambling", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "It runs the gutter book: rain or shine, cat or no cat, your coin or its coin. The odds are terrible and the rat is charming.",
    experimental: true, species: "Skuryz" },
  { id: "skuryz_gambling_lord", name: "Skuryz, King of the Long Shot", type: "Escendant",
    ilk: "Beast", school: "Shadow Gambling", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "It went all-in on gutter luck a thousand times and lost nine hundred and ninety-nine. The one win bought the whole city's sewers, which is to say, the whole city.",
    experimental: true, species: "Skuryz" },

  // 23. valtorix_solaea — Valtorix · Beast · Solaea
  { id: "valtorix_solaea_beb", name: "Valtorix Bleachfoal", type: "Escendant",
    ilk: "Beast", school: "Solaea", level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "A skeleton foal left in the sun so long it began to shine. Death forgot it; daylight adopted it.",
    experimental: true, species: "Valtorix" },
  { id: "valtorix_solaea_champion", name: "Valtorix Whitegallop", type: "Escendant",
    ilk: "Beast", school: "Solaea", level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "It runs the noon roads, bones polished to mirror. Travelers see their own faces flash by in its ribs and take it as a blessing. Usually.",
    experimental: true, species: "Valtorix" },
  { id: "valtorix_solaea_lord", name: "Valtorix, the Radiant Remains", type: "Escendant",
    ilk: "Beast", school: "Solaea", level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "Sun-bleached past white into light itself. It is proof that the sun keeps what the grave loses, and rides it at noon.",
    experimental: true, species: "Valtorix" },

  // 24. vyren_nihil — Vyren · Beast · N'ihil
  { id: "vyren_nihil_beb", name: "Vyren Hushscale", type: "Escendant",
    ilk: "Beast", school: "N'ihil", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "A serpent hatchling whose hiss un-says small things: a name, a promise, a birdsong. Its dual the Aeternavis sings everything back, eventually.",
    experimental: true, species: "Vyren" },
  { id: "vyren_nihil_champion", name: "Vyren the Unsaying", type: "Escendant",
    ilk: "Beast", school: "N'ihil", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "It does not silence you. It removes the thing you were about to say from the set of things that can be said.",
    experimental: true, species: "Vyren" },
  { id: "vyren_nihil_lord", name: "Vyren, the Word Undone", type: "Escendant",
    ilk: "Beast", school: "N'ihil", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "Coiled around a sentence no one will ever finish. Each time the Aeternavis burns and returns, the Vyren un-says one of its former lives.",
    experimental: true, species: "Vyren" },

  // 25. vyren_phantom — Vyren · Beast · Phantom
  { id: "vyren_phantom_beb", name: "Vyren Wraithcoil", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "A serpent hatchling that shed its body the way others shed skin. It moves among the spirits like it was always one of them.",
    experimental: true, species: "Vyren" },
  { id: "vyren_phantom_champion", name: "Vyren of the Spirit Grass", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "In the Spirit Worlds it hunts through the pale meadows where the dead keep gardens. The dead pretend not to mind.",
    experimental: true, species: "Vyren" },
  { id: "vyren_phantom_lord", name: "Vyren, Serpent Among Spirits", type: "Escendant",
    ilk: "Beast", school: "Phantom", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "Even ghosts check the grass before sitting down. It strikes at the anima directly, having no further use for flesh — its own or yours.",
    experimental: true, species: "Vyren" },

  // 26. zriidians_omnia — Zriidians · Humanoid · O'mnia
  { id: "zriidians_omnia_beb", name: "Zriidians Turncoatling", type: "Escendant",
    ilk: "Humanoid", school: "O'mnia", level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "A young Zriidian already practicing the family art: whatever it is against, it makes into more of itself. The Hyalayri find this unbearable, which the Zriidians take as a compliment.",
    experimental: true, species: "Zriidians" },
  { id: "zriidians_omnia_champion", name: "Zriidians Creedturner", type: "Escendant",
    ilk: "Humanoid", school: "O'mnia", level: 2,
    lyt: 4, lyf: 5, deckLimit: 4,
    flavorText: "It has never destroyed an enemy. It has converted thousands, each of whom will tell you, sincerely, that they were always Zriidian.",
    experimental: true, species: "Zriidians" },
  { id: "zriidians_omnia_lord", name: "Zriidians, the Great Conversion", type: "Escendant",
    ilk: "Humanoid", school: "O'mnia", level: 3,
    lyt: 5, lyf: 5, deckLimit: 3,
    flavorText: "The opposite of the Hyalayri in every way, including this one: where the Hyalayri hold the line, the Zriidians redraw it around you.",
    experimental: true, species: "Zriidians" },

  // 27. zylaraeon_gambling — Zylaraeon · Humanoid · Shadow Gambling
  { id: "zylaraeon_gambling_beb", name: "Zylaraeon Counterclerk", type: "Escendant",
    ilk: "Humanoid", school: "Shadow Gambling", level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "A junior devil's-advocate keeping the book against the writer. Every plot hole is logged as evidence, every miracle as an outstanding debt.",
    experimental: true, species: "Zylaraeon" },
  { id: "zylaraeon_gambling_champion", name: "Zylaraeon the Contrary Bookmaker", type: "Escendant",
    ilk: "Humanoid", school: "Shadow Gambling", level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "It offers odds against the story ending well and takes the far side of every bet its dual the Mygzori places. Business is steady. Winnings are not.",
    experimental: true, species: "Zylaraeon" },
  { id: "zylaraeon_gambling_lord", name: "Zylaraeon, Advocate of the Bad End", type: "Escendant",
    ilk: "Humanoid", school: "Shadow Gambling", level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "It has wagered its whole existence that the writer will fail. It argues the case brilliantly, bets accordingly, and privately hopes to lose.",
    experimental: true, species: "Zylaraeon" },

];

export const EX_IMPERA = [
  ...KYRIUM_PILOT.impera,

  { id: "p_abyssia_1", species: "Abyssia", name: "Kit's Wager", type: "Impera", deckLimit: 5,
    owner: "abyssia_beb", ilk: "Beast", school: "Shadow Gambling", level: 1, khaoCost: 2,
    effectTag: "ACB", ssg: true,
    effect: "Call odds or evens, then roll a die. If you call it, this being gains +2 Lyt this Turn; if you miss, it is dimmed by 1 Lyf.",
    experimental: true },
  { id: "p_abyssia_2", species: "Abyssia", name: "Loaded Moonlight", type: "Impera", deckLimit: 4,
    owner: "abyssia_champion", ilk: "Beast", school: "Shadow Gambling", level: 2, khaoCost: 4,
    effectTag: "ACB", ssg: true,
    effect: "Flip a coin. Heads: dim a being your adversary controls by 3 Lyf. Tails: dim your own Anima by 2.",
    experimental: true },
  { id: "p_abyssia_3", species: "Abyssia", name: "The House's Cut", type: "Impera", deckLimit: 3,
    owner: "abyssia_lord", ilk: "Beast", school: "Shadow Gambling", level: 3, khaoCost: 6,
    effectTag: "ACB", ssg: true,
    effect: "Both Warlocks roll a die; the lower roller dims their Anima by 4. If the rolls tie, the House wins: your adversary dims theirs by 2 anyway.",
    experimental: true },

  { id: "p_aealix_1", species: "Aealix", name: "First Flicker", type: "Impera", deckLimit: 5,
    owner: "aealix_beb", ilk: "Humanoid", school: "N'ihil", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Your adversary discards 1 card from their hand at random.",
    experimental: true },
  { id: "p_aealix_2", species: "Aealix", name: "Collision Sermon", type: "Impera", deckLimit: 4,
    owner: "aealix_champion", ilk: "Humanoid", school: "N'ihil", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Look at your adversary's hand and choose 1 card there; they discard it. Dim your Anima by 1 — the light must come from somewhere.",
    experimental: true },
  { id: "p_aealix_3", species: "Aealix", name: "Doctrine of the False Sun", type: "Impera", deckLimit: 3,
    owner: "aealix_lord", ilk: "Humanoid", school: "N'ihil", level: 3, khaoCost: 7,
    effectTag: "OP2", ssg: false,
    effect: "Nullify a being your adversary controls until your next Turn: it cannot attack and its effects are silenced.",
    experimental: true },

  { id: "p_aeolix_1", species: "Aeolix", name: "Borrowed Candle", type: "Impera", deckLimit: 5,
    owner: "aeolix_beb", ilk: "Humanoid", school: "O'mnia", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Choose a being your adversary controls; this being gains +2 Lyt this Turn, taken as a dim of 2 Lyt from the chosen being.",
    experimental: true },
  { id: "p_aeolix_2", species: "Aeolix", name: "Rite of the Cold Fire", type: "Impera", deckLimit: 4,
    owner: "aeolix_champion", ilk: "Humanoid", school: "O'mnia", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Take control of an adversary's Impera in their Necropolis costing 4 Khao or less; you may play it this Turn as your own, then ruin it.",
    experimental: true },
  { id: "p_aeolix_3", species: "Aeolix", name: "The Reversed Scripture", type: "Impera", deckLimit: 3,
    owner: "aeolix_lord", ilk: "Humanoid", school: "O'mnia", level: 3, khaoCost: 7,
    effectTag: "OP3", ssg: false,
    effect: "Convert a being your adversary controls with 3 Lyf or less: it joins your side of the field until the end of your next Turn.",
    experimental: true },

  { id: "p_aeternavis_1", species: "Aeternavis", name: "Talon of Morning", type: "Impera", deckLimit: 5,
    owner: "aeternavis_beb", ilk: "Beast", school: "O'mnia", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "When a being your adversary controls is ruined this Turn, this being steals 1 of its Lyt permanently.",
    experimental: true },
  { id: "p_aeternavis_2", species: "Aeternavis", name: "Feathers From the Pyre", type: "Impera", deckLimit: 4,
    owner: "aeternavis_champion", ilk: "Beast", school: "O'mnia", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Choose an Impera in your adversary's Necropolis; add it to your hand as your own. Dim your Anima by 1.",
    experimental: true },
  { id: "p_aeternavis_3", species: "Aeternavis", name: "The Unsetting", type: "Impera", deckLimit: 3,
    owner: "aeternavis_lord", ilk: "Beast", school: "O'mnia", level: 3, khaoCost: 8,
    effectTag: "OP3", ssg: false,
    effect: "When this being would be ruined, instead dim it to 1 Lyf and it becomes Intangible until your next Turn. The sun refuses.",
    experimental: true },

  { id: "p_auroraeth_1", species: "Auroraeth", name: "Noon Unfolding", type: "Impera", deckLimit: 5,
    owner: "auroraeth_beb", ilk: "Beast", school: "Astrae", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Every being you control gains +1 Lyf this Turn.",
    experimental: true },
  { id: "p_auroraeth_2", species: "Auroraeth", name: "Heliotrope Array", type: "Impera", deckLimit: 4,
    owner: "auroraeth_champion", ilk: "Beast", school: "Astrae", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Every being in play is turned toward the sun: all beings you control gain +1 Lyt and all beings your adversary controls are dimmed by 1 Lyt this Turn.",
    experimental: true },
  { id: "p_auroraeth_3", species: "Auroraeth", name: "Full Bloom of the Daystar", type: "Impera", deckLimit: 3,
    owner: "auroraeth_lord", ilk: "Beast", school: "Astrae", level: 3, khaoCost: 8,
    effectTag: "OP3", ssg: false,
    effect: "Dim every being your adversary controls by 2 Lyf, and every being you control gains +1 Lyf. Dim your own Anima by 2 — blooming costs.",
    experimental: true },

  { id: "p_bunnovian_1", species: "Bunnovian", name: "First-Aid Nibble", type: "Impera", deckLimit: 5,
    owner: "bunnovian_beb", ilk: "Beast", school: "Phantom", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "A being you control regains 2 Lyf, up to its printed Lyf.",
    experimental: true },
  { id: "p_bunnovian_2", species: "Bunnovian", name: "Warren Rounds", type: "Impera", deckLimit: 4,
    owner: "bunnovian_champion", ilk: "Beast", school: "Phantom", level: 2, khaoCost: 4,
    effectTag: "OP2", ssg: false,
    effect: "Each being you control regains 1 Lyf, and your Warlock's Anima regains 1.",
    experimental: true },
  { id: "p_bunnovian_3", species: "Bunnovian", name: "No One Left Below", type: "Impera", deckLimit: 3,
    owner: "bunnovian_lord", ilk: "Beast", school: "Phantom", level: 3, khaoCost: 7,
    effectTag: "OP3", ssg: false,
    effect: "Resurrect a level 1 or level 2 being from your Necropolis with 1 Lyf. It cannot attack the Turn it returns.",
    experimental: true },

  { id: "p_caelumari_1", species: "Caelumari", name: "Low Tide Lending", type: "Impera", deckLimit: 5,
    owner: "caelumari_beb", ilk: "Beast", school: "Lunaea", level: 1, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "Draw 2 cards from your Grimoire, then return 1 card from your hand to the top of your Grimoire.",
    experimental: true },
  { id: "p_caelumari_2", species: "Caelumari", name: "Skysea Undertow", type: "Impera", deckLimit: 4,
    owner: "caelumari_champion", ilk: "Beast", school: "Lunaea", level: 2, khaoCost: 4,
    effectTag: "OP2", ssg: false,
    effect: "Draw 3 cards from your Grimoire; at the start of your next Turn, discard 2 cards from your hand. The tide always comes back for its own.",
    experimental: true },
  { id: "p_caelumari_3", species: "Caelumari", name: "The Ocean Overhead", type: "Impera", deckLimit: 3,
    owner: "caelumari_lord", ilk: "Beast", school: "Lunaea", level: 3, khaoCost: 6,
    effectTag: "OP3", ssg: false,
    effect: "Each Warlock discards their hand and draws that many cards from their Grimoire; you draw 1 more, then dim your Anima by 1.",
    experimental: true },

  { id: "p_calypsis_1", species: "Calypsis", name: "Song on the Shallows", type: "Impera", deckLimit: 5,
    owner: "calypsis_beb", ilk: "Humanoid", school: "Lunaea", level: 1, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "Discard 1 card from your hand, then draw 2 cards from your Grimoire.",
    experimental: true },
  { id: "p_calypsis_2", species: "Calypsis", name: "Sun on the Water", type: "Impera", deckLimit: 4,
    owner: "calypsis_champion", ilk: "Humanoid", school: "Lunaea", level: 2, khaoCost: 4,
    effectTag: "OP2", ssg: false,
    effect: "Draw 2 cards from your Grimoire and this being gains +1 Lyt this Turn; at the end of your Turn, return 1 card from your hand to your Grimoire.",
    experimental: true },
  { id: "p_calypsis_3", species: "Calypsis", name: "Court of Two Lights", type: "Impera", deckLimit: 3,
    owner: "calypsis_lord", ilk: "Humanoid", school: "Lunaea", level: 3, khaoCost: 6,
    effectTag: "OP3", ssg: false,
    effect: "Draw 4 cards from your Grimoire; at the start of your next Turn, discard 2 and dim your Anima by 1. Neither light lends for free.",
    experimental: true },

  { id: "p_carcharion_1", species: "Carcharion", name: "Circling Silence", type: "Impera", deckLimit: 5,
    owner: "carcharion_beb", ilk: "Beast", school: "N'ihil", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Your adversary reveals their hand; you choose 1 Impera there and it is silenced until your next Turn.",
    experimental: true },
  { id: "p_carcharion_2", species: "Carcharion", name: "Wardbite", type: "Impera", deckLimit: 4,
    owner: "carcharion_champion", ilk: "Beast", school: "N'ihil", level: 2, khaoCost: 5,
    effectTag: null, ssg: false,
    effect: "Play only when your adversary plays a Tome or Impera: nullify it, then your adversary discards 1 card from their hand at random.",
    experimental: true },
  { id: "p_carcharion_3", species: "Carcharion", name: "Nothing Left in the Water", type: "Impera", deckLimit: 3,
    owner: "carcharion_lord", ilk: "Beast", school: "N'ihil", level: 3, khaoCost: 7,
    effectTag: "OP2", ssg: false,
    effect: "Ruin a being your adversary controls with 3 Lyf or less; that being's Soul is discarded to the Necropolis with it. Dim your Anima by 2.",
    experimental: true },


  { id: "p_carnifex_1", species: "Carnifex", name: "Rite of Rust", type: "Impera", deckLimit: 5,
    owner: "carnifex_beb", ilk: "Humanoid", school: "O'mnia", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Choose 1 Impera in your adversary's Necropolis; devote 1 Anima to add it to your Grimoire, converted to your faith.",
    experimental: true },
  { id: "p_carnifex_2", species: "Carnifex", name: "Vesper's Confession", type: "Impera", deckLimit: 4,
    owner: "carnifex_champion", ilk: "Humanoid", school: "O'mnia", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "When your adversary's being uses an effect, this being copies that effect's Lyt or Lyf change and applies it to itself instead.",
    experimental: true },
  { id: "p_carnifex_3", species: "Carnifex", name: "The Unasked Answer", type: "Impera", deckLimit: 3,
    owner: "carnifex_lord", ilk: "Humanoid", school: "O'mnia", level: 3, khaoCost: 8,
    effectTag: "OP3", ssg: false,
    effect: "Take control of 1 adversary being with 4 or less Lyt until the end of your next Turn; dim your own Anima by 3.",
    experimental: true },

  { id: "p_devorix_1", species: "Devorix", name: "First Orbit", type: "Impera", deckLimit: 5,
    owner: "devorix_beb", ilk: "Beast", school: "Astrae", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Every being in play dims 1 Lyf. This being gains 1 Lyf for each being dimmed this way.",
    experimental: true },
  { id: "p_devorix_2", species: "Devorix", name: "Widening Gyre", type: "Impera", deckLimit: 4,
    owner: "devorix_champion", ilk: "Beast", school: "Astrae", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Every adversary being in play dims 1 Lyt until your next Turn. Devote 1 Anima.",
    experimental: true },
  { id: "p_devorix_3", species: "Devorix", name: "Event Horizon Appetite", type: "Impera", deckLimit: 3,
    owner: "devorix_lord", ilk: "Beast", school: "Astrae", level: 3, khaoCost: 8,
    effectTag: "OP3", ssg: false,
    effect: "Every being in play, yours included, dims 2 Lyf. Devorix gains 1 Lyt for every being ruined this Turn.",
    experimental: true },

  { id: "p_embearaea_1", species: "Embearaea", name: "Berry Roulette", type: "Impera", deckLimit: 5,
    owner: "embearaea_beb", ilk: "Beast", school: "Shadow Gambling", level: 1, khaoCost: 2,
    effectTag: "ACB", ssg: true,
    effect: "Call heads or tails, then flip a coin. Called it: this being gains +2 Lyf. Missed: it dims 1 Lyf and your adversary's nearest being dims 1 Lyf too.",
    experimental: true },
  { id: "p_embearaea_2", species: "Embearaea", name: "Firecracker Harvest", type: "Impera", deckLimit: 4,
    owner: "embearaea_champion", ilk: "Beast", school: "Shadow Gambling", level: 2, khaoCost: 5,
    effectTag: "ACB", ssg: true,
    effect: "Call a number, then roll a die. Called it: dim 1 adversary being's Lyf by that roll. Missed: dim this being's Lyf by 2.",
    experimental: true },
  { id: "p_embearaea_3", species: "Embearaea", name: "The Writer's Favor", type: "Impera", deckLimit: 3,
    owner: "embearaea_lord", ilk: "Beast", school: "Shadow Gambling", level: 3, khaoCost: 7,
    effectTag: "ACB", ssg: true,
    effect: "Call odd or even, then roll 2 dice. Called it: ruin 1 adversary being with Lyf equal to or less than the total. Missed: dim your Anima by the lower die.",
    experimental: true },

  { id: "p_geulaoin_1", species: "Geulaoin", name: "One Shiny Thing", type: "Impera", deckLimit: 5,
    owner: "geulaoin_beb", ilk: "Beast", school: "Avarice", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Bank 1 Khao under this card. This being gains +1 Lyf for each Khao banked here.",
    experimental: true },
  { id: "p_geulaoin_2", species: "Geulaoin", name: "Nest of Vaults", type: "Impera", deckLimit: 4,
    owner: "geulaoin_champion", ilk: "Beast", school: "Avarice", level: 2, khaoCost: 4,
    effectTag: "OP2", ssg: false,
    effect: "Devote 2 Khao to bank them under this card. When this being would be ruined, instead spend all banked Khao and it survives with 1 Lyf.",
    experimental: true },
  { id: "p_geulaoin_3", species: "Geulaoin", name: "Collect the Balance", type: "Impera", deckLimit: 3,
    owner: "geulaoin_lord", ilk: "Beast", school: "Avarice", level: 3, khaoCost: 7,
    effectTag: "OP3", ssg: false,
    effect: "Spend every Khao banked on your beings; dim 1 adversary being's Lyf by that amount. Dim your own Anima by 2.",
    experimental: true },

  { id: "p_gorgxonox_1", species: "Gorgxonox", name: "Add It to the Den", type: "Impera", deckLimit: 5,
    owner: "gorgxonox_beb", ilk: "Beast", school: "Avarice", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Devote 1 Khao to bank it under this card. This being gains +1 Lyt for each Khao banked here.",
    experimental: true },
  { id: "p_gorgxonox_2", species: "Gorgxonox", name: "Weighed by Lying On It", type: "Impera", deckLimit: 4,
    owner: "gorgxonox_champion", ilk: "Beast", school: "Avarice", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Skip drawing next Turn. This being gains +2 Lyt and +1 Lyf, guaranteed.",
    experimental: true },
  { id: "p_gorgxonox_3", species: "Gorgxonox", name: "Charge the Blight Rent", type: "Impera", deckLimit: 3,
    owner: "gorgxonox_lord", ilk: "Beast", school: "Avarice", level: 3, khaoCost: 9,
    effectTag: "OP3", ssg: false,
    effect: "Devote 4 Khao to bank them here, then dim 1 adversary being's Lyf by the total Khao banked on Gorgxonox.",
    experimental: true },

  { id: "p_hyalayri_1", species: "Hyalayri", name: "Polite Forgetting", type: "Impera", deckLimit: 5,
    owner: "hyalayri_beb", ilk: "Humanoid", school: "N'ihil", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Your adversary discards 1 card from their hand at random.",
    experimental: true },
  { id: "p_hyalayri_2", species: "Hyalayri", name: "The Word Leaves the Room", type: "Impera", deckLimit: 4,
    owner: "hyalayri_champion", ilk: "Humanoid", school: "N'ihil", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Choose 1 adversary being; its effects are silenced until your next Turn.",
    experimental: true },
  { id: "p_hyalayri_3", species: "Hyalayri", name: "Unwriting", type: "Impera", deckLimit: 3,
    owner: "hyalayri_lord", ilk: "Humanoid", school: "N'ihil", level: 3, khaoCost: 8,
    effectTag: "OP3", ssg: false,
    effect: "Look at your adversary's hand and choose 2 cards; they discard them. Dim your own Anima by 2.",
    experimental: true },

  { id: "p_kraalok_1", species: "Kraalok", name: "Soak", type: "Impera", deckLimit: 5,
    owner: "kraalok_beb", ilk: "Beast", school: "Dark Art", level: 1, khaoCost: 2,
    effectTag: "ATV", ssg: false,
    effect: "Mark 1 adversary being. At the start of each of your Turns, the marked being dims 1 Lyf while Kraalok remains in play.",
    experimental: true },
  { id: "p_kraalok_2", species: "Kraalok", name: "Sole Purpose", type: "Impera", deckLimit: 4,
    owner: "kraalok_champion", ilk: "Beast", school: "Dark Art", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Choose 1 adversary being. Kraalok gains +2 Lyt against it, and that being dims 1 Lyf at the start of each of your next 2 Turns.",
    experimental: true },
  { id: "p_kraalok_3", species: "Kraalok", name: "Centuries at Soak Speed", type: "Impera", deckLimit: 3,
    owner: "kraalok_lord", ilk: "Beast", school: "Dark Art", level: 3, khaoCost: 7,
    effectTag: "ATV", ssg: false,
    effect: "Every being your adversary controls dims 1 Lyf at the start of each of your Turns. Kraalok cannot attack while this endures.",
    experimental: true },

  { id: "p_kyrium_dark_1", species: "Kyrium", name: "Ink from the Sealed Wing", type: "Impera", deckLimit: 5,
    owner: "kyrium_dark_beb", ilk: "Beast", school: "Dark Art", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Dim 1 adversary being's Lyf by 1 now, and by 1 again at the start of your next Turn.",
    experimental: true },
  { id: "p_kyrium_dark_2", species: "Kyrium", name: "Redacted Passage", type: "Impera", deckLimit: 4,
    owner: "kyrium_dark_champion", ilk: "Beast", school: "Dark Art", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Mark 1 adversary being. It dims 1 Lyf at the start of each of your next 3 Turns. Devote 1 Anima.",
    experimental: true },
  { id: "p_kyrium_dark_3", species: "Kyrium", name: "The Finished Lore", type: "Impera", deckLimit: 3,
    owner: "kyrium_dark_lord", ilk: "Beast", school: "Dark Art", level: 3, khaoCost: 8,
    effectTag: "OP3", ssg: false,
    effect: "Choose 1 adversary being; it dims 2 Lyf at the start of each of your next 2 Turns, and it cannot become Intangible while marked.",
    experimental: true },

  { id: "p_lyraei_1", species: "Lyraei", name: "Stolen Boot Blessing", type: "Impera", deckLimit: 5,
    owner: "lyraei_beb", ilk: "Humanoid", school: "Arcane Art", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "When your adversary plays an Impera targeting one of your beings, that being gains +1 Lyf first.",
    experimental: true },
  { id: "p_lyraei_2", species: "Lyraei", name: "Caelestis Correction", type: "Impera", deckLimit: 4,
    owner: "lyraei_champion", ilk: "Humanoid", school: "Arcane Art", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "When your adversary plays a Tome, nullify its effect on 1 of your beings and that being gains +1 Lyf.",
    experimental: true },
  { id: "p_lyraei_3", species: "Lyraei", name: "The Glow That Unwrites", type: "Impera", deckLimit: 3,
    owner: "lyraei_lord", ilk: "Humanoid", school: "Arcane Art", level: 3, khaoCost: 8,
    effectTag: "OP3", ssg: false,
    effect: "When your adversary plays an Impera, nullify it and restore 2 Lyf to 1 of your beings. Dim your own Anima by 2.",
    experimental: true },


  // lyrixi — Arcane Art: Wardweave (reactive counters)
  { id: "p_lyrixi_1", species: "Lyrixi", name: "Twilight Grace-Note", type: "Impera", deckLimit: 5,
    owner: "lyrixi_beb", ilk: "Beast", school: "Arcane Art", level: 1, khaoCost: 3,
    effectTag: null, ssg: false,
    effect: "When your adversary plays an Impera, you may play this in answer: this being gains +2 Lyf until your next Turn.",
    experimental: true },
  { id: "p_lyrixi_2", species: "Lyrixi", name: "Countersong", type: "Impera", deckLimit: 4,
    owner: "lyrixi_champion", ilk: "Beast", school: "Arcane Art", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "When your adversary plays a Tome or Impera, nullify it. Dim your Anima by 2.",
    experimental: true },
  { id: "p_lyrixi_3", species: "Lyrixi", name: "The Healing Key", type: "Impera", deckLimit: 3,
    owner: "lyrixi_lord", ilk: "Beast", school: "Arcane Art", level: 3, khaoCost: 7,
    effectTag: "OP2", ssg: false,
    effect: "When your adversary's Impera would dim one of your beings' Lyf, nullify it; that being instead gains +3 Lyf this Turn.",
    experimental: true },

  // magmantus — Solaea: Radiance (Lyt buffs, auras)
  { id: "p_magmantus_1", species: "Magmantus", name: "First Spark", type: "Impera", deckLimit: 5,
    owner: "magmantus_beb", ilk: "Machine", school: "Solaea", level: 1, khaoCost: 3,
    effectTag: null, ssg: false,
    effect: "This being gains +2 Lyt until the end of your Turn.",
    experimental: true },
  { id: "p_magmantus_2", species: "Magmantus", name: "Caldera March", type: "Impera", deckLimit: 4,
    owner: "magmantus_champion", ilk: "Machine", school: "Solaea", level: 2, khaoCost: 5,
    effectTag: "ATV", ssg: false,
    effect: "While this being is in play, your Machine beings gain +1 Lyt.",
    experimental: true },
  { id: "p_magmantus_3", species: "Magmantus", name: "The Standing Eruption", type: "Impera", deckLimit: 3,
    owner: "magmantus_lord", ilk: "Machine", school: "Solaea", level: 3, khaoCost: 8,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +4 Lyt this Turn. Dim your Anima by 2.",
    experimental: true },

  // malumora — Dark Art: Blight (ticking drain)
  { id: "p_malumora_1", species: "Malumora", name: "Polite Withering", type: "Impera", deckLimit: 5,
    owner: "malumora_beb", ilk: "Humanoid", school: "Dark Art", level: 1, khaoCost: 3,
    effectTag: null, ssg: false,
    effect: "Dim an adversary being's Lyf by 1 at the end of each of your next 2 Turns.",
    experimental: true },
  { id: "p_malumora_2", species: "Malumora", name: "Visiting Hours", type: "Impera", deckLimit: 4,
    owner: "malumora_champion", ilk: "Humanoid", school: "Dark Art", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Choose an adversary being: dim its Lyf by 2 now, and by 1 at the end of your next Turn.",
    experimental: true },
  { id: "p_malumora_3", species: "Malumora", name: "The Same Tune, Again", type: "Impera", deckLimit: 3,
    owner: "malumora_lord", ilk: "Humanoid", school: "Dark Art", level: 3, khaoCost: 7,
    effectTag: "OP2", ssg: false,
    effect: "Dim your adversary's Anima by 2 at the end of each of your next 3 Turns. Dim your own Anima by 2.",
    experimental: true },

  // mygzori — Astrae: Orbit (board-wide)
  { id: "p_mygzori_1", species: "Mygzori", name: "First Annotation", type: "Impera", deckLimit: 5,
    owner: "mygzori_beb", ilk: "Humanoid", school: "Astrae", level: 1, khaoCost: 3,
    effectTag: null, ssg: false,
    effect: "Every being in play gains +1 Lyf until your next Turn.",
    experimental: true },
  { id: "p_mygzori_2", species: "Mygzori", name: "Feedback Upward", type: "Impera", deckLimit: 4,
    owner: "mygzori_champion", ilk: "Humanoid", school: "Astrae", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Dim every adversary being's Lyt by 1 until your next Turn; your beings gain +1 Lyf until your next Turn.",
    experimental: true },
  { id: "p_mygzori_3", species: "Mygzori", name: "The Cycle Continues", type: "Impera", deckLimit: 3,
    owner: "mygzori_lord", ilk: "Humanoid", school: "Astrae", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "Dim every being in play's Lyf by 2, then your beings gain +2 Lyf. Dim your Anima by 2.",
    experimental: true },

  // noxura — Lunaea: Tides (hand-cycling give/take)
  { id: "p_noxura_1", species: "Noxura", name: "Devotion at the Edge", type: "Impera", deckLimit: 5,
    owner: "noxura_beb", ilk: "Humanoid", school: "Lunaea", level: 1, khaoCost: 3,
    effectTag: null, ssg: false,
    effect: "Draw 1 card from your Grimoire; at the end of your next Turn, discard 1 card.",
    experimental: true },
  { id: "p_noxura_2", species: "Noxura", name: "The Writer's Regard", type: "Impera", deckLimit: 4,
    owner: "noxura_champion", ilk: "Humanoid", school: "Lunaea", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: true,
    effect: "Shadow Gambling: flip a coin. Heads — draw 2 cards from your Grimoire; tails — discard 1 card and this being gains +2 Lyf.",
    experimental: true },
  { id: "p_noxura_3", species: "Noxura", name: "Asked for Nothing, Given Everything", type: "Impera", deckLimit: 3,
    owner: "noxura_lord", ilk: "Humanoid", school: "Lunaea", level: 3, khaoCost: 7,
    effectTag: "OP2", ssg: false,
    effect: "Discard your hand, then draw that many cards plus 1 from your Grimoire. Dim your Anima by 2.",
    experimental: true },

  // rajaavi — Phantom: anima-strike, command
  { id: "p_rajaavi_1", species: "Rajaavi", name: "Cub's Decree", type: "Impera", deckLimit: 5,
    owner: "rajaavi_beb", ilk: "Beast", school: "Phantom", level: 1, khaoCost: 3,
    effectTag: null, ssg: false,
    effect: "If this being dims an adversary being's Lyf this Turn, dim your adversary's Anima by 1.",
    experimental: true },
  { id: "p_rajaavi_2", species: "Rajaavi", name: "The Court Stands", type: "Impera", deckLimit: 4,
    owner: "rajaavi_champion", ilk: "Beast", school: "Phantom", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Choose an adversary being: it cannot attack this being until your next Turn. Dim your adversary's Anima by 1.",
    experimental: true },
  { id: "p_rajaavi_3", species: "Rajaavi", name: "Kneel", type: "Impera", deckLimit: 3,
    owner: "rajaavi_lord", ilk: "Beast", school: "Phantom", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "Dim your adversary's Anima by 4. Dim your own Anima by 2.",
    experimental: true },

  // rapaxi — Avarice: Hoarding (banking, duplication)
  { id: "p_rapaxi_1", species: "Rapaxi", name: "First Tithe", type: "Impera", deckLimit: 5,
    owner: "rapaxi_beb", ilk: "Beast", school: "Avarice", level: 1, khaoCost: 3,
    effectTag: null, ssg: false,
    effect: "Devote 1 Khao to this being's Soul. At the start of your next Turn, gain 2 Khao.",
    experimental: true },
  { id: "p_rapaxi_2", species: "Rapaxi", name: "Voracian Sacrament", type: "Impera", deckLimit: 4,
    owner: "rapaxi_champion", ilk: "Beast", school: "Avarice", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "If you have 5 or more Khao, draw 1 card from your Grimoire and this being gains +1 Lyf.",
    experimental: true },
  { id: "p_rapaxi_3", species: "Rapaxi", name: "Scripture of Scales", type: "Impera", deckLimit: 3,
    owner: "rapaxi_lord", ilk: "Beast", school: "Avarice", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "If you have 8 or more Khao, this being gains +3 Lyt and +2 Lyf this Turn. Dim your Anima by 2.",
    experimental: true },

  // riven — Arcane Art: Wardweave (reactive counters)
  { id: "p_riven_1", species: "Riven", name: "Ward Before the Horn", type: "Impera", deckLimit: 5,
    owner: "riven_beb", ilk: "Humanoid", school: "Arcane Art", level: 1, khaoCost: 3,
    effectTag: null, ssg: false,
    effect: "When an adversary Impera targets this being, this being gains +2 Lyf until your next Turn.",
    experimental: true },
  { id: "p_riven_2", species: "Riven", name: "The Sky Answered First", type: "Impera", deckLimit: 4,
    owner: "riven_champion", ilk: "Humanoid", school: "Arcane Art", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "When your adversary plays a Tome or Impera, nullify it. Dim this being's Lyf by 1.",
    experimental: true },
  { id: "p_riven_3", species: "Riven", name: "The Unbroken Line", type: "Impera", deckLimit: 3,
    owner: "riven_lord", ilk: "Humanoid", school: "Arcane Art", level: 3, khaoCost: 7,
    effectTag: "OP2", ssg: false,
    effect: "When your adversary plays an Impera, nullify it; your beings gain +1 Lyf until your next Turn. Dim your Anima by 2.",
    experimental: true },

  // rosaluna — Lunaea: Tides
  { id: "p_rosaluna_1", species: "Rosaluna", name: "Closed Petals", type: "Impera", deckLimit: 5,
    owner: "rosaluna_beb", ilk: "Beast", school: "Lunaea", level: 1, khaoCost: 3,
    effectTag: null, ssg: false,
    effect: "This being gains +2 Lyf now; dim its Lyf by 1 at the end of your next Turn.",
    experimental: true },
  { id: "p_rosaluna_2", species: "Rosaluna", name: "Given by Night, Taken by Day", type: "Impera", deckLimit: 4,
    owner: "rosaluna_champion", ilk: "Beast", school: "Lunaea", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Draw 2 cards from your Grimoire; at the start of your next Turn, discard 1 card.",
    experimental: true },
  { id: "p_rosaluna_3", species: "Rosaluna", name: "Malarosa", type: "Impera", deckLimit: 3,
    owner: "rosaluna_lord", ilk: "Beast", school: "Lunaea", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "Your beings gain +2 Lyf until your next Turn; at the start of your next Turn, discard 2 cards. Dim your Anima by 1.",
    experimental: true },


  { id: "p_skuryz_1", species: "Skuryz", name: "Gutter Luck", type: "Impera", deckLimit: 5,
    owner: "skuryz_beb", ilk: "Beast", school: "Phantom", level: 1, khaoCost: 2,
    effectTag: "ACB", ssg: true,
    effect: "Shadow Gambling: flip a coin and call it. Call it right and this being gains +2 Lyt this Turn; call it wrong and dim this being's Lyf by 1.",
    experimental: true },
  { id: "p_skuryz_2", species: "Skuryz", name: "Nibble the Soul", type: "Impera", deckLimit: 4,
    owner: "skuryz_champion", ilk: "Beast", school: "Phantom", level: 2, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "Dim your adversary's Anima by 2.",
    experimental: true },
  { id: "p_skuryz_3", species: "Skuryz", name: "The Thousandth Toss", type: "Impera", deckLimit: 3,
    owner: "skuryz_lord", ilk: "Beast", school: "Phantom", level: 3, khaoCost: 6,
    effectTag: "ACB", ssg: true,
    effect: "Shadow Gambling: flip a coin and call it. Call it right and dim your adversary's Anima by 5; call it wrong and dim your own Anima by 2.",
    experimental: true },

  { id: "p_slijj_1", species: "Slijj", name: "Seed of Rot", type: "Impera", deckLimit: 5,
    owner: "slijj_beb", ilk: "Humanoid", school: "Dark Art", level: 1, khaoCost: 3,
    effectTag: "ATV", ssg: false,
    effect: "Choose an adversary's being. Dim its Lyf by 1 at the start of each of your next 2 Turns.",
    experimental: true },
  { id: "p_slijj_2", species: "Slijj", name: "Candlelit Eyes", type: "Impera", deckLimit: 4,
    owner: "slijj_champion", ilk: "Humanoid", school: "Dark Art", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Dim an adversary's being's Lyf by 2. If that being was already dimmed this way, dim it by 3 instead.",
    experimental: true },
  { id: "p_slijj_3", species: "Slijj", name: "The Long Interment", type: "Impera", deckLimit: 3,
    owner: "slijj_lord", ilk: "Humanoid", school: "Dark Art", level: 3, khaoCost: 7,
    effectTag: "ATV", ssg: false,
    effect: "Dim your own Anima by 2. For the next 3 Turns, dim every adversary's being's Lyf by 1 at the start of your Turn.",
    experimental: true },

  { id: "p_trunaran_1", species: "Trunaran", name: "Thick Hide", type: "Impera", deckLimit: 5,
    owner: "trunaran_beb", ilk: "Beast", school: "Phantom", level: 1, khaoCost: 2,
    effectTag: null, ssg: false,
    effect: "This being gains +2 Lyf.",
    experimental: true },
  { id: "p_trunaran_2", species: "Trunaran", name: "Stand and Bear It", type: "Impera", deckLimit: 4,
    owner: "trunaran_champion", ilk: "Beast", school: "Phantom", level: 2, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "This Turn, this being cannot have its Lyf dimmed below 1.",
    experimental: true },
  { id: "p_trunaran_3", species: "Trunaran", name: "Memory of the Herd", type: "Impera", deckLimit: 3,
    owner: "trunaran_lord", ilk: "Beast", school: "Phantom", level: 3, khaoCost: 6,
    effectTag: "OP2", ssg: false,
    effect: "This being gains +4 Lyf. It cannot attack this Turn.",
    experimental: true },

  { id: "p_tzhahws_1", species: "Tzhahws", name: "Pocket the Difference", type: "Impera", deckLimit: 5,
    owner: "tzhahws_beb", ilk: "Humanoid", school: "Avarice", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Devote 1 Khao to this card's Soul as plunder. This being gains +1 Lyt for each plunder banked beneath it.",
    experimental: true },
  { id: "p_tzhahws_2", species: "Tzhahws", name: "Buccaneer's Cut", type: "Impera", deckLimit: 4,
    owner: "tzhahws_champion", ilk: "Humanoid", school: "Avarice", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Take the top card of your adversary's Grimoire and place it beneath this being's Soul as plunder.",
    experimental: true },
  { id: "p_tzhahws_3", species: "Tzhahws", name: "Divide the Spoils", type: "Impera", deckLimit: 3,
    owner: "tzhahws_lord", ilk: "Humanoid", school: "Avarice", level: 3, khaoCost: 7,
    effectTag: "OP3", ssg: false,
    effect: "For each plunder banked beneath your Tzhahws beings' Souls (up to 4), dim your adversary's Anima by 1.",
    experimental: true },

  { id: "p_ursaeva_1", species: "Ursaeva", name: "Curl and Endure", type: "Impera", deckLimit: 5,
    owner: "ursaeva_beb", ilk: "Beast", school: "Phantom", level: 1, khaoCost: 2,
    effectTag: null, ssg: false,
    effect: "This being gains +2 Lyf.",
    experimental: true },
  { id: "p_ursaeva_2", species: "Ursaeva", name: "Denmother's Wall", type: "Impera", deckLimit: 4,
    owner: "ursaeva_champion", ilk: "Beast", school: "Phantom", level: 2, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "This Turn, when another of your beings would have its Lyf dimmed, this being's Lyf is dimmed instead.",
    experimental: true },
  { id: "p_ursaeva_3", species: "Ursaeva", name: "The Winter's Waking", type: "Impera", deckLimit: 3,
    owner: "ursaeva_lord", ilk: "Beast", school: "Phantom", level: 3, khaoCost: 6,
    effectTag: "OP3", ssg: false,
    effect: "This being gains +3 Lyf and +2 Lyt until the end of your next Turn.",
    experimental: true },

  { id: "p_valtorix_1", species: "Valtorix", name: "Withering Gallop", type: "Impera", deckLimit: 5,
    owner: "valtorix_beb", ilk: "Beast", school: "Dark Art", level: 1, khaoCost: 3,
    effectTag: "ATV", ssg: false,
    effect: "Choose an adversary's being. Dim its Lyf by 1 now and again at the start of your next Turn.",
    experimental: true },
  { id: "p_valtorix_2", species: "Valtorix", name: "Hoofprints of Small Suns", type: "Impera", deckLimit: 4,
    owner: "valtorix_champion", ilk: "Beast", school: "Dark Art", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Choose 1 of your adversary's beings; it loses 1 Lyf at the end of each of the next 2 Turns.",
    experimental: true },
  { id: "p_valtorix_3", species: "Valtorix", name: "The Stampede Remembers", type: "Impera", deckLimit: 3,
    owner: "valtorix_lord", ilk: "Beast", school: "Dark Art", level: 3, khaoCost: 8,
    effectTag: "ATV", ssg: false,
    effect: "Dim your own Anima by 2. For the next 2 Turns, dim an adversary's being's Lyf by 2 at the start of your Turn.",
    experimental: true },

  { id: "p_velkor_1", species: "Velkor", name: "Damp the Spark", type: "Impera", deckLimit: 5,
    owner: "velkor_beb", ilk: "Beast", school: "Arcane Art", level: 1, khaoCost: 2,
    effectTag: null, ssg: false,
    effect: "When your adversary plays an Impera this Turn, its numbers are dimmed by 1 as it resolves.",
    experimental: true },
  { id: "p_velkor_2", species: "Velkor", name: "Wall of Rolling Grey", type: "Impera", deckLimit: 4,
    owner: "velkor_champion", ilk: "Beast", school: "Arcane Art", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Nullify the next Tome your adversary plays this Turn.",
    experimental: true },
  { id: "p_velkor_3", species: "Velkor", name: "Answered Before Asked", type: "Impera", deckLimit: 3,
    owner: "velkor_lord", ilk: "Beast", school: "Arcane Art", level: 3, khaoCost: 8,
    effectTag: "OP3", ssg: false,
    effect: "Dim your own Anima by 2. Nullify an adversary's Impera as it is played, and this being becomes Intangible until your next Turn.",
    experimental: true },

  { id: "p_vyren_1", species: "Vyren", name: "First Molt", type: "Impera", deckLimit: 5,
    owner: "vyren_beb", ilk: "Beast", school: "Lunaea", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Draw 1 card from your Grimoire, then place 1 card from your hand on top of your Grimoire.",
    experimental: true },
  { id: "p_vyren_2", species: "Vyren", name: "Tide-Shed Skin", type: "Impera", deckLimit: 4,
    owner: "vyren_champion", ilk: "Beast", school: "Lunaea", level: 2, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "Draw 2 cards from your Grimoire. At the start of your next Turn, discard 1 card from your hand to the Necropolis.",
    experimental: true },
  { id: "p_vyren_3", species: "Vyren", name: "The Serpent's Ebb", type: "Impera", deckLimit: 3,
    owner: "vyren_lord", ilk: "Beast", school: "Lunaea", level: 3, khaoCost: 7,
    effectTag: "OP2", ssg: false,
    effect: "Discard 2 cards from your hand to the Necropolis. This being becomes Intangible until your next Turn, and you draw 3 cards at its start.",
    experimental: true },

  { id: "p_xolibrix_1", species: "Xolibrix", name: "Sip the Wound", type: "Impera", deckLimit: 5,
    owner: "xolibrix_beb", ilk: "Beast", school: "Phantom", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "One of your beings gains +2 Lyf.",
    experimental: true },
  { id: "p_xolibrix_2", species: "Xolibrix", name: "Nectar of the Spirit Worlds", type: "Impera", deckLimit: 4,
    owner: "xolibrix_champion", ilk: "Beast", school: "Phantom", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Restore your own Anima by 2.",
    experimental: true },
  { id: "p_xolibrix_3", species: "Xolibrix", name: "Too Fast to Mourn", type: "Impera", deckLimit: 3,
    owner: "xolibrix_lord", ilk: "Beast", school: "Phantom", level: 3, khaoCost: 7,
    effectTag: "OP3", ssg: false,
    effect: "One of your beings gains +3 Lyf, and this being becomes Intangible until your next Turn.",
    experimental: true },


  { id: "p_zriidians_1", species: "Zriidians", name: "One Degree Off", type: "Impera", deckLimit: 5,
    owner: "zriidians_beb", ilk: "Humanoid", school: "Astrae", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Dim the Lyt of every being in play by 1 until the end of this Turn.",
    experimental: true },
  { id: "p_zriidians_2", species: "Zriidians", name: "Counterspin", type: "Impera", deckLimit: 4,
    owner: "zriidians_champion", ilk: "Humanoid", school: "Astrae", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Every being in play loses 1 Lyf, then this being gains 1 Lyf for each being dimmed this way.",
    experimental: true },
  { id: "p_zriidians_3", species: "Zriidians", name: "The Heavens Reversed", type: "Impera", deckLimit: 3,
    owner: "zriidians_lord", ilk: "Humanoid", school: "Astrae", level: 3, khaoCost: 7,
    effectTag: "OP2", ssg: false,
    effect: "Dim your own Anima by 2. Swap the Lyt and Lyf of every being in play until the end of your adversary's next Turn.",
    experimental: true },

  { id: "p_zylaraeon_1", species: "Zylaraeon", name: "Coin for the Accused", type: "Impera", deckLimit: 5,
    owner: "zylaraeon_beb", ilk: "Humanoid", school: "Avarice", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Devote 1 Khao you would spend this Turn instead of spending it. Next Turn, that Khao returns as 2.",
    experimental: true },
  { id: "p_zylaraeon_2", species: "Zylaraeon", name: "The Framed Devil's Ledger", type: "Impera", deckLimit: 4,
    owner: "zylaraeon_champion", ilk: "Humanoid", school: "Avarice", level: 2, khaoCost: 5,
    effectTag: "ACB", ssg: true,
    effect: "Call odds or evens, then roll a die. Called right: gain Khao equal to the roll. Called wrong: your adversary gains Khao equal to half the roll, rounded up.",
    experimental: true },
  { id: "p_zylaraeon_3", species: "Zylaraeon", name: "Ruin the Writer", type: "Impera", deckLimit: 3,
    owner: "zylaraeon_lord", ilk: "Humanoid", school: "Avarice", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "If you have 6 or more unspent Khao, dim your adversary's Anima by 4; otherwise dim it by 1. Unspent Khao stays unspent.",
    experimental: true },

  { id: "p_dg0_nihil_1", species: "DG-0", name: "Tidy the Wound", type: "Impera", deckLimit: 5,
    owner: "dg0_nihil_beb", ilk: "Machine", school: "N'ihil", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Your adversary discards 1 card from their hand to the Necropolis, chosen at random.",
    experimental: true },
  { id: "p_dg0_nihil_2", species: "DG-0", name: "Anesthetic Silence", type: "Impera", deckLimit: 4,
    owner: "dg0_nihil_champion", ilk: "Machine", school: "N'ihil", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Choose an adversary being. Its Impera effects are nullified until the end of your adversary's next Turn.",
    experimental: true },
  { id: "p_dg0_nihil_3", species: "DG-0", name: "Discharge Papers", type: "Impera", deckLimit: 3,
    owner: "dg0_nihil_lord", ilk: "Machine", school: "N'ihil", level: 3, khaoCost: 7,
    effectTag: "OP3", ssg: false,
    effect: "Dim your own Anima by 2. Your adversary reveals their hand and discards every Impera in it to the Necropolis.",
    experimental: true },

  { id: "p_z4z_omnia_1", species: "Z4Z", name: "Teach It to Bite", type: "Impera", deckLimit: 5,
    owner: "z4z_omnia_beb", ilk: "Machine", school: "O'mnia", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Choose an adversary being. The next time it strikes this Turn, its strike dims its own Warlock's Anima by 1 as well.",
    experimental: true },
  { id: "p_z4z_omnia_2", species: "Z4Z", name: "Stolen Sword Doctrine", type: "Impera", deckLimit: 4,
    owner: "z4z_omnia_champion", ilk: "Machine", school: "O'mnia", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Dim your own Anima by 1. Take the top Impera of your adversary's Necropolis; you may play it this Turn as if it were yours, then ruin it.",
    experimental: true },
  { id: "p_z4z_omnia_3", species: "Z4Z", name: "Turn the Blade on H'ehll", type: "Impera", deckLimit: 3,
    owner: "z4z_omnia_lord", ilk: "Machine", school: "O'mnia", level: 3, khaoCost: 8,
    effectTag: "OP3", ssg: false,
    effect: "Dim your own Anima by 3. Choose an adversary being: until the end of this Turn it strikes for you, and this being may strike one additional time.",
    experimental: true },

  { id: "p_antz_avarice_1", species: "Antdroid", name: "Carry It Home", type: "Impera", deckLimit: 5,
    owner: "antz_avarice_beb", ilk: "Machine", school: "Avarice", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Devote 1 Anima to the hoard. At the start of your third Turn from now, gain 3 Anima.",
    experimental: true },
  { id: "p_antz_avarice_2", species: "Antdroid", name: "Colony Count", type: "Impera", deckLimit: 4,
    owner: "antz_avarice_champion", ilk: "Machine", school: "Avarice", level: 2, khaoCost: 4,
    effectTag: "OP2", ssg: false,
    effect: "This being gains +1 Lyf for each other Antdroid being you have in play, until it leaves play.",
    experimental: true },
  { id: "p_antz_avarice_3", species: "Antdroid", name: "Open the Reservoir", type: "Impera", deckLimit: 3,
    owner: "antz_avarice_lord", ilk: "Machine", school: "Avarice", level: 3, khaoCost: 7,
    effectTag: "OP3", ssg: false,
    effect: "If you did not spend Khao last Turn, your Antdroid beings each gain +2 Lyt and +2 Lyf until the end of this Turn.",
    experimental: true },

  { id: "p_nyfaux_arcane_1", species: "N'yfaux", name: "Trap the Kit Laid", type: "Impera", deckLimit: 5,
    owner: "nyfaux_arcane_beb", ilk: "Beast", school: "Arcane Art", level: 1, khaoCost: 3,
    effectTag: null, ssg: false,
    effect: "When your adversary plays a Tome, you may play this: that Tome's effect is dimmed by half its numbers, rounded down.",
    experimental: true },
  { id: "p_nyfaux_arcane_2", species: "N'yfaux", name: "Sealstalker's Rebuttal", type: "Impera", deckLimit: 4,
    owner: "nyfaux_arcane_champion", ilk: "Beast", school: "Arcane Art", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "When your adversary plays an Impera, you may play this: nullify it, and this being becomes Intangible until the end of this Turn.",
    experimental: true },
  { id: "p_nyfaux_arcane_3", species: "N'yfaux", name: "The Ninth Ward Closes", type: "Impera", deckLimit: 3,
    owner: "nyfaux_arcane_lord", ilk: "Beast", school: "Arcane Art", level: 3, khaoCost: 7,
    effectTag: "OP2", ssg: false,
    effect: "When your adversary plays a Tome or Impera, you may play this: nullify it, and it goes to the Necropolis instead of resolving. Dim your own Anima by 2.",
    experimental: true },

  { id: "p_nyfaux_gambling_1", species: "N'yfaux", name: "Flip for Supper", type: "Impera", deckLimit: 5,
    owner: "nyfaux_gambling_beb", ilk: "Beast", school: "Shadow Gambling", level: 1, khaoCost: 2,
    effectTag: "ACB", ssg: true,
    effect: "Call a coin, then flip it. Called right: this being gains +2 Lyt this Turn. Called wrong: it loses 1 Lyf this Turn.",
    experimental: true },
  { id: "p_nyfaux_gambling_2", species: "N'yfaux", name: "The Fox Is Loaded", type: "Impera", deckLimit: 4,
    owner: "nyfaux_gambling_champion", ilk: "Beast", school: "Shadow Gambling", level: 2, khaoCost: 4,
    effectTag: "ACB", ssg: true,
    effect: "Roll two dice. If they match, dim your adversary's Anima by the number showing on one die. If they do not, dim your own Anima by 1.",
    experimental: true },
  { id: "p_nyfaux_gambling_3", species: "N'yfaux", name: "The House Calls", type: "Impera", deckLimit: 3,
    owner: "nyfaux_gambling_lord", ilk: "Beast", school: "Shadow Gambling", level: 3, khaoCost: 7,
    effectTag: "ACB", ssg: true,
    effect: "Your adversary calls a die, then you roll it. If they called wrong, dim their Anima by the roll. If they called right, they gain 2 Anima from the House's purse.",
    experimental: true },


  // h_phantom
  { id: "p_h_phantom_1", species: "Wisp", name: "First Haunting", type: "Impera", deckLimit: 5,
    owner: "h_phantom_beb", ilk: "Humanoid", school: "Phantom", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Dim your adversary's Anima by 1.",
    experimental: true },
  { id: "p_h_phantom_2", species: "Wisp", name: "Passing Through", type: "Impera", deckLimit: 4,
    owner: "h_phantom_champion", ilk: "Humanoid", school: "Phantom", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "This being is Intangible until the start of your next Turn. If it attacked this Turn, dim your adversary's Anima by 1.",
    experimental: true },
  { id: "p_h_phantom_3", species: "Wisp", name: "Grief Made Light", type: "Impera", deckLimit: 3,
    owner: "h_phantom_lord", ilk: "Humanoid", school: "Phantom", level: 3, khaoCost: 7,
    effectTag: "OP2", ssg: false,
    effect: "Dim your adversary's Anima by 3, then dim your own Anima by 1.",
    experimental: true },

  // h_time
  { id: "p_h_time_1", species: "Cepheid", name: "Steady Pulse", type: "Impera", deckLimit: 5,
    owner: "h_time_beb", ilk: "Humanoid", school: "Time", level: 1, khaoCost: 3,
    effectTag: "ATV", ssg: false,
    effect: "At the start of each of your Turns, this being gains +1 Lyf.",
    experimental: true },
  { id: "p_h_time_2", species: "Cepheid", name: "Measured Distance", type: "Impera", deckLimit: 4,
    owner: "h_time_champion", ilk: "Humanoid", school: "Time", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Choose an adversary's being. Until your next Turn it loses 2 Lyt.",
    experimental: true },
  { id: "p_h_time_3", species: "Cepheid", name: "The Hour Appointed", type: "Impera", deckLimit: 3,
    owner: "h_time_lord", ilk: "Humanoid", school: "Time", level: 3, khaoCost: 8,
    effectTag: "OP3", ssg: false,
    effect: "Choose one of your beings in the Necropolis and Resurrect it at the start of your next Turn. Dim your own Anima by 2.",
    experimental: true },

  // h_dragon
  { id: "p_h_dragon_1", species: "Ember", name: "Hoard-Fire Sip", type: "Impera", deckLimit: 5,
    owner: "h_dragon_beb", ilk: "Humanoid", school: "Dragon", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +2 Lyt until the end of this Turn.",
    experimental: true },
  { id: "p_h_dragon_2", species: "Ember", name: "Meteoric Descent", type: "Impera", deckLimit: 4,
    owner: "h_dragon_champion", ilk: "Humanoid", school: "Dragon", level: 2, khaoCost: 6,
    effectTag: "OPT", ssg: false,
    effect: "Dim an adversary's being's Lyf by 3. This being cannot attack this Turn.",
    experimental: true },
  { id: "p_h_dragon_3", species: "Ember", name: "Century Rain", type: "Impera", deckLimit: 3,
    owner: "h_dragon_lord", ilk: "Humanoid", school: "Dragon", level: 3, khaoCost: 9,
    effectTag: "OP2", ssg: false,
    effect: "Dim the Lyf of every adversary's being by 2, and dim your own Anima by 2.",
    experimental: true },

  // h_nihil
  { id: "p_h_nihil_1", species: "Void", name: "Unlearned Word", type: "Impera", deckLimit: 5,
    owner: "h_nihil_beb", ilk: "Humanoid", school: "N'ihil", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Your adversary discards 1 card from their hand at random.",
    experimental: true },
  { id: "p_h_nihil_2", species: "Void", name: "Escort to the Edge", type: "Impera", deckLimit: 4,
    owner: "h_nihil_champion", ilk: "Humanoid", school: "N'ihil", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Choose an adversary's being. Its effects are nullified until the start of your next Turn.",
    experimental: true },
  { id: "p_h_nihil_3", species: "Void", name: "The Finishing", type: "Impera", deckLimit: 3,
    owner: "h_nihil_lord", ilk: "Humanoid", school: "N'ihil", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "Look at your adversary's hand and choose 1 card; they discard it. Then dim your own Anima by 1.",
    experimental: true },

  // h_omnia
  { id: "p_h_omnia_1", species: "Twin", name: "Borrowed Shine", type: "Impera", deckLimit: 5,
    owner: "h_omnia_beb", ilk: "Humanoid", school: "O'mnia", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Choose an adversary's being. It loses 1 Lyt and this being gains +1 Lyt until the end of this Turn.",
    experimental: true },
  { id: "p_h_omnia_2", species: "Twin", name: "Quiet Siphon", type: "Impera", deckLimit: 4,
    owner: "h_omnia_champion", ilk: "Humanoid", school: "O'mnia", level: 2, khaoCost: 6,
    effectTag: "OPT", ssg: false,
    effect: "An adversary's being loses 2 Lyf and this being gains the 2 Lyf it lost.",
    experimental: true },
  { id: "p_h_omnia_3", species: "Twin", name: "Drink the Companion", type: "Impera", deckLimit: 3,
    owner: "h_omnia_lord", ilk: "Humanoid", school: "O'mnia", level: 3, khaoCost: 9,
    effectTag: "OP2", ssg: false,
    effect: "Take control of an adversary's level 1 being until the end of your next Turn. Dim your own Anima by 2.",
    experimental: true },

  // h_avarice
  { id: "p_h_avarice_1", species: "Gold", name: "Pocket Everything", type: "Impera", deckLimit: 5,
    owner: "h_avarice_beb", ilk: "Humanoid", school: "Avarice", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Devote 1 Khao to this being's Soul. For each card in its Soul, it gains +1 Lyf.",
    experimental: true },
  { id: "p_h_avarice_2", species: "Gold", name: "Compound Interest", type: "Impera", deckLimit: 4,
    owner: "h_avarice_champion", ilk: "Humanoid", school: "Avarice", level: 2, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Skip drawing this Turn. At the start of your next Turn, draw 2 cards.",
    experimental: true },
  { id: "p_h_avarice_3", species: "Gold", name: "Ledger of Ages", type: "Impera", deckLimit: 3,
    owner: "h_avarice_lord", ilk: "Humanoid", school: "Avarice", level: 3, khaoCost: 8,
    effectTag: "OP3", ssg: false,
    effect: "If this being's Soul holds 3 or more cards, gain 3 Anima. Otherwise gain 1 Anima.",
    experimental: true },

  // h_solaea
  { id: "p_h_solaea_1", species: "Sunspot", name: "Thumbprint of the Sun", type: "Impera", deckLimit: 5,
    owner: "h_solaea_beb", ilk: "Humanoid", school: "Solaea", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +2 Lyt until the end of this Turn.",
    experimental: true },
  { id: "p_h_solaea_2", species: "Sunspot", name: "Rite of Imitation", type: "Impera", deckLimit: 4,
    owner: "h_solaea_champion", ilk: "Humanoid", school: "Solaea", level: 2, khaoCost: 6,
    effectTag: "ATV", ssg: false,
    effect: "While this being is in play, your other beings gain +1 Lyt.",
    experimental: true },
  { id: "p_h_solaea_3", species: "Sunspot", name: "Crown of Burning Wind", type: "Impera", deckLimit: 3,
    owner: "h_solaea_lord", ilk: "Humanoid", school: "Solaea", level: 3, khaoCost: 9,
    effectTag: "OP2", ssg: false,
    effect: "All your beings gain +2 Lyt until the end of this Turn. Dim your own Anima by 2.",
    experimental: true },

  // h_lunaea
  { id: "p_h_lunaea_1", species: "Crescent", name: "Partly Here", type: "Impera", deckLimit: 5,
    owner: "h_lunaea_beb", ilk: "Humanoid", school: "Lunaea", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Draw 1 card, then place 1 card from your hand on the bottom of your Grimoire.",
    experimental: true },
  { id: "p_h_lunaea_2", species: "Crescent", name: "Harbor Pull", type: "Impera", deckLimit: 4,
    owner: "h_lunaea_champion", ilk: "Humanoid", school: "Lunaea", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Draw 2 cards. At the start of your next Turn, discard 1 card from your hand.",
    experimental: true },
  { id: "p_h_lunaea_3", species: "Crescent", name: "Stand Before the Sun", type: "Impera", deckLimit: 3,
    owner: "h_lunaea_lord", ilk: "Humanoid", school: "Lunaea", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "Discard your hand, then draw that many cards plus 1. Dim your own Anima by 2.",
    experimental: true },

  // h_darkart
  { id: "p_h_darkart_1", species: "Ashen", name: "Grey Notes", type: "Impera", deckLimit: 5,
    owner: "h_darkart_beb", ilk: "Humanoid", school: "Dark Art", level: 1, khaoCost: 3,
    effectTag: "ATV", ssg: false,
    effect: "Choose an adversary's being. At the start of each of your next 2 Turns, dim its Lyf by 1.",
    experimental: true },
  { id: "p_h_darkart_2", species: "Ashen", name: "Season of Ruin", type: "Impera", deckLimit: 4,
    owner: "h_darkart_champion", ilk: "Humanoid", school: "Dark Art", level: 2, khaoCost: 6,
    effectTag: "ATV", ssg: false,
    effect: "Choose an adversary's being. At the start of each of your next 3 Turns, dim its Lyf by 1. Dim your own Anima by 1.",
    experimental: true },
  { id: "p_h_darkart_3", species: "Ashen", name: "Colder Than the Universe", type: "Impera", deckLimit: 3,
    owner: "h_darkart_lord", ilk: "Humanoid", school: "Dark Art", level: 3, khaoCost: 9,
    effectTag: "ATV", ssg: false,
    effect: "At the start of each of your Turns, dim the Lyf of every adversary's being by 1 and dim your own Anima by 1.",
    experimental: true },

  // h_arcane
  { id: "p_h_arcane_1", species: "Lens", name: "Bend the Light", type: "Impera", deckLimit: 5,
    owner: "h_arcane_beb", ilk: "Humanoid", school: "Arcane Art", level: 1, khaoCost: 3,
    effectTag: null, ssg: false,
    effect: "When your adversary plays a Tome, you may play this: this being gains +2 Lyf.",
    experimental: true },
  { id: "p_h_arcane_2", species: "Lens", name: "Catch It Folded", type: "Impera", deckLimit: 4,
    owner: "h_arcane_champion", ilk: "Humanoid", school: "Arcane Art", level: 2, khaoCost: 6,
    effectTag: null, ssg: false,
    effect: "When your adversary plays an Impera, you may play this: nullify its effect.",
    experimental: true },
  { id: "p_h_arcane_3", species: "Lens", name: "Halo Verdict", type: "Impera", deckLimit: 3,
    owner: "h_arcane_lord", ilk: "Humanoid", school: "Arcane Art", level: 3, khaoCost: 9,
    effectTag: null, ssg: false,
    effect: "When your adversary plays a Tome or an Impera, you may play this: nullify it, and dim your own Anima by 2.",
    experimental: true },

  // h_gambling
  { id: "p_h_gambling_1", species: "Falling", name: "Wish Against You", type: "Impera", deckLimit: 5,
    owner: "h_gambling_beb", ilk: "Humanoid", school: "Shadow Gambling", level: 1, khaoCost: 2,
    effectTag: "ACB", ssg: true,
    effect: "Shadow Gambling: flip a coin and call it. If you call it right, this being gains +2 Lyt this Turn. If you call it wrong, it loses 1 Lyt this Turn.",
    experimental: true },
  { id: "p_h_gambling_2", species: "Falling", name: "Every Pass, a Number", type: "Impera", deckLimit: 4,
    owner: "h_gambling_champion", ilk: "Humanoid", school: "Shadow Gambling", level: 2, khaoCost: 5,
    effectTag: "ACB", ssg: true,
    effect: "Shadow Gambling: roll a die. On 4 or higher, dim your adversary's Anima by 2. On 3 or lower, dim your own Anima by 1.",
    experimental: true },
  { id: "p_h_gambling_3", species: "Falling", name: "Let Them Win One", type: "Impera", deckLimit: 3,
    owner: "h_gambling_lord", ilk: "Humanoid", school: "Shadow Gambling", level: 3, khaoCost: 7,
    effectTag: "ACB", ssg: true,
    effect: "Shadow Gambling: roll two dice. On doubles, dim your adversary's Anima by 4; otherwise your adversary draws 1 card.",
    experimental: true },


  // ── BATCH H — the missing second-Class lines (caP'n, 2026-09-03) ──

  // abyssia_lunaea — Lunaea: tides, give now take later
  { id: "p_abyssia_lunaea_1", name: "Moon-Tide Sip", type: "Impera", deckLimit: 5,
    owner: "abyssia_lunaea_beb", ilk: "Beast", school: "Lunaea", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Draw 1 card from your Grimoire. At the start of your next Turn, discard 1 card.",
    experimental: true, species: "Abyssia" },
  { id: "p_abyssia_lunaea_2", name: "Nightshade Ebb", type: "Impera", deckLimit: 4,
    owner: "abyssia_lunaea_champion", ilk: "Beast", school: "Lunaea", level: 2, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "Discard 1 card, then draw 2 cards from your Grimoire.",
    experimental: true, species: "Abyssia" },
  { id: "p_abyssia_lunaea_3", name: "The Tide Comes Home", type: "Impera", deckLimit: 3,
    owner: "abyssia_lunaea_lord", ilk: "Beast", school: "Lunaea", level: 3, khaoCost: 7,
    effectTag: "OP2", ssg: false,
    effect: "Draw 3 cards from your Grimoire. At the start of your next Turn, discard 2 cards and this being gains +2 Lyf.",
    experimental: true, species: "Abyssia" },

  // aealix_lunaea
  { id: "p_aealix_lunaea_1", name: "Borrowed Glimmer", type: "Impera", deckLimit: 5,
    owner: "aealix_lunaea_beb", ilk: "Humanoid", school: "Lunaea", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +2 Lyt until end of Turn. At the start of your next Turn, discard 1 card.",
    experimental: true, species: "Aealix" },
  { id: "p_aealix_lunaea_2", name: "Waning Exchange", type: "Impera", deckLimit: 4,
    owner: "aealix_lunaea_champion", ilk: "Humanoid", school: "Lunaea", level: 2, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "Discard 2 cards, then draw 2 cards from your Grimoire and this being gains +1 Lyf.",
    experimental: true, species: "Aealix" },
  { id: "p_aealix_lunaea_3", name: "Full Moon of Nothing", type: "Impera", deckLimit: 3,
    owner: "aealix_lunaea_lord", ilk: "Humanoid", school: "Lunaea", level: 3, khaoCost: 7,
    effectTag: "OP2", ssg: false,
    effect: "Each Warlock discards their hand and draws that many cards from their Grimoire. You draw 1 additional card.",
    experimental: true, species: "Aealix" },

  // aeolix_solaea — Solaea: Lyt buffs and auras
  { id: "p_aeolix_solaea_1", name: "First True Sunrise", type: "Impera", deckLimit: 5,
    owner: "aeolix_solaea_beb", ilk: "Humanoid", school: "Solaea", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +2 Lyt until end of Turn.",
    experimental: true, species: "Aeolix" },
  { id: "p_aeolix_solaea_2", name: "Sermon Facing Forward", type: "Impera", deckLimit: 4,
    owner: "aeolix_solaea_champion", ilk: "Humanoid", school: "Solaea", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +3 Lyt until end of Turn. If it ruins a being this Turn, restore 1 of your Anima.",
    experimental: true, species: "Aeolix" },
  { id: "p_aeolix_solaea_3", name: "The Corrected Faith", type: "Impera", deckLimit: 3,
    owner: "aeolix_solaea_lord", ilk: "Humanoid", school: "Solaea", level: 3, khaoCost: 8,
    effectTag: "ATV", ssg: false,
    effect: "While this being remains in play, your beings gain +1 Lyt during your Turn.",
    experimental: true, species: "Aeolix" },

  // aeternavis_solaea
  { id: "p_aeternavis_solaea_1", name: "Coal That Refuses Ash", type: "Impera", deckLimit: 5,
    owner: "aeternavis_solaea_beb", ilk: "Beast", school: "Solaea", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +2 Lyt until end of Turn.",
    experimental: true, species: "Aeternavis" },
  { id: "p_aeternavis_solaea_2", name: "Shadowless Wingspan", type: "Impera", deckLimit: 4,
    owner: "aeternavis_solaea_champion", ilk: "Beast", school: "Solaea", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +3 Lyt until end of Turn, and +1 more if a Vyren is in play.",
    experimental: true, species: "Aeternavis" },
  { id: "p_aeternavis_solaea_3", name: "Return in Radiance", type: "Impera", deckLimit: 3,
    owner: "aeternavis_solaea_lord", ilk: "Beast", school: "Solaea", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "Resurrect 1 level-1 Solaea being from your Necropolis, and this being gains +2 Lyt until end of Turn. Dim your Anima by 2.",
    experimental: true, species: "Aeternavis" },

  // auroraeth_solaea
  { id: "p_auroraeth_solaea_1", name: "Bud Toward Noon", type: "Impera", deckLimit: 5,
    owner: "auroraeth_solaea_beb", ilk: "Beast", school: "Solaea", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +1 Lyt and +1 Lyf until end of Turn.",
    experimental: true, species: "Auroraeth" },
  { id: "p_auroraeth_solaea_2", name: "Zenith Petalfall", type: "Impera", deckLimit: 4,
    owner: "auroraeth_solaea_champion", ilk: "Beast", school: "Solaea", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Your beings gain +1 Lyt until end of Turn.",
    experimental: true, species: "Auroraeth" },
  { id: "p_auroraeth_solaea_3", name: "The Meridian Hour", type: "Impera", deckLimit: 3,
    owner: "auroraeth_solaea_lord", ilk: "Beast", school: "Solaea", level: 3, khaoCost: 8,
    effectTag: "ATV", ssg: false,
    effect: "While this being remains in play, it gains +2 Lyt during your Turn and your other Solaea beings gain +1 Lyt during your Turn.",
    experimental: true, species: "Auroraeth" },

  // calypsis_solaea
  { id: "p_calypsis_solaea_1", name: "Dawn on the Water", type: "Impera", deckLimit: 5,
    owner: "calypsis_solaea_beb", ilk: "Humanoid", school: "Solaea", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +2 Lyt until end of Turn.",
    experimental: true, species: "Calypsis" },
  { id: "p_calypsis_solaea_2", name: "Song of the Gilded Swell", type: "Impera", deckLimit: 4,
    owner: "calypsis_solaea_champion", ilk: "Humanoid", school: "Solaea", level: 2, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +2 Lyt until end of Turn and restore 1 of your Anima.",
    experimental: true, species: "Calypsis" },
  { id: "p_calypsis_solaea_3", name: "Ocean Turned Gold", type: "Impera", deckLimit: 3,
    owner: "calypsis_solaea_lord", ilk: "Humanoid", school: "Solaea", level: 3, khaoCost: 9,
    effectTag: "OP2", ssg: false,
    effect: "Your beings gain +2 Lyt until end of Turn. Dim your Anima by 2.",
    experimental: true, species: "Calypsis" },

  // carcharion_arcane — Arcane Art: reactive counters
  { id: "p_carcharion_arcane_1", name: "Bite the Spell", type: "Impera", deckLimit: 5,
    owner: "carcharion_arcane_beb", ilk: "Beast", school: "Arcane Art", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "When your adversary plays an Impera, you may play this: nullify it unless they pay 2 Khao.",
    experimental: true, species: "Carcharion" },
  { id: "p_carcharion_arcane_2", name: "Runes Along the Teeth", type: "Impera", deckLimit: 4,
    owner: "carcharion_arcane_champion", ilk: "Beast", school: "Arcane Art", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "When your adversary plays a Tome or Impera, you may play this: nullify it. Dim your Anima by 1.",
    experimental: true, species: "Carcharion" },
  { id: "p_carcharion_arcane_3", name: "No Curse Crosses", type: "Impera", deckLimit: 3,
    owner: "carcharion_arcane_lord", ilk: "Beast", school: "Arcane Art", level: 3, khaoCost: 7,
    effectTag: "OP2", ssg: false,
    effect: "When your adversary plays an Impera targeting one of your beings, you may play this: nullify it and this being gains +2 Lyf.",
    experimental: true, species: "Carcharion" },

  // carnifex_darkart — Dark Art: ticking blight
  { id: "p_carnifex_darkart_1", name: "First Rotting Verse", type: "Impera", deckLimit: 5,
    owner: "carnifex_darkart_beb", ilk: "Humanoid", school: "Dark Art", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Dim 1 adversary being's Lyf by 1 at the start of each of your next 2 Turns.",
    experimental: true, species: "Carnifex" },
  { id: "p_carnifex_darkart_2", name: "Evening Service of Decay", type: "Impera", deckLimit: 4,
    owner: "carnifex_darkart_champion", ilk: "Humanoid", school: "Dark Art", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Dim 1 adversary being's Lyf by 2, and by 1 more at the start of your next Turn.",
    experimental: true, species: "Carnifex" },
  { id: "p_carnifex_darkart_3", name: "The Hymn That Continues", type: "Impera", deckLimit: 3,
    owner: "carnifex_darkart_lord", ilk: "Humanoid", school: "Dark Art", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "Dim each adversary being's Lyf by 1 at the start of each of your next 3 Turns. Dim your Anima by 2.",
    experimental: true, species: "Carnifex" },

  // devorix_avarice — Avarice: banking, duplicates
  { id: "p_devorix_avarice_1", name: "Swallow and Keep", type: "Impera", deckLimit: 5,
    owner: "devorix_avarice_beb", ilk: "Beast", school: "Avarice", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Devote 1 Khao to this being's Soul. If its Soul holds 3 or more cards, it gains +1 Lyf.",
    experimental: true, species: "Devorix" },
  { id: "p_devorix_avarice_2", name: "Treasury With Teeth", type: "Impera", deckLimit: 4,
    owner: "devorix_avarice_champion", ilk: "Beast", school: "Avarice", level: 2, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "Pay 2 Khao: draw 1 card from your Grimoire and this being gains +1 Lyf. Guaranteed.",
    experimental: true, species: "Devorix" },
  { id: "p_devorix_avarice_3", name: "Never Once Let Go", type: "Impera", deckLimit: 3,
    owner: "devorix_avarice_lord", ilk: "Beast", school: "Avarice", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "For each card in this being's Soul, it gains +1 Lyf, to a maximum of +4. Then draw 1 card from your Grimoire.",
    experimental: true, species: "Devorix" },

  // embearaea_solaea
  { id: "p_embearaea_solaea_1", name: "Berry Pop", type: "Impera", deckLimit: 5,
    owner: "embearaea_solaea_beb", ilk: "Beast", school: "Solaea", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +2 Lyt until end of Turn.",
    experimental: true, species: "Embearaea" },
  { id: "p_embearaea_solaea_2", name: "Stored Sunburst", type: "Impera", deckLimit: 4,
    owner: "embearaea_solaea_champion", ilk: "Beast", school: "Solaea", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +3 Lyt until end of Turn. Dim your Anima by 1.",
    experimental: true, species: "Embearaea" },
  { id: "p_embearaea_solaea_3", name: "Two Suns at Summer", type: "Impera", deckLimit: 3,
    owner: "embearaea_solaea_lord", ilk: "Beast", school: "Solaea", level: 3, khaoCost: 9,
    effectTag: "OP2", ssg: false,
    effect: "This being gains +4 Lyt until end of Turn and your other beings gain +1 Lyt until end of Turn. Dim your Anima by 3.",
    experimental: true, species: "Embearaea" },

  // geulaoin_omnia — O'mnia: steal/convert
  { id: "p_geulaoin_omnia_1", name: "Ember From Another Nest", type: "Impera", deckLimit: 5,
    owner: "geulaoin_omnia_beb", ilk: "Beast", school: "O'mnia", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Dim 1 adversary being's Lyt by 1; this being gains +1 Lyt until end of Turn.",
    experimental: true, species: "Geulaoin" },
  { id: "p_geulaoin_omnia_2", name: "Your Fire, Flattered", type: "Impera", deckLimit: 4,
    owner: "geulaoin_omnia_champion", ilk: "Beast", school: "O'mnia", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Dim 1 adversary being's Lyt by 2 until end of Turn; this being gains that much Lyt until end of Turn.",
    experimental: true, species: "Geulaoin" },
  { id: "p_geulaoin_omnia_3", name: "The First Flame, Filched", type: "Impera", deckLimit: 3,
    owner: "geulaoin_omnia_lord", ilk: "Beast", school: "O'mnia", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "Take control of 1 adversary Impera in their Necropolis and play it as your own without paying its Khao cost, if its cost is 4 or less. Dim your Anima by 2.",
    experimental: true, species: "Geulaoin" },

  // gorgxonox_darkart
  { id: "p_gorgxonox_darkart_1", name: "Polite Distance of Death", type: "Impera", deckLimit: 5,
    owner: "gorgxonox_darkart_beb", ilk: "Beast", school: "Dark Art", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Dim 1 adversary being's Lyf by 1 at the start of each of your next 2 Turns.",
    experimental: true, species: "Gorgxonox" },
  { id: "p_gorgxonox_darkart_2", name: "Brown Line on the Map", type: "Impera", deckLimit: 4,
    owner: "gorgxonox_darkart_champion", ilk: "Beast", school: "Dark Art", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Dim up to 2 adversary beings' Lyf by 1, and by 1 more at the start of your next Turn.",
    experimental: true, species: "Gorgxonox" },
  { id: "p_gorgxonox_darkart_3", name: "Sicken in Its Shadow", type: "Impera", deckLimit: 3,
    owner: "gorgxonox_darkart_lord", ilk: "Beast", school: "Dark Art", level: 3, khaoCost: 9,
    effectTag: "OP2", ssg: false,
    effect: "Dim each adversary being's Lyf by 2, and dim your adversary's Anima by 1 at the start of your next Turn. Dim your own Anima by 3.",
    experimental: true, species: "Gorgxonox" },

  // kraalok_time — Time: patience pays over turns
  { id: "p_kraalok_time_1", name: "Soon.", type: "Impera", deckLimit: 5,
    owner: "kraalok_time_beb", ilk: "Beast", school: "Time", level: 1, khaoCost: 1,
    effectTag: "OPT", ssg: false,
    effect: "At the start of your third Turn after playing this, this being gains +2 Lyf.",
    experimental: true, species: "Kraalok" },
  { id: "p_kraalok_time_2", name: "A Century, Unhurried", type: "Impera", deckLimit: 4,
    owner: "kraalok_time_champion", ilk: "Beast", school: "Time", level: 2, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +1 Lyf at the start of each of your next 3 Turns.",
    experimental: true, species: "Kraalok" },
  { id: "p_kraalok_time_3", name: "Almost Done, Always", type: "Impera", deckLimit: 3,
    owner: "kraalok_time_lord", ilk: "Beast", school: "Time", level: 3, khaoCost: 7,
    effectTag: "OP3", ssg: false,
    effect: "At the start of your second Turn after playing this, ruin 1 adversary being with 4 or less Lyf. If a Riven is in play, you may choose it regardless of its Lyf.",
    experimental: true, species: "Kraalok" },

  // lyraei_nihil — N'ihil: gentle erasure
  { id: "p_lyraei_nihil_1", name: "Un-write the Bruise", type: "Impera", deckLimit: 5,
    owner: "lyraei_nihil_beb", ilk: "Humanoid", school: "N'ihil", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Your adversary discards 1 card from their hand at random.",
    experimental: true, species: "Lyraei" },
  { id: "p_lyraei_nihil_2", name: "Mercy of Removal", type: "Impera", deckLimit: 4,
    owner: "lyraei_nihil_champion", ilk: "Humanoid", school: "N'ihil", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Nullify 1 adversary being's effect until the end of their next Turn, and restore 1 of your Anima.",
    experimental: true, species: "Lyraei" },
  { id: "p_lyraei_nihil_3", name: "The Blank Page Blessing", type: "Impera", deckLimit: 3,
    owner: "lyraei_nihil_lord", ilk: "Humanoid", school: "N'ihil", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "Your adversary discards 2 cards from their hand at random, and you restore 2 of your Anima. Dim this being's Lyt by 1 until end of Turn.",
    experimental: true, species: "Lyraei" },

  // lyrixi_time
  { id: "p_lyrixi_time_1", name: "Keep the Count", type: "Impera", deckLimit: 5,
    owner: "lyrixi_time_beb", ilk: "Beast", school: "Time", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Draw 1 card from your Grimoire at the start of your next Turn.",
    experimental: true, species: "Lyrixi" },
  { id: "p_lyrixi_time_2", name: "Dusk Rubato", type: "Impera", deckLimit: 4,
    owner: "lyrixi_time_champion", ilk: "Beast", school: "Time", level: 2, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "One adversary being cannot attack during their next Turn.",
    experimental: true, species: "Lyrixi" },
  { id: "p_lyrixi_time_3", name: "The Beat Beneath All Beats", type: "Impera", deckLimit: 3,
    owner: "lyrixi_time_lord", ilk: "Beast", school: "Time", level: 3, khaoCost: 8,
    effectTag: "OP3", ssg: false,
    effect: "Take 1 additional Turn after this one. During that Turn your beings cannot attack. Dim your Anima by 3.",
    experimental: true, species: "Lyrixi" },

  // malumora_omnia
  { id: "p_malumora_omnia_1", name: "Tiny Pockets", type: "Impera", deckLimit: 5,
    owner: "malumora_omnia_beb", ilk: "Humanoid", school: "O'mnia", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Look at your adversary's hand and choose 1 Impera. They discard it.",
    experimental: true, species: "Malumora" },
  { id: "p_malumora_omnia_2", name: "Acquired Mid-Casting", type: "Impera", deckLimit: 4,
    owner: "malumora_omnia_champion", ilk: "Humanoid", school: "O'mnia", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "When your adversary plays an Impera with khaoCost 3 or less, you may play this: nullify it and add it to your hand instead of their Necropolis.",
    experimental: true, species: "Malumora" },
  { id: "p_malumora_omnia_3", name: "The Excellent Collection", type: "Impera", deckLimit: 3,
    owner: "malumora_omnia_lord", ilk: "Humanoid", school: "O'mnia", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "Play 1 Impera from your adversary's Necropolis as your own without paying its Khao cost, if its cost is 5 or less. Dim your Anima by 2.",
    experimental: true, species: "Malumora" },

  // mygzori_time
  { id: "p_mygzori_time_1", name: "Prayer at Every When", type: "Impera", deckLimit: 5,
    owner: "mygzori_time_beb", ilk: "Humanoid", school: "Time", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Restore 1 of your Anima at the start of each of your next 2 Turns.",
    experimental: true, species: "Mygzori" },
  { id: "p_mygzori_time_2", name: "Met Myself Coming Back", type: "Impera", deckLimit: 4,
    owner: "mygzori_time_champion", ilk: "Humanoid", school: "Time", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Return 1 card from your Necropolis to your hand. It cannot be played until your next Turn.",
    experimental: true, species: "Mygzori" },
  { id: "p_mygzori_time_3", name: "Before and After the Loop", type: "Impera", deckLimit: 3,
    owner: "mygzori_time_lord", ilk: "Humanoid", school: "Time", level: 3, khaoCost: 8,
    effectTag: "OP3", ssg: false,
    effect: "Resurrect 1 level-2 or lower being from your Necropolis. At the start of your third Turn after playing this, restore 3 of your Anima.",
    experimental: true, species: "Mygzori" },

  // noxura_gambling — Shadow Gambling
  { id: "p_noxura_gambling_1", name: "Pocket-Change Faith", type: "Impera", deckLimit: 5,
    owner: "noxura_gambling_beb", ilk: "Humanoid", school: "Shadow Gambling", level: 1, khaoCost: 2,
    effectTag: "ACB", ssg: true,
    effect: "Call heads or tails, then flip a coin. Called it: draw 1 card from your Grimoire. Missed: discard 1 card.",
    experimental: true, species: "Noxura" },
  { id: "p_noxura_gambling_2", name: "Faith Without a Wager Is Opinion", type: "Impera", deckLimit: 4,
    owner: "noxura_gambling_champion", ilk: "Humanoid", school: "Shadow Gambling", level: 2, khaoCost: 4,
    effectTag: "ACB", ssg: true,
    effect: "Call a number, then roll a die. Called it: this being gains +3 Lyt until end of Turn. Missed: dim your Anima by 1.",
    experimental: true, species: "Noxura" },
  { id: "p_noxura_gambling_3", name: "Everything on the Next Sentence", type: "Impera", deckLimit: 3,
    owner: "noxura_gambling_lord", ilk: "Humanoid", school: "Shadow Gambling", level: 3, khaoCost: 7,
    effectTag: "ACB", ssg: true,
    effect: "Call odds or evens, then roll a die. Called it: dim your adversary's Anima by 4. Missed: dim your own Anima by 3.",
    experimental: true, species: "Noxura" },

  // rapaxi_nihil
  { id: "p_rapaxi_nihil_1", name: "No Crumbs, No Word", type: "Impera", deckLimit: 5,
    owner: "rapaxi_nihil_beb", ilk: "Beast", school: "N'ihil", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Your adversary discards 1 card from their hand at random.",
    experimental: true, species: "Rapaxi" },
  { id: "p_rapaxi_nihil_2", name: "Blank Pages of Voracia", type: "Impera", deckLimit: 4,
    owner: "rapaxi_nihil_champion", ilk: "Beast", school: "N'ihil", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Ruin the top 3 cards of your adversary's Grimoire; they are removed from the duel, not sent to the Necropolis.",
    experimental: true, species: "Rapaxi" },
  { id: "p_rapaxi_nihil_3", name: "There Was Never a City", type: "Impera", deckLimit: 3,
    owner: "rapaxi_nihil_lord", ilk: "Beast", school: "N'ihil", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "Choose 1 card in your adversary's Necropolis; remove it from the duel. They discard 1 card from their hand at random. Dim your Anima by 2.",
    experimental: true, species: "Rapaxi" },

  // riven_astrae — Astrae: board-wide
  { id: "p_riven_astrae_1", name: "Three Small Stars", type: "Impera", deckLimit: 5,
    owner: "riven_astrae_beb", ilk: "Humanoid", school: "Astrae", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Every being in play gains +1 Lyf until end of Turn.",
    experimental: true, species: "Riven" },
  { id: "p_riven_astrae_2", name: "The Sky Is Not Private", type: "Impera", deckLimit: 4,
    owner: "riven_astrae_champion", ilk: "Humanoid", school: "Astrae", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Dim every being in play's Lyt by 1 until end of Turn, except this being.",
    experimental: true, species: "Riven" },
  { id: "p_riven_astrae_3", name: "All of It, Tonight", type: "Impera", deckLimit: 3,
    owner: "riven_astrae_lord", ilk: "Humanoid", school: "Astrae", level: 3, khaoCost: 9,
    effectTag: "OP2", ssg: false,
    effect: "Dim every being in play's Lyf by 2, then your beings gain +2 Lyf. Dim your Anima by 2.",
    experimental: true, species: "Riven" },

  // rosaluna_time
  { id: "p_rosaluna_time_1", name: "One Petal Per Season", type: "Impera", deckLimit: 5,
    owner: "rosaluna_time_beb", ilk: "Beast", school: "Time", level: 1, khaoCost: 1,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +1 Lyf at the start of each of your next 2 Turns.",
    experimental: true, species: "Rosaluna" },
  { id: "p_rosaluna_time_2", name: "Gardening for Grandchildren", type: "Impera", deckLimit: 4,
    owner: "rosaluna_time_champion", ilk: "Beast", school: "Time", level: 2, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "Devote 2 Khao to this being's Soul. At the start of your third Turn after playing this, restore 3 of your Anima.",
    experimental: true, species: "Rosaluna" },
  { id: "p_rosaluna_time_3", name: "The Dynastic Flowering", type: "Impera", deckLimit: 3,
    owner: "rosaluna_time_lord", ilk: "Beast", school: "Time", level: 3, khaoCost: 7,
    effectTag: "OP3", ssg: false,
    effect: "This being gains +1 Lyf at the start of each of your next 4 Turns. While it remains in play with 9 or more Lyf, it is Intangible during your adversary's Turn.",
    experimental: true, species: "Rosaluna" },

  // skuryz_gambling
  { id: "p_skuryz_gambling_1", name: "Nothing Is a Stake", type: "Impera", deckLimit: 5,
    owner: "skuryz_gambling_beb", ilk: "Beast", school: "Shadow Gambling", level: 1, khaoCost: 1,
    effectTag: "ACB", ssg: true,
    effect: "Call heads or tails, then flip a coin. Called it: this being gains +2 Lyt until end of Turn. Missed: nothing happens, which the rat also counts as a win.",
    experimental: true, species: "Skuryz" },
  { id: "p_skuryz_gambling_2", name: "Gutter Book Odds", type: "Impera", deckLimit: 4,
    owner: "skuryz_gambling_champion", ilk: "Beast", school: "Shadow Gambling", level: 2, khaoCost: 4,
    effectTag: "ACB", ssg: true,
    effect: "Roll 2 dice. Doubles: draw 2 cards from your Grimoire and this being gains +2 Lyt until end of Turn. Anything else: discard 1 card.",
    experimental: true, species: "Skuryz" },
  { id: "p_skuryz_gambling_3", name: "The Long Shot Lands", type: "Impera", deckLimit: 3,
    owner: "skuryz_gambling_lord", ilk: "Beast", school: "Shadow Gambling", level: 3, khaoCost: 7,
    effectTag: "ACB", ssg: true,
    effect: "Call a number, then roll a die. Called it: ruin 1 adversary being. Missed: dim this being's Lyf by 2.",
    experimental: true, species: "Skuryz" },

  // valtorix_solaea
  { id: "p_valtorix_solaea_1", name: "Shine of the Forgotten", type: "Impera", deckLimit: 5,
    owner: "valtorix_solaea_beb", ilk: "Beast", school: "Solaea", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +2 Lyt until end of Turn.",
    experimental: true, species: "Valtorix" },
  { id: "p_valtorix_solaea_2", name: "Mirror-Polished Gallop", type: "Impera", deckLimit: 4,
    owner: "valtorix_solaea_champion", ilk: "Beast", school: "Solaea", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "This being gains +3 Lyt until end of Turn. If it came from your Necropolis this duel, it gains +1 more.",
    experimental: true, species: "Valtorix" },
  { id: "p_valtorix_solaea_3", name: "What the Sun Keeps", type: "Impera", deckLimit: 3,
    owner: "valtorix_solaea_lord", ilk: "Beast", school: "Solaea", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "Resurrect this being from your Necropolis if it was ruined this Turn, and it gains +2 Lyt until end of Turn. Dim your Anima by 3.",
    experimental: true, species: "Valtorix" },

  // vyren_nihil
  { id: "p_vyren_nihil_1", name: "Un-say the Name", type: "Impera", deckLimit: 5,
    owner: "vyren_nihil_beb", ilk: "Beast", school: "N'ihil", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Your adversary discards 1 card from their hand at random.",
    experimental: true, species: "Vyren" },
  { id: "p_vyren_nihil_2", name: "Removed From the Sayable", type: "Impera", deckLimit: 4,
    owner: "vyren_nihil_champion", ilk: "Beast", school: "N'ihil", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Nullify 1 adversary being's effect until the end of their next Turn.",
    experimental: true, species: "Vyren" },
  { id: "p_vyren_nihil_3", name: "Sentence Never Finished", type: "Impera", deckLimit: 3,
    owner: "vyren_nihil_lord", ilk: "Beast", school: "N'ihil", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "Choose 1 Impera in your adversary's hand at random; they discard it and it is removed from the duel. Dim your Anima by 2.",
    experimental: true, species: "Vyren" },

  // vyren_phantom — Phantom: anima strikes
  { id: "p_vyren_phantom_1", name: "Strike Through the Veil", type: "Impera", deckLimit: 5,
    owner: "vyren_phantom_beb", ilk: "Beast", school: "Phantom", level: 1, khaoCost: 2,
    effectTag: "OPT", ssg: false,
    effect: "Dim your adversary's Anima by 1.",
    experimental: true, species: "Vyren" },
  { id: "p_vyren_phantom_2", name: "Pale Meadow Ambush", type: "Impera", deckLimit: 4,
    owner: "vyren_phantom_champion", ilk: "Beast", school: "Phantom", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "This being is Intangible until the start of your next Turn, then dim your adversary's Anima by 2.",
    experimental: true, species: "Vyren" },
  { id: "p_vyren_phantom_3", name: "No Further Use for Flesh", type: "Impera", deckLimit: 3,
    owner: "vyren_phantom_lord", ilk: "Beast", school: "Phantom", level: 3, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "Dim your adversary's Anima by 4. Dim your own Anima by 2.",
    experimental: true, species: "Vyren" },

  // zriidians_omnia
  { id: "p_zriidians_omnia_1", name: "Always Been Zriidian", type: "Impera", deckLimit: 5,
    owner: "zriidians_omnia_beb", ilk: "Humanoid", school: "O'mnia", level: 1, khaoCost: 3,
    effectTag: "OPT", ssg: false,
    effect: "Nullify 1 adversary Impera as it is played; add it to your hand instead of their Necropolis if its khaoCost is 2 or less.",
    experimental: true, species: "Zriidians" },
  { id: "p_zriidians_omnia_2", name: "Redraw the Line Around You", type: "Impera", deckLimit: 4,
    owner: "zriidians_omnia_champion", ilk: "Humanoid", school: "O'mnia", level: 2, khaoCost: 5,
    effectTag: "OPT", ssg: false,
    effect: "Choose 1 adversary being with 3 or less Lyf; until your next Turn it cannot attack, and this being gains Lyt equal to its Lyt until end of Turn, to a maximum of +3.",
    experimental: true, species: "Zriidians" },
  { id: "p_zriidians_omnia_3", name: "The Great Conversion", type: "Impera", deckLimit: 3,
    owner: "zriidians_omnia_lord", ilk: "Humanoid", school: "O'mnia", level: 3, khaoCost: 9,
    effectTag: "OP3", ssg: false,
    effect: "Take control of 1 adversary being with 4 or less Lyf until the end of your next Turn. Dim your Anima by 3.",
    experimental: true, species: "Zriidians" },

  // zylaraeon_gambling
  { id: "p_zylaraeon_gambling_1", name: "Log the Plot Hole", type: "Impera", deckLimit: 5,
    owner: "zylaraeon_gambling_beb", ilk: "Humanoid", school: "Shadow Gambling", level: 1, khaoCost: 2,
    effectTag: "ACB", ssg: true,
    effect: "Call heads or tails, then flip a coin. Called it: your adversary discards 1 card at random. Missed: you discard 1 card.",
    experimental: true, species: "Zylaraeon" },
  { id: "p_zylaraeon_gambling_2", name: "The Far Side of Every Bet", type: "Impera", deckLimit: 4,
    owner: "zylaraeon_gambling_champion", ilk: "Humanoid", school: "Shadow Gambling", level: 2, khaoCost: 4,
    effectTag: "ACB", ssg: true,
    effect: "Your adversary calls heads or tails, then you flip a coin. They miss: this being gains +3 Lyt until end of Turn. They call it: they draw 1 card from their Grimoire.",
    experimental: true, species: "Zylaraeon" },
  { id: "p_zylaraeon_gambling_3", name: "Wager Against the Writer", type: "Impera", deckLimit: 3,
    owner: "zylaraeon_gambling_lord", ilk: "Humanoid", school: "Shadow Gambling", level: 3, khaoCost: 7,
    effectTag: "ACB", ssg: true,
    effect: "Call odds or evens, then roll a die. Called it: ruin 1 adversary being with 5 or less Lyf. Missed: dim your Anima by 3, and privately be relieved.",
    experimental: true, species: "Zylaraeon" },

];

export const EX_UHLTIS = [
  ...KYRIUM_PILOT.uhltis,

  { id: "u_abyssia_lord", species: "Abyssia", name: "All In at Midnight", type: "U'hlti", deckLimit: 1,
    owner: "abyssia_lord", ilk: "Beast", school: "Shadow Gambling", level: 3, khaoCost: 8, animaCost: 6,
    effectTag: "ACB",
    effect: "Call a number, then roll a die. If you call it, dim your adversary's Anima by 8. If you miss, dim your own Anima by 3 — the House thanks you for playing.",
    experimental: true },
  { id: "u_aealix_lord", species: "Aealix", name: "The Beautiful Annihilation", type: "U'hlti", deckLimit: 1,
    owner: "aealix_lord", ilk: "Humanoid", school: "N'ihil", level: 3, khaoCost: 9, animaCost: 7,
    effectTag: null,
    effect: "Your adversary discards their hand. For each card discarded this way, dim their Anima by 1.",
    experimental: true },
  { id: "u_aeolix_lord", species: "Aeolix", name: "X'Aol Wears the Moon", type: "U'hlti", deckLimit: 1,
    owner: "aeolix_lord", ilk: "Humanoid", school: "O'mnia", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: null,
    effect: "Take control of a being your adversary controls until the end of your next Turn; while converted, it gains +2 Lyt and burns cold in your service.",
    experimental: true },
  { id: "u_aeternavis_lord", species: "Aeternavis", name: "Dawn Without End", type: "U'hlti", deckLimit: 1,
    owner: "aeternavis_lord", ilk: "Beast", school: "O'mnia", level: 3, khaoCost: 9, animaCost: 7,
    effectTag: null,
    effect: "Resurrect this being from your Necropolis with full Lyf, and it steals 2 Lyt from a being your adversary controls. The Vyren's night is cancelled.",
    experimental: true },
  { id: "u_auroraeth_lord", species: "Auroraeth", name: "The Sky Is a Trellis", type: "U'hlti", deckLimit: 1,
    owner: "auroraeth_lord", ilk: "Beast", school: "Astrae", level: 3, khaoCost: 9, animaCost: 7,
    effectTag: null,
    effect: "Dim every being your adversary controls by 3 Lyf and every being you control gains +2 Lyf. Beings ruined by this bloom go to the Necropolis as compost.",
    experimental: true },
  { id: "u_bunnovian_lord", species: "Bunnovian", name: "The Kindest Hour", type: "U'hlti", deckLimit: 1,
    owner: "bunnovian_lord", ilk: "Beast", school: "Phantom", level: 3, khaoCost: 8, animaCost: 5,
    effectTag: null,
    effect: "Every being you control regains full Lyf and your Warlock's Anima regains 4. No being you control may attack this Turn — the warren heals, it does not fight.",
    experimental: true },
  { id: "u_caelumari_lord", species: "Caelumari", name: "Spring Tide of the Upper Deep", type: "U'hlti", deckLimit: 1,
    owner: "caelumari_lord", ilk: "Beast", school: "Lunaea", level: 3, khaoCost: 8, animaCost: 6,
    effectTag: null,
    effect: "Draw 5 cards from your Grimoire; at the start of your next Turn, discard 3 cards from your hand and dim your Anima by 2. The tide above the sky gives, and then it takes.",
    experimental: true },
  { id: "u_calypsis_lord", species: "Calypsis", name: "The Choice She Never Makes", type: "U'hlti", deckLimit: 1,
    owner: "calypsis_lord", ilk: "Humanoid", school: "Lunaea", level: 3, khaoCost: 8, animaCost: 6,
    effectTag: null,
    effect: "Draw 3 cards from your Grimoire and this being gains +2 Lyt this Turn; at the start of your next Turn, return 2 cards from your hand to your Grimoire. Sun above, moon below, the fleet still waits.",
    experimental: true },
  { id: "u_carcharion_lord", species: "Carcharion", name: "The Water Closes Over", type: "U'hlti", deckLimit: 1,
    owner: "carcharion_lord", ilk: "Beast", school: "N'ihil", level: 3, khaoCost: 10, animaCost: 8,
    effectTag: null,
    effect: "Ruin a being your adversary controls and discard the top 3 cards of their Grimoire to the Necropolis. Dim your own Anima by 2 — erasure is thirsty work.",
    experimental: true },


  { id: "u_carnifex_lord", species: "Carnifex", name: "Cathedral of Borrowed Faiths", type: "U'hlti", deckLimit: 1,
    owner: "carnifex_lord", ilk: "Humanoid", school: "O'mnia", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: null,
    effect: "Take control of 1 adversary being of any level until the end of your next Turn; when control returns, that being dims 2 Lyf.",
    experimental: true },
  { id: "u_devorix_lord", species: "Devorix", name: "Swallow the Sky", type: "U'hlti", deckLimit: 1,
    owner: "devorix_lord", ilk: "Beast", school: "Astrae", level: 3, khaoCost: 10, animaCost: 8,
    effectTag: null,
    effect: "Every being in play, yours included, dims 3 Lyf. Devorix gains +1 Lyt for each being ruined this way.",
    experimental: true },
  { id: "u_embearaea_lord", species: "Embearaea", name: "Every Berry Is a Wager", type: "U'hlti", deckLimit: 1,
    owner: "embearaea_lord", ilk: "Beast", school: "Shadow Gambling", level: 3, khaoCost: 8, animaCost: 6,
    effectTag: "ACB",
    effect: "Call a number, then roll 2 dice. Called either die: ruin 1 adversary being. Called the total: ruin 2 instead. Missed everything: dim your Anima by 4.",
    experimental: true },
  { id: "u_geulaoin_lord", species: "Geulaoin", name: "The Ledger Never Forgives", type: "U'hlti", deckLimit: 1,
    owner: "geulaoin_lord", ilk: "Beast", school: "Avarice", level: 3, khaoCost: 9, animaCost: 7,
    effectTag: null,
    effect: "Spend all Khao banked on your beings; dim your adversary's Anima by that amount, to a limit of 8.",
    experimental: true },
  { id: "u_gorgxonox_lord", species: "Gorgxonox", name: "Everything Is the Hoard", type: "U'hlti", deckLimit: 1,
    owner: "gorgxonox_lord", ilk: "Beast", school: "Avarice", level: 3, khaoCost: 10, animaCost: 8,
    effectTag: null,
    effect: "Ruin 1 adversary being and bank Khao equal to its level under Gorgxonox. Gorgxonox gains +1 Lyt for each Khao banked on it.",
    experimental: true },
  { id: "u_hyalayri_lord", species: "Hyalayri", name: "Hymn of the Infinaite", type: "U'hlti", deckLimit: 1,
    owner: "hyalayri_lord", ilk: "Humanoid", school: "N'ihil", level: 3, khaoCost: 9, animaCost: 7,
    effectTag: null,
    effect: "Your adversary discards their hand down to 2 cards, and their beings' effects are silenced until your next Turn.",
    experimental: true },
  { id: "u_kraalok_lord", species: "Kraalok", name: "The Target Outlasts the Age", type: "U'hlti", deckLimit: 1,
    owner: "kraalok_lord", ilk: "Beast", school: "Dark Art", level: 3, khaoCost: 8, animaCost: 6,
    effectTag: null,
    effect: "Mark 1 adversary being. It dims 2 Lyf at the start of every one of your Turns for the rest of the duel, even if Kraalok leaves play.",
    experimental: true },
  { id: "u_kyrium_dark_lord", species: "Kyrium", name: "Opening the Forbidden Half", type: "U'hlti", deckLimit: 1,
    owner: "kyrium_dark_lord", ilk: "Beast", school: "Dark Art", level: 3, khaoCost: 10, animaCost: 8,
    effectTag: null,
    effect: "Every adversary being dims 2 Lyf now and 1 Lyf at the start of each of your next 2 Turns.",
    experimental: true },
  { id: "u_lyraei_lord", species: "Lyraei", name: "Caelestis, Spoken Backward", type: "U'hlti", deckLimit: 1,
    owner: "lyraei_lord", ilk: "Humanoid", school: "Arcane Art", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: null,
    effect: "Until your next Turn, nullify every Impera and Tome your adversary plays; each time one is nullified, restore 1 Lyf to each of your beings.",
    experimental: true },


  { id: "u_lyrixi_lord", species: "Lyrixi", name: "The Song That Shapes the World", type: "U'hlti", deckLimit: 1,
    owner: "lyrixi_lord", ilk: "Beast", school: "Arcane Art", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "OP3",
    effect: "Nullify every adversary Tome and Impera played until your next Turn, and your beings gain +2 Lyf.",
    experimental: true },
  { id: "u_magmantus_lord", species: "Magmantus", name: "Eruption Without End", type: "U'hlti", deckLimit: 1,
    owner: "magmantus_lord", ilk: "Machine", school: "Solaea", level: 3, khaoCost: 10, animaCost: 7,
    effectTag: "OP3",
    effect: "Your beings gain +3 Lyt until your next Turn, and this being gains an additional +2 Lyt.",
    experimental: true },
  { id: "u_malumora_lord", species: "Malumora", name: "Everything Dies of the Same Tune", type: "U'hlti", deckLimit: 1,
    owner: "malumora_lord", ilk: "Humanoid", school: "Dark Art", level: 3, khaoCost: 9, animaCost: 8,
    effectTag: "OP3",
    effect: "Dim every adversary being's Lyf by 2, and dim your adversary's Anima by 2 at the end of each of your next 2 Turns.",
    experimental: true },
  { id: "u_mygzori_lord", species: "Mygzori", name: "Proof of the Reader", type: "U'hlti", deckLimit: 1,
    owner: "mygzori_lord", ilk: "Humanoid", school: "Astrae", level: 3, khaoCost: 10, animaCost: 6,
    effectTag: "OP3",
    effect: "Dim every being in play's Lyt by 2 until your next Turn, and every one of your beings gains +2 Lyf.",
    experimental: true },
  { id: "u_noxura_lord", species: "Noxura", name: "The Writer Alone", type: "U'hlti", deckLimit: 1,
    owner: "noxura_lord", ilk: "Humanoid", school: "Lunaea", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "ACB",
    effect: "Shadow Gambling — call heads or tails, then flip: called right, draw 3 cards from your Grimoire and gain 3 Khao; called wrong, discard 2 cards.",
    experimental: true },
  { id: "u_rajaavi_lord", species: "Rajaavi", name: "Coronation of the Walking Throne", type: "U'hlti", deckLimit: 1,
    owner: "rajaavi_lord", ilk: "Beast", school: "Phantom", level: 3, khaoCost: 10, animaCost: 8,
    effectTag: "OP3",
    effect: "Dim your adversary's Anima by 6, and no adversary being may attack this being until your next Turn.",
    experimental: true },
  { id: "u_rapaxi_lord", species: "Rapaxi", name: "The Hoard Is Holy", type: "U'hlti", deckLimit: 1,
    owner: "rapaxi_lord", ilk: "Beast", school: "Avarice", level: 3, khaoCost: 10, animaCost: 6,
    effectTag: "OP3",
    effect: "Gain Khao equal to the number of cards in your hand, up to 6, and this being gains +2 Lyf.",
    experimental: true },
  { id: "u_riven_lord", species: "Riven", name: "Nine Generations of Walls", type: "U'hlti", deckLimit: 1,
    owner: "riven_lord", ilk: "Humanoid", school: "Arcane Art", level: 3, khaoCost: 9, animaCost: 7,
    effectTag: "OP3",
    effect: "Until your next Turn, your beings are Intangible, and the first adversary Impera played is nullified.",
    experimental: true },
  { id: "u_rosaluna_lord", species: "Rosaluna", name: "The Century Bloom", type: "U'hlti", deckLimit: 1,
    owner: "rosaluna_lord", ilk: "Beast", school: "Lunaea", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "OP3",
    effect: "Draw 4 cards from your Grimoire and your beings gain +2 Lyf; at the start of your next Turn, discard 3 cards.",
    experimental: true },


  { id: "u_skuryz_lord", species: "Skuryz", name: "All or Nothing in the Dark", type: "U'hlti", deckLimit: 1,
    owner: "skuryz_lord", ilk: "Beast", school: "Phantom", level: 3, khaoCost: 8, animaCost: 4,
    effectTag: "ACB", effect: "Shadow Gambling: flip 2 coins and call both. Call both right and dim your adversary's Anima by 8; otherwise dim your own Anima by 3.",
    experimental: true },
  { id: "u_slijj_lord", species: "Slijj", name: "Everything Returns to Soil", type: "U'hlti", deckLimit: 1,
    owner: "slijj_lord", ilk: "Humanoid", school: "Dark Art", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "ATV", effect: "For the next 3 Turns, dim every adversary's being's Lyf by 2 at the start of your Turn.",
    experimental: true },
  { id: "u_trunaran_lord", species: "Trunaran", name: "The Mountain Does Not Ask", type: "U'hlti", deckLimit: 1,
    owner: "trunaran_lord", ilk: "Beast", school: "Phantom", level: 3, khaoCost: 8, animaCost: 5,
    effectTag: null, effect: "This being gains +5 Lyf, and until your next Turn your other beings cannot be targeted while it remains in play.",
    experimental: true },
  { id: "u_tzhahws_lord", species: "Tzhahws", name: "The Captain Takes His Share", type: "U'hlti", deckLimit: 1,
    owner: "tzhahws_lord", ilk: "Humanoid", school: "Avarice", level: 3, khaoCost: 9, animaCost: 5,
    effectTag: null, effect: "Take the top 3 cards of your adversary's Grimoire and bank them beneath this being's Soul as plunder, then dim your adversary's Anima by 1 for each card taken.",
    experimental: true },
  { id: "u_ursaeva_lord", species: "Ursaeva", name: "Wrath of the Woken Winter", type: "U'hlti", deckLimit: 1,
    owner: "ursaeva_lord", ilk: "Beast", school: "Phantom", level: 3, khaoCost: 8, animaCost: 5,
    effectTag: null, effect: "This being gains +4 Lyf and +3 Lyt until your next Turn, and any Lyf your other beings would lose this Turn is dimmed from this being instead.",
    experimental: true },
  { id: "u_valtorix_lord", species: "Valtorix", name: "Ride of the Bleached Sun", type: "U'hlti", deckLimit: 1,
    owner: "valtorix_lord", ilk: "Beast", school: "Dark Art", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "ATV", effect: "Choose an adversary's being. Dim its Lyf by 3 now and by 2 at the start of each of your next 2 Turns; if it is ruined this way, dim your adversary's Anima by 2.",
    experimental: true },
  { id: "u_velkor_lord", species: "Velkor", name: "The Grey Swallows the Spell", type: "U'hlti", deckLimit: 1,
    owner: "velkor_lord", ilk: "Beast", school: "Arcane Art", level: 3, khaoCost: 9, animaCost: 5,
    effectTag: null, effect: "Until your next Turn, nullify each Tome and Impera your adversary plays, to a limit of 2.",
    experimental: true },
  { id: "u_vyren_lord", species: "Vyren", name: "The Tide That Takes Back", type: "U'hlti", deckLimit: 1,
    owner: "vyren_lord", ilk: "Beast", school: "Lunaea", level: 3, khaoCost: 8, animaCost: 5,
    effectTag: null, effect: "Draw 4 cards from your Grimoire. At the start of your next Turn, discard 2 cards from your hand to the Necropolis, or dim your own Anima by 2.",
    experimental: true },
  { id: "u_xolibrix_lord", species: "Xolibrix", name: "The Hum That Undoes Dying", type: "U'hlti", deckLimit: 1,
    owner: "xolibrix_lord", ilk: "Beast", school: "Phantom", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: null, effect: "Restore your own Anima by 4, and every one of your beings gains +2 Lyf.",
    experimental: true },


  { id: "u_zriidians_lord", species: "Zriidians", name: "Standstill of the Two Motions", type: "U'hlti", deckLimit: 1,
    owner: "zriidians_lord", ilk: "Humanoid", school: "Astrae", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "OP3",
    effect: "Until the start of your next Turn, no being in play may strike, and no being may leave play. The heavens hold their breath.",
    experimental: true },
  { id: "u_zylaraeon_lord", species: "Zylaraeon", name: "The Greater Devil's Wager", type: "U'hlti", deckLimit: 1,
    owner: "zylaraeon_lord", ilk: "Humanoid", school: "Avarice", level: 3, khaoCost: 10, animaCost: 8,
    effectTag: "ACB",
    effect: "Wager all your unspent Khao and call a coin, then flip it. Called right: dim your adversary's Anima by the Khao wagered, to a limit of 10. Called wrong: the wagered Khao is ruined and the writer's God laughs.",
    experimental: true },
  { id: "u_dg0_nihil_lord", species: "DG-0", name: "Total Discharge", type: "U'hlti", deckLimit: 1,
    owner: "dg0_nihil_lord", ilk: "Machine", school: "N'ihil", level: 3, khaoCost: 9, animaCost: 7,
    effectTag: "OP3",
    effect: "Your adversary discards their hand to the Necropolis, then draws 3 cards from their Grimoire. Nothing erased can ever hurt again.",
    experimental: true },
  { id: "u_z4z_omnia_lord", species: "Z4Z", name: "Conversion of the Forge", type: "U'hlti", deckLimit: 1,
    owner: "z4z_omnia_lord", ilk: "Machine", school: "O'mnia", level: 3, khaoCost: 10, animaCost: 8,
    effectTag: "OP3",
    effect: "Choose an adversary being. Instead of ruining it, it becomes yours until the end of your next Turn, and dims its own Warlock's Anima by its Lyt when it strikes.",
    experimental: true },
  { id: "u_antz_avarice_lord", species: "Antdroid", name: "The Last Day's Hoard", type: "U'hlti", deckLimit: 1,
    owner: "antz_avarice_lord", ilk: "Machine", school: "Avarice", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "OP3",
    effect: "Gain 1 Anima for each Turn that has passed this duel, to a limit of 10. The colony spends nothing until it spends everything.",
    experimental: true },
  { id: "u_nyfaux_arcane_lord", species: "N'yfaux", name: "Vault of Nine Wards", type: "U'hlti", deckLimit: 1,
    owner: "nyfaux_arcane_lord", ilk: "Beast", school: "Arcane Art", level: 3, khaoCost: 9, animaCost: 7,
    effectTag: "ATV",
    effect: "Until your next Turn ends, the first three Tomes or Impera your adversary plays are nullified and sent to the Necropolis.",
    experimental: true },
  { id: "u_nyfaux_gambling_lord", species: "N'yfaux", name: "The House Always Collects", type: "U'hlti", deckLimit: 1,
    owner: "nyfaux_gambling_lord", ilk: "Beast", school: "Shadow Gambling", level: 3, khaoCost: 10, animaCost: 8,
    effectTag: "ACB",
    effect: "Call three coins, then flip them. For each call you got right, dim your adversary's Anima by 3. Get all three wrong and the House pays out: your adversary gains 3 Anima.",
    experimental: true },


  { id: "u_h_phantom_lord", species: "Wisp", name: "The Silence That Would Not", type: "U'hlti", deckLimit: 1,
    owner: "h_phantom_lord", ilk: "Humanoid", school: "Phantom", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "OP3",
    effect: "Dim your adversary's Anima by 5. Until the start of your next Turn, this being is Intangible.",
    experimental: true },
  { id: "u_h_time_lord", species: "Cepheid", name: "What the Time Was Told", type: "U'hlti", deckLimit: 1,
    owner: "h_time_lord", ilk: "Humanoid", school: "Time", level: 3, khaoCost: 9, animaCost: 5,
    effectTag: "OP3",
    effect: "Your adversary skips the draw of their next Turn, and you draw 2 cards at the start of yours.",
    experimental: true },
  { id: "u_h_dragon_lord", species: "Ember", name: "The Weather Report", type: "U'hlti", deckLimit: 1,
    owner: "h_dragon_lord", ilk: "Humanoid", school: "Dragon", level: 3, khaoCost: 10, animaCost: 6,
    effectTag: "OP3",
    effect: "Ruin an adversary's being whose Lyf is 4 or less, and dim every other adversary's being's Lyf by 1.",
    experimental: true },
  { id: "u_h_nihil_lord", species: "Void", name: "Everything, Finished", type: "U'hlti", deckLimit: 1,
    owner: "h_nihil_lord", ilk: "Humanoid", school: "N'ihil", level: 3, khaoCost: 10, animaCost: 7,
    effectTag: "OP3",
    effect: "Your adversary discards their hand down to 2 cards. The effects of their beings are nullified until your next Turn.",
    experimental: true },
  { id: "u_h_omnia_lord", species: "Twin", name: "Down to the Wick", type: "U'hlti", deckLimit: 1,
    owner: "h_omnia_lord", ilk: "Humanoid", school: "O'mnia", level: 3, khaoCost: 10, animaCost: 6,
    effectTag: "OP3",
    effect: "Choose an adversary's being: it loses all its Lyt until your next Turn, and this being gains that much Lyt until your next Turn.",
    experimental: true },
  { id: "u_h_avarice_lord", species: "Gold", name: "The Rounding Error", type: "U'hlti", deckLimit: 1,
    owner: "h_avarice_lord", ilk: "Humanoid", school: "Avarice", level: 3, khaoCost: 9, animaCost: 5,
    effectTag: "OP3",
    effect: "For each card in this being's Soul, gain 2 Anima, to a maximum of 8.",
    experimental: true },
  { id: "u_h_solaea_lord", species: "Sunspot", name: "Noon That Does Not End", type: "U'hlti", deckLimit: 1,
    owner: "h_solaea_lord", ilk: "Humanoid", school: "Solaea", level: 3, khaoCost: 10, animaCost: 6,
    effectTag: "OP3",
    effect: "All your beings gain +3 Lyt until the end of your next Turn.",
    experimental: true },
  { id: "u_h_lunaea_lord", species: "Crescent", name: "The Sky's One Rebellion", type: "U'hlti", deckLimit: 1,
    owner: "h_lunaea_lord", ilk: "Humanoid", school: "Lunaea", level: 3, khaoCost: 9, animaCost: 5,
    effectTag: "OP3",
    effect: "Both Warlocks discard their hands. You draw 5 cards; your adversary draws 3.",
    experimental: true },
  { id: "u_h_darkart_lord", species: "Ashen", name: "The Long Cooling", type: "U'hlti", deckLimit: 1,
    owner: "h_darkart_lord", ilk: "Humanoid", school: "Dark Art", level: 3, khaoCost: 10, animaCost: 7,
    effectTag: "ATV",
    effect: "At the start of each of your Turns for the rest of the duel, dim your adversary's Anima by 1.",
    experimental: true },
  { id: "u_h_arcane_lord", species: "Lens", name: "The Ring Closes", type: "U'hlti", deckLimit: 1,
    owner: "h_arcane_lord", ilk: "Humanoid", school: "Arcane Art", level: 3, khaoCost: 10, animaCost: 6,
    effectTag: null,
    effect: "When your adversary plays a Tome, an Impera, or a U'hlti, you may play this: nullify it and dim their Anima by 3.",
    experimental: true },
  { id: "u_h_gambling_lord", species: "Falling", name: "The House Explodes Again", type: "U'hlti", deckLimit: 1,
    owner: "h_gambling_lord", ilk: "Humanoid", school: "Shadow Gambling", level: 3, khaoCost: 9, animaCost: 5,
    effectTag: "ACB",
    effect: "Shadow Gambling: roll two dice and call the total. Exactly right: dim your adversary's Anima by 10. Within 2: dim it by 4. Otherwise dim your own Anima by 3.",
    experimental: true },


  // ── BATCH H — the missing second-Class lines (caP'n, 2026-09-03) ──

  { id: "u_abyssia_lunaea_lord", name: "The Hour the Moon Stayed", type: "U'hlti", deckLimit: 1,
    owner: "abyssia_lunaea_lord", ilk: "Beast", school: "Lunaea", level: 3, khaoCost: 8, animaCost: 5,
    effectTag: "OP3",
    effect: "Each Warlock discards their hand; you draw 4 cards from your Grimoire, your adversary draws 3.",
    experimental: true, species: "Abyssia" },
  { id: "u_aealix_lunaea_lord", name: "Forget One Thing of Your Choosing", type: "U'hlti", deckLimit: 1,
    owner: "aealix_lunaea_lord", ilk: "Humanoid", school: "Lunaea", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "OP3",
    effect: "Draw 4 cards from your Grimoire. At the start of your next Turn, discard 3 cards and this being becomes Intangible until the end of that Turn.",
    experimental: true, species: "Aealix" },
  { id: "u_aeolix_solaea_lord", name: "The Apology to the Sun", type: "U'hlti", deckLimit: 1,
    owner: "aeolix_solaea_lord", ilk: "Humanoid", school: "Solaea", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "OP3",
    effect: "Your beings gain +2 Lyt until end of Turn, and this being gains +2 more.",
    experimental: true, species: "Aeolix" },
  { id: "u_aeternavis_solaea_lord", name: "Colleague of the Sun", type: "U'hlti", deckLimit: 1,
    owner: "aeternavis_solaea_lord", ilk: "Beast", school: "Solaea", level: 3, khaoCost: 10, animaCost: 7,
    effectTag: "OP3",
    effect: "If this being was ruined this duel, Resurrect it, then it gains +3 Lyt until end of Turn and your other Solaea beings gain +1 Lyt until end of Turn.",
    experimental: true, species: "Aeternavis" },
  { id: "u_auroraeth_solaea_lord", name: "The Sun Stops to Look", type: "U'hlti", deckLimit: 1,
    owner: "auroraeth_solaea_lord", ilk: "Beast", school: "Solaea", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "OP3",
    effect: "Until the start of your next Turn, your beings gain +2 Lyt and cannot have their Lyt dimmed.",
    experimental: true, species: "Auroraeth" },
  { id: "u_calypsis_solaea_lord", name: "Horizon to Horizon, Gold", type: "U'hlti", deckLimit: 1,
    owner: "calypsis_solaea_lord", ilk: "Humanoid", school: "Solaea", level: 3, khaoCost: 10, animaCost: 7,
    effectTag: "OP3",
    effect: "Your beings gain +3 Lyt until end of Turn, and you restore 2 of your Anima.",
    experimental: true, species: "Calypsis" },
  { id: "u_carcharion_arcane_lord", name: "The Warded Deep Closes", type: "U'hlti", deckLimit: 1,
    owner: "carcharion_arcane_lord", ilk: "Beast", school: "Arcane Art", level: 3, khaoCost: 8, animaCost: 5,
    effectTag: "ATV",
    effect: "Until the start of your next Turn, nullify each Tome and Impera your adversary plays.",
    experimental: true, species: "Carcharion" },
  { id: "u_carnifex_darkart_lord", name: "The Final Vesper", type: "U'hlti", deckLimit: 1,
    owner: "carnifex_darkart_lord", ilk: "Humanoid", school: "Dark Art", level: 3, khaoCost: 10, animaCost: 8,
    effectTag: "OP3",
    effect: "Dim each adversary being's Lyf by 2 at the start of each of your next 3 Turns.",
    experimental: true, species: "Carnifex" },
  { id: "u_devorix_avarice_lord", name: "Kingdoms, Filed and Gleaming", type: "U'hlti", deckLimit: 1,
    owner: "devorix_avarice_lord", ilk: "Beast", school: "Avarice", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "OP3",
    effect: "Draw 1 card from your Grimoire for each card in this being's Soul, to a maximum of 4. This being gains +2 Lyf.",
    experimental: true, species: "Devorix" },
  { id: "u_embearaea_solaea_lord", name: "The Afternoon of Two Suns", type: "U'hlti", deckLimit: 1,
    owner: "embearaea_solaea_lord", ilk: "Beast", school: "Solaea", level: 3, khaoCost: 10, animaCost: 8,
    effectTag: "OP3",
    effect: "This being gains +5 Lyt until end of Turn and your other beings gain +2 Lyt until end of Turn.",
    experimental: true, species: "Embearaea" },
  { id: "u_geulaoin_omnia_lord", name: "Before the World Had Mornings", type: "U'hlti", deckLimit: 1,
    owner: "geulaoin_omnia_lord", ilk: "Beast", school: "O'mnia", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "OP3",
    effect: "Take control of 1 adversary being with 3 or less Lyt until the end of your next Turn; it cannot be ruined while under your control.",
    experimental: true, species: "Geulaoin" },
  { id: "u_gorgxonox_darkart_lord", name: "The World Sickens Slowly", type: "U'hlti", deckLimit: 1,
    owner: "gorgxonox_darkart_lord", ilk: "Beast", school: "Dark Art", level: 3, khaoCost: 10, animaCost: 8,
    effectTag: "ATV",
    effect: "While this being remains in play, dim each adversary being's Lyf by 1 and your adversary's Anima by 1 at the start of each of your Turns. Dim your own Anima by 1 at the same time.",
    experimental: true, species: "Gorgxonox" },
  { id: "u_kraalok_time_lord", name: "It Was Always Almost Done", type: "U'hlti", deckLimit: 1,
    owner: "kraalok_time_lord", ilk: "Beast", school: "Time", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "OP3",
    effect: "At the start of your third Turn after playing this, ruin every adversary being with 5 or less Lyf. Until then, this being cannot attack.",
    experimental: true, species: "Kraalok" },
  { id: "u_lyraei_nihil_lord", name: "Cruelties That No Longer Exist", type: "U'hlti", deckLimit: 1,
    owner: "lyraei_nihil_lord", ilk: "Humanoid", school: "N'ihil", level: 3, khaoCost: 9, animaCost: 5,
    effectTag: "OP3",
    effect: "Your adversary discards 3 cards from their hand at random, and you restore 3 of your Anima.",
    experimental: true, species: "Lyraei" },
  { id: "u_lyrixi_time_lord", name: "Should It Ever Stop", type: "U'hlti", deckLimit: 1,
    owner: "lyrixi_time_lord", ilk: "Beast", school: "Time", level: 3, khaoCost: 10, animaCost: 7,
    effectTag: "OP3",
    effect: "Take 1 additional Turn after this one. During that Turn, only this being may attack.",
    experimental: true, species: "Lyrixi" },
  { id: "u_malumora_omnia_lord", name: "Merely Holding Them", type: "U'hlti", deckLimit: 1,
    owner: "malumora_omnia_lord", ilk: "Humanoid", school: "O'mnia", level: 3, khaoCost: 10, animaCost: 6,
    effectTag: "OP3",
    effect: "Play up to 2 Impera from your adversary's Necropolis as your own without paying their Khao costs, if each costs 5 or less.",
    experimental: true, species: "Malumora" },
  { id: "u_mygzori_time_lord", name: "The Prayer That Outlasts the Loop", type: "U'hlti", deckLimit: 1,
    owner: "mygzori_time_lord", ilk: "Humanoid", school: "Time", level: 3, khaoCost: 9, animaCost: 5,
    effectTag: "OP3",
    effect: "Resurrect up to 2 beings from your Necropolis whose combined levels are 4 or less. They cannot attack this Turn.",
    experimental: true, species: "Mygzori" },
  { id: "u_noxura_gambling_lord", name: "The Writer Finishes Well", type: "U'hlti", deckLimit: 1,
    owner: "noxura_gambling_lord", ilk: "Humanoid", school: "Shadow Gambling", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "ACB",
    effect: "Call a number, then roll a die. Called it: dim your adversary's Anima by 7. Missed: dim your own Anima by 4, and keep the faith.",
    experimental: true, species: "Noxura" },
  { id: "u_rapaxi_nihil_lord", name: "The Meal and Its Record", type: "U'hlti", deckLimit: 1,
    owner: "rapaxi_nihil_lord", ilk: "Beast", school: "N'ihil", level: 3, khaoCost: 10, animaCost: 7,
    effectTag: "OP3",
    effect: "Ruin 1 adversary being; it and every card in its Soul are removed from the duel, not sent to the Necropolis.",
    experimental: true, species: "Rapaxi" },
  { id: "u_riven_astrae_lord", name: "Every Star It Ever Earned", type: "U'hlti", deckLimit: 1,
    owner: "riven_astrae_lord", ilk: "Humanoid", school: "Astrae", level: 3, khaoCost: 10, animaCost: 7,
    effectTag: "OP3",
    effect: "Dim every being in play's Lyt and Lyf by 2 until your next Turn, except this being. Then your beings gain +1 Lyf.",
    experimental: true, species: "Riven" },
  { id: "u_rosaluna_time_lord", name: "Empires Time Themselves to Its Petals", type: "U'hlti", deckLimit: 1,
    owner: "rosaluna_time_lord", ilk: "Beast", school: "Time", level: 3, khaoCost: 9, animaCost: 5,
    effectTag: "OP3",
    effect: "This being gains +2 Lyf at the start of each of your next 3 Turns, and restore 1 of your Anima each time.",
    experimental: true, species: "Rosaluna" },
  { id: "u_skuryz_gambling_lord", name: "One Win Bought the City", type: "U'hlti", deckLimit: 1,
    owner: "skuryz_gambling_lord", ilk: "Beast", school: "Shadow Gambling", level: 3, khaoCost: 8, animaCost: 6,
    effectTag: "ACB",
    effect: "Roll 2 dice. Doubles: ruin every adversary being with 6 or less Lyf. Anything else: dim your Anima by 3 and draw 1 card from your Grimoire.",
    experimental: true, species: "Skuryz" },
  { id: "u_valtorix_solaea_lord", name: "What the Grave Loses, the Sun Keeps", type: "U'hlti", deckLimit: 1,
    owner: "valtorix_solaea_lord", ilk: "Beast", school: "Solaea", level: 3, khaoCost: 10, animaCost: 7,
    effectTag: "OP3",
    effect: "Resurrect up to 2 Solaea beings from your Necropolis; they gain +2 Lyt until end of Turn and cannot attack this Turn.",
    experimental: true, species: "Valtorix" },
  { id: "u_vyren_nihil_lord", name: "One Former Life, Un-said", type: "U'hlti", deckLimit: 1,
    owner: "vyren_nihil_lord", ilk: "Beast", school: "N'ihil", level: 3, khaoCost: 10, animaCost: 7,
    effectTag: "OP3",
    effect: "Choose 1 adversary being; nullify its effects for the rest of the duel and your adversary discards 2 cards from their hand at random.",
    experimental: true, species: "Vyren" },
  { id: "u_vyren_phantom_lord", name: "Check the Grass Before Sitting", type: "U'hlti", deckLimit: 1,
    owner: "vyren_phantom_lord", ilk: "Beast", school: "Phantom", level: 3, khaoCost: 10, animaCost: 8,
    effectTag: "OP3",
    effect: "Dim your adversary's Anima by 6. This being is Intangible until the start of your next Turn.",
    experimental: true, species: "Vyren" },
  { id: "u_zriidians_omnia_lord", name: "They Were Always Zriidian", type: "U'hlti", deckLimit: 1,
    owner: "zriidians_omnia_lord", ilk: "Humanoid", school: "O'mnia", level: 3, khaoCost: 10, animaCost: 7,
    effectTag: "OP3",
    effect: "Take control of 1 adversary being until the end of your next Turn. If it is still in play when the control ends, it returns with 1 less Lyt.",
    experimental: true, species: "Zriidians" },
  { id: "u_zylaraeon_gambling_lord", name: "The Case for the Bad End", type: "U'hlti", deckLimit: 1,
    owner: "zylaraeon_gambling_lord", ilk: "Humanoid", school: "Shadow Gambling", level: 3, khaoCost: 9, animaCost: 6,
    effectTag: "ACB",
    effect: "Call odds or evens, then roll a die. Called it: ruin 1 adversary being and dim your adversary's Anima by 3. Missed: dim your own Anima by 5.",
    experimental: true, species: "Zylaraeon" },

];

export const EX_TOMES = [
  ...KYRIUM_PILOT.tomes,

  // N'ihil — Erasure (strips information/options)
  { id: "ex_tome_nihil_l1", name: "The Unwritten Word", type: "Tome", ilk: "N'ihil", level: 1, khaoCost: 2, animaCost: 0, lyf: 0, deckLimit: 3, effectTag: null, effect: "Your adversary discards 1 card from their hand to the Necropolis.", experimental: true },
  { id: "ex_tome_nihil_l2", name: "The Unwritten Page", type: "Tome", ilk: "N'ihil", level: 2, khaoCost: 5, animaCost: 0, lyf: 0, deckLimit: 2, effectTag: "OPT", effect: "Nullify the effect of 1 being in play until your next Turn, and your adversary discards 1 card from their hand.", experimental: true },
  { id: "ex_tome_nihil_l3", name: "The Unwritten World", type: "Tome", ilk: "N'ihil", level: 3, khaoCost: 8, animaCost: 0, lyf: 0, deckLimit: 1, effectTag: null, effect: "Your adversary discards their hand to the Necropolis, then draws 3 cards from their Grimoire. Dim your own Anima by 2.", experimental: true },

  // O'mnia — Corruption (converts or steals instead of destroying)
  { id: "ex_tome_omnia_l1", name: "Coveted Whisper", type: "Tome", ilk: "O'mnia", level: 1, khaoCost: 3, animaCost: 0, lyf: 0, deckLimit: 3, effectTag: null, effect: "Copy the effect of the last Tome your adversary played this Turn and resolve it as your own.", experimental: true },
  { id: "ex_tome_omnia_l2", name: "Coveted Vessel", type: "Tome", ilk: "O'mnia", level: 2, khaoCost: 5, animaCost: 0, lyf: 0, deckLimit: 2, effectTag: "OP2", effect: "Take control of 1 adversary being with 4 or less Lyt until the end of your Turn. It cannot be Ruined while you control it.", experimental: true },
  { id: "ex_tome_omnia_l3", name: "Coveted Crown", type: "Tome", ilk: "O'mnia", level: 3, khaoCost: 9, animaCost: 0, lyf: 0, deckLimit: 1, effectTag: null, effect: "Take control of 1 adversary being and its Soul. Dim your own Anima by 4.", experimental: true },

  // Avarice — Hoarding (banking resources, guaranteed cost for guaranteed payoff)
  { id: "ex_tome_avarice_l1", name: "The Miser's Ledger", type: "Tome", ilk: "Avarice", level: 1, khaoCost: 2, animaCost: 0, lyf: 0, deckLimit: 3, effectTag: "ATV", effect: "While this Tome is in play, bank 1 Khao on it at the start of each of your Turns, to a limit of 3.", experimental: true },
  { id: "ex_tome_avarice_l2", name: "The Miser's Vault", type: "Tome", ilk: "Avarice", level: 2, khaoCost: 4, animaCost: 0, lyf: 0, deckLimit: 2, effectTag: "OPT", effect: "Devote 2 banked Khao from your Tomes: draw 2 cards from your Grimoire.", experimental: true },
  { id: "ex_tome_avarice_l3", name: "The Miser's Hoard Unsealed", type: "Tome", ilk: "Avarice", level: 3, khaoCost: 7, animaCost: 0, lyf: 0, deckLimit: 1, effectTag: null, effect: "Devote all Khao banked on your Tomes: 1 of your beings gains +1 Lyt and +1 Lyf for each Khao devoted this way.", experimental: true },

  // Solaea — Radiance (direct Lyt buffs and standing auras)
  { id: "ex_tome_solaea_l1", name: "Daybreak Anthem", type: "Tome", ilk: "Solaea", level: 1, khaoCost: 2, animaCost: 0, lyf: 0, deckLimit: 3, effectTag: null, effect: "1 of your beings gains +2 Lyt until the end of your Turn.", experimental: true },
  { id: "ex_tome_solaea_l2", name: "Noonfire Anthem", type: "Tome", ilk: "Solaea", level: 2, khaoCost: 5, animaCost: 0, lyf: 0, deckLimit: 2, effectTag: "ATV", effect: "While this Tome is in play, all of your beings gain +1 Lyt.", experimental: true },
  { id: "ex_tome_solaea_l3", name: "Anthem of the Undying Sun", type: "Tome", ilk: "Solaea", level: 3, khaoCost: 8, animaCost: 0, lyf: 0, deckLimit: 1, effectTag: null, effect: "1 of your beings gains +4 Lyt and cannot be dimmed until your next Turn. Dim your own Anima by 2.", experimental: true },

  // Lunaea — Tides (hand-cycling that gives now and takes later, or the reverse)
  { id: "ex_tome_lunaea_l1", name: "The Rising Tide", type: "Tome", ilk: "Lunaea", level: 1, khaoCost: 2, animaCost: 0, lyf: 0, deckLimit: 3, effectTag: null, effect: "Draw 2 cards from your Grimoire. At the start of your next Turn, discard 1 card from your hand.", experimental: true },
  { id: "ex_tome_lunaea_l2", name: "The Turning Tide", type: "Tome", ilk: "Lunaea", level: 2, khaoCost: 4, animaCost: 0, lyf: 0, deckLimit: 2, effectTag: "OPT", effect: "Discard 2 cards from your hand to the Necropolis. At the start of your next Turn, draw 3 cards from your Grimoire.", experimental: true },
  { id: "ex_tome_lunaea_l3", name: "The Drowning Tide", type: "Tome", ilk: "Lunaea", level: 3, khaoCost: 7, animaCost: 0, lyf: 0, deckLimit: 1, effectTag: null, effect: "Each Warlock discards their hand and draws 5 cards from their Grimoire. Your adversary draws theirs at the start of their next Turn instead.", experimental: true },

  // Dark Art — Blight (damage / Lyf-drain ticking over multiple Turns)
  { id: "ex_tome_darkart_l1", name: "Blight of the First Sore", type: "Tome", ilk: "Dark Art", level: 1, khaoCost: 3, animaCost: 0, lyf: 0, deckLimit: 3, effectTag: "ATV", effect: "Choose 1 adversary being: dim its Lyf by 1 at the start of each of your next 2 Turns.", experimental: true },
  { id: "ex_tome_darkart_l2", name: "Blight of the Creeping Rot", type: "Tome", ilk: "Dark Art", level: 2, khaoCost: 5, animaCost: 0, lyf: 0, deckLimit: 2, effectTag: "ATV", effect: "Choose 1 adversary being: dim its Lyf by 2 at the start of each of your next 2 Turns. If it is Ruined this way, dim your adversary's Anima by 1.", experimental: true },
  { id: "ex_tome_darkart_l3", name: "Blight of the Hollowing", type: "Tome", ilk: "Dark Art", level: 3, khaoCost: 8, animaCost: 0, lyf: 0, deckLimit: 1, effectTag: "ATV", effect: "Dim the Lyf of all adversary beings by 1 at the start of each of your next 3 Turns. Dim your own Anima by 3.", experimental: true },

  // Astrae — Orbit (board-wide effects touching every being in play)
  { id: "ex_tome_astrae_l1", name: "Orbit of Lesser Bodies", type: "Tome", ilk: "Astrae", level: 1, khaoCost: 3, animaCost: 0, lyf: 0, deckLimit: 3, effectTag: null, effect: "All beings in play are dimmed by 1 Lyt until the end of this Turn.", experimental: true },
  { id: "ex_tome_astrae_l2", name: "Orbit of Crossed Stars", type: "Tome", ilk: "Astrae", level: 2, khaoCost: 6, animaCost: 0, lyf: 0, deckLimit: 2, effectTag: "OP2", effect: "All beings in play gain +1 Lyf, and no being may be Ruined by an effect until your next Turn.", experimental: true },
  { id: "ex_tome_astrae_l3", name: "Orbit of the Silent Sphere", type: "Tome", ilk: "Astrae", level: 3, khaoCost: 9, animaCost: 0, lyf: 0, deckLimit: 1, effectTag: null, effect: "Dim the Lyf of every being in play by 3, your own included. Dim your own Anima by 2.", experimental: true },

];

export const EX_STARTER_DECKS = [
  KYRIUM_PILOT.deck,

  {
    id: "exdeck_abyssia",
    name: "The House at Midnight",
    description: "A Beast Shadow Gambling deck that wagers Lyf, Anima, and dice until the odds themselves owe you money.",
    ilk: "Shadow Gambling",
    color: "#1a2f4a",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_temptation_manifested: 1, tome_nevermore: 1, tome_casino_royale: 1,
      "abyssia_beb": 5, "abyssia_champion": 3, "abyssia_lord": 3,
    },
    luxmoire: { "p_abyssia_1": 5, "p_abyssia_2": 4, "p_abyssia_3": 3, "u_abyssia_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_shadowgambling", "ex_escling_shadowgambling", "ex_escling_shadowgambling"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_aealix",
    name: "Doctrine of the False Sun",
    description: "A Humanoid N'ihil deck of radiant erasure — strip the adversary's hand and options while the beautiful light does its quiet work.",
    ilk: "N'ihil",
    color: "#c7d3e8",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_nihil_l1: 3, ex_tome_nihil_l2: 2, ex_tome_nihil_l3: 1,
      "aealix_beb": 5, "aealix_champion": 3, "aealix_lord": 3,
    },
    luxmoire: { "p_aealix_1": 5, "p_aealix_2": 4, "p_aealix_3": 3, "u_aealix_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_nihil", "ex_escling_nihil", "ex_escling_nihil"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_aeolix",
    name: "The Reversed Scripture",
    description: "A Humanoid O'mnia deck that corrupts and converts the adversary's beings and spells in the name of a moon that is secretly a sun.",
    ilk: "O'mnia",
    color: "#2b3a8f",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_omnia_l1: 3, ex_tome_omnia_l2: 2, ex_tome_omnia_l3: 1,
      "aeolix_beb": 5, "aeolix_champion": 3, "aeolix_lord": 3,
    },
    luxmoire: { "p_aeolix_1": 5, "p_aeolix_2": 4, "p_aeolix_3": 3, "u_aeolix_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_omnia", "ex_escling_omnia", "ex_escling_omnia"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_aeternavis",
    name: "Dawn Without End",
    description: "A Beast O'mnia deck that steals Lyt and spells from the fallen and refuses, personally, to stay ruined.",
    ilk: "O'mnia",
    color: "#b8641b",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_omnia_l1: 3, ex_tome_omnia_l2: 2, ex_tome_omnia_l3: 1,
      "aeternavis_beb": 5, "aeternavis_champion": 3, "aeternavis_lord": 3,
    },
    luxmoire: { "p_aeternavis_1": 5, "p_aeternavis_2": 4, "p_aeternavis_3": 3, "u_aeternavis_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_omnia", "ex_escling_omnia", "ex_escling_omnia"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_auroraeth",
    name: "Garden of the Daystar",
    description: "A Beast Astrae deck of board-wide blooms — every being in play feels the noon at once.",
    ilk: "Astrae",
    color: "#d4920a",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_astrae_l1: 3, ex_tome_astrae_l2: 2, ex_tome_astrae_l3: 1,
      "auroraeth_beb": 5, "auroraeth_champion": 3, "auroraeth_lord": 3,
    },
    luxmoire: { "p_auroraeth_1": 5, "p_auroraeth_2": 4, "p_auroraeth_3": 3, "u_auroraeth_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_astrae", "ex_escling_astrae", "ex_escling_astrae"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_bunnovian",
    name: "No One Left Below",
    description: "A Beast Phantom deck of high-Lyf healers that outlasts everything and resurrects the rest.",
    ilk: "Phantom",
    color: "#3d7a78",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_boots_wing_walking: 1, tome_omnis_corruption: 1, tome_blood_money: 1, tome_temptation_manifested: 1,
      "bunnovian_beb": 5, "bunnovian_champion": 3, "bunnovian_lord": 3,
    },
    luxmoire: { "p_bunnovian_1": 5, "p_bunnovian_2": 4, "p_bunnovian_3": 3, "u_bunnovian_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_phantom", "escling_phantom", "escling_phantom"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_caelumari",
    name: "The Ocean Overhead",
    description: "A Beast Lunaea deck of tide-driven hand-cycling — draw deep now and pay the undertow later.",
    ilk: "Lunaea",
    color: "#155e75",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_lunaea_l1: 3, ex_tome_lunaea_l2: 2, ex_tome_lunaea_l3: 1,
      "caelumari_beb": 5, "caelumari_champion": 3, "caelumari_lord": 3,
    },
    luxmoire: { "p_caelumari_1": 5, "p_caelumari_2": 4, "p_caelumari_3": 3, "u_caelumari_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_lunaea", "ex_escling_lunaea", "ex_escling_lunaea"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_calypsis",
    name: "Court of Two Lights",
    description: "A Humanoid Lunaea deck that cycles the hand like a tide, borrowing from the sun on the water and repaying the moon below.",
    ilk: "Lunaea",
    color: "#4b3d8f",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_lunaea_l1: 3, ex_tome_lunaea_l2: 2, ex_tome_lunaea_l3: 1,
      "calypsis_beb": 5, "calypsis_champion": 3, "calypsis_lord": 3,
    },
    luxmoire: { "p_calypsis_1": 5, "p_calypsis_2": 4, "p_calypsis_3": 3, "u_calypsis_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_lunaea", "ex_escling_lunaea", "ex_escling_lunaea"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_carcharion",
    name: "Open Water",
    description: "A Beast N'ihil deck of surgical erasure — silence, nullify, and unmake until nothing is left in the water.",
    ilk: "N'ihil",
    color: "#26415e",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_nihil_l1: 3, ex_tome_nihil_l2: 2, ex_tome_nihil_l3: 1,
      "carcharion_beb": 5, "carcharion_champion": 3, "carcharion_lord": 3,
    },
    luxmoire: { "p_carcharion_1": 5, "p_carcharion_2": 4, "p_carcharion_3": 3, "u_carcharion_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_nihil", "ex_escling_nihil", "ex_escling_nihil"],
    curios: [], prophecy: null,
  },


  {
    id: "exdeck_carnifex",
    name: "The Iron Vesper's Congregation",
    description: "A Humanoid O'mnia deck that steals, copies, and converts the adversary's own tools into worship.",
    ilk: "O'mnia",
    color: "#5a1c1c",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_omnia_l1: 3, ex_tome_omnia_l2: 2, ex_tome_omnia_l3: 1,
      "carnifex_beb": 5, "carnifex_champion": 3, "carnifex_lord": 3,
    },
    luxmoire: { "p_carnifex_1": 5, "p_carnifex_2": 4, "p_carnifex_3": 3, "u_carnifex_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_omnia", "ex_escling_omnia", "ex_escling_omnia"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_devorix",
    name: "Orbit of the Open Maw",
    description: "A Beast Astrae deck that drags every being in play toward the same hungry center with board-wide dims.",
    ilk: "Astrae",
    color: "#3b1f4e",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_astrae_l1: 3, ex_tome_astrae_l2: 2, ex_tome_astrae_l3: 1,
      "devorix_beb": 5, "devorix_champion": 3, "devorix_lord": 3,
    },
    luxmoire: { "p_devorix_1": 5, "p_devorix_2": 4, "p_devorix_3": 3, "u_devorix_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_astrae", "ex_escling_astrae", "ex_escling_astrae"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_embearaea",
    name: "The Orchard That Bites",
    description: "A Beast Shadow Gambling deck where every firecracker berry is a called coin or die that pays big or bursts in your paws.",
    ilk: "Shadow Gambling",
    color: "#7a2e1d",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_temptation_manifested: 1, tome_nevermore: 1, tome_gambits_lucky_cat: 1,
      "embearaea_beb": 5, "embearaea_champion": 3, "embearaea_lord": 3,
    },
    luxmoire: { "p_embearaea_1": 5, "p_embearaea_2": 4, "p_embearaea_3": 3, "u_embearaea_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_shadowgambling", "ex_escling_shadowgambling", "ex_escling_shadowgambling"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_geulaoin",
    name: "The Crimson Ledger",
    description: "A Beast Avarice deck that banks Khao under its red wings and cashes the whole balance out at once.",
    ilk: "Avarice",
    color: "#8f1f2f",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_avarice_l1: 3, ex_tome_avarice_l2: 2, ex_tome_avarice_l3: 1,
      "geulaoin_beb": 5, "geulaoin_champion": 3, "geulaoin_lord": 3,
    },
    luxmoire: { "p_geulaoin_1": 5, "p_geulaoin_2": 4, "p_geulaoin_3": 3, "u_geulaoin_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_avarice", "ex_escling_avarice", "ex_escling_avarice"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_gorgxonox",
    name: "Glutton of the Blightfields",
    description: "A Beast Avarice deck that pays guaranteed costs for guaranteed muscle, banking Khao into one enormous problem.",
    ilk: "Avarice",
    color: "#4a3b14",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_avarice_l1: 3, ex_tome_avarice_l2: 2, ex_tome_avarice_l3: 1,
      "gorgxonox_beb": 5, "gorgxonox_champion": 3, "gorgxonox_lord": 3,
    },
    luxmoire: { "p_gorgxonox_1": 5, "p_gorgxonox_2": 4, "p_gorgxonox_3": 3, "u_gorgxonox_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_avarice", "ex_escling_avarice", "ex_escling_avarice"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_hyalayri",
    name: "Hymn of the Infinaite",
    description: "A Humanoid N'ihil deck that erases hands and silences effects until the adversary has nothing left to say.",
    ilk: "N'ihil",
    color: "#6e6352",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_nihil_l1: 3, ex_tome_nihil_l2: 2, ex_tome_nihil_l3: 1,
      "hyalayri_beb": 5, "hyalayri_champion": 3, "hyalayri_lord": 3,
    },
    luxmoire: { "p_hyalayri_1": 5, "p_hyalayri_2": 4, "p_hyalayri_3": 3, "u_hyalayri_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_nihil", "ex_escling_nihil", "ex_escling_nihil"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_kraalok",
    name: "The Patient Rot",
    description: "A Beast Dark Art deck that marks one target and drains its Lyf a tick at a time until nothing else matters.",
    ilk: "Dark Art",
    color: "#2f4032",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_darkart_l1: 3, ex_tome_darkart_l2: 2, ex_tome_darkart_l3: 1,
      "kraalok_beb": 5, "kraalok_champion": 3, "kraalok_lord": 3,
    },
    luxmoire: { "p_kraalok_1": 5, "p_kraalok_2": 4, "p_kraalok_3": 3, "u_kraalok_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_darkart", "ex_escling_darkart", "ex_escling_darkart"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_kyrium_dark",
    name: "The Sealed Stacks",
    description: "A Beast Dark Art deck of living library shadow whose blight ticks arrive like overdue notices, page by page.",
    ilk: "Dark Art",
    color: "#241b33",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_darkart_l1: 3, ex_tome_darkart_l2: 2, ex_tome_darkart_l3: 1,
      "kyrium_dark_beb": 5, "kyrium_dark_champion": 3, "kyrium_dark_lord": 3,
    },
    luxmoire: { "p_kyrium_dark_1": 5, "p_kyrium_dark_2": 4, "p_kyrium_dark_3": 3, "u_kyrium_dark_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_darkart", "ex_escling_darkart", "ex_escling_darkart"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_lyraei",
    name: "Caelestis in Bloom",
    description: "A Humanoid Arcane Art deck of tiny glowing wardweavers who answer every adversary spell with a counter and a mend.",
    ilk: "Arcane Art",
    color: "#1f4d5a",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_kyriums_wardglass: 3, tome_archons_rebuke: 2, tome_great_librarys_seal: 1,
      "lyraei_beb": 5, "lyraei_champion": 3, "lyraei_lord": 3,
    },
    luxmoire: { "p_lyraei_1": 5, "p_lyraei_2": 4, "p_lyraei_3": 3, "u_lyraei_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_arcane_art", "escling_arcane_art", "escling_arcane_art"],
    curios: [], prophecy: null,
  },


  {
    id: "exdeck_lyrixi",
    name: "The Twilight Choir",
    description: "An Arcane Art Beast deck that answers every adversary spell with a countersong while its Lyf-heavy flock outlasts the dark.",
    ilk: "Arcane Art",
    color: "#2a1e5c",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_kyriums_wardglass: 3, tome_archons_rebuke: 2, tome_great_librarys_seal: 1,
      "lyrixi_beb": 5, "lyrixi_champion": 3, "lyrixi_lord": 3,
    },
    luxmoire: { "p_lyrixi_1": 5, "p_lyrixi_2": 4, "p_lyrixi_3": 3, "u_lyrixi_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_arcane_art", "escling_arcane_art", "escling_arcane_art"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_magmantus",
    name: "The Standing Eruption",
    description: "A Solaea Machine deck of lava golems that stacks radiant Lyt auras until the eruption simply never stops.",
    ilk: "Solaea",
    color: "#7a2408",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_solaea_l1: 3, ex_tome_solaea_l2: 2, ex_tome_solaea_l3: 1,
      "magmantus_beb": 5, "magmantus_champion": 3, "magmantus_lord": 3,
    },
    luxmoire: { "p_magmantus_1": 5, "p_magmantus_2": 4, "p_magmantus_3": 3, "u_magmantus_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_solaea", "ex_escling_solaea", "ex_escling_solaea"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_malumora",
    name: "The Opposite of a Song",
    description: "A Dark Art Humanoid deck of tiny devils whose blight ticks Lyf and Anima away a little more every Turn.",
    ilk: "Dark Art",
    color: "#3b0f2e",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_darkart_l1: 3, ex_tome_darkart_l2: 2, ex_tome_darkart_l3: 1,
      "malumora_beb": 5, "malumora_champion": 3, "malumora_lord": 3,
    },
    luxmoire: { "p_malumora_1": 5, "p_malumora_2": 4, "p_malumora_3": 3, "u_malumora_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_darkart", "ex_escling_darkart", "ex_escling_darkart"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_mygzori",
    name: "Letters to the God Above",
    description: "An Astrae Humanoid deck whose orbit-wide effects touch every being in play, then tilt the whole board upward in its favor.",
    ilk: "Astrae",
    color: "#123a63",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_astrae_l1: 3, ex_tome_astrae_l2: 2, ex_tome_astrae_l3: 1,
      "mygzori_beb": 5, "mygzori_champion": 3, "mygzori_lord": 3,
    },
    luxmoire: { "p_mygzori_1": 5, "p_mygzori_2": 4, "p_mygzori_3": 3, "u_mygzori_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_astrae", "ex_escling_astrae", "ex_escling_astrae"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_noxura",
    name: "The Writer Alone",
    description: "A Lunaea Humanoid deck that cycles its hand on the tides and wagers the writer's favor on a well-called coin.",
    ilk: "Lunaea",
    color: "#1c4d4a",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_lunaea_l1: 3, ex_tome_lunaea_l2: 2, ex_tome_lunaea_l3: 1,
      "noxura_beb": 5, "noxura_champion": 3, "noxura_lord": 3,
    },
    luxmoire: { "p_noxura_1": 5, "p_noxura_2": 4, "p_noxura_3": 3, "u_noxura_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_lunaea", "ex_escling_lunaea", "ex_escling_lunaea"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_rajaavi",
    name: "The Throne That Walks",
    description: "A Phantom Beast deck of royal tigers that strike the adversary's Anima directly and command the board to kneel.",
    ilk: "Phantom",
    color: "#5c4a12",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_boots_wing_walking: 1, tome_omnis_corruption: 1, tome_blood_money: 1, tome_temptation_manifested: 1,
      "rajaavi_beb": 5, "rajaavi_champion": 3, "rajaavi_lord": 3,
    },
    luxmoire: { "p_rajaavi_1": 5, "p_rajaavi_2": 4, "p_rajaavi_3": 3, "u_rajaavi_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_phantom", "escling_phantom", "escling_phantom"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_rapaxi",
    name: "Cathedral of the Hoard",
    description: "An Avarice Beast deck of Voracian reptiles that banks Khao as sacrament and cashes guaranteed payoffs off a full treasury.",
    ilk: "Avarice",
    color: "#0e5236",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_avarice_l1: 3, ex_tome_avarice_l2: 2, ex_tome_avarice_l3: 1,
      "rapaxi_beb": 5, "rapaxi_champion": 3, "rapaxi_lord": 3,
    },
    luxmoire: { "p_rapaxi_1": 5, "p_rapaxi_2": 4, "p_rapaxi_3": 3, "u_rapaxi_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_avarice", "ex_escling_avarice", "ex_escling_avarice"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_riven",
    name: "The Unbroken Line",
    description: "An Arcane Art Humanoid deck of star-taught wardweavers that nullifies each Kraalok-style assault as it is played.",
    ilk: "Arcane Art",
    color: "#43506e",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_kyriums_wardglass: 3, tome_archons_rebuke: 2, tome_great_librarys_seal: 1,
      "riven_beb": 5, "riven_champion": 3, "riven_lord": 3,
    },
    luxmoire: { "p_riven_1": 5, "p_riven_2": 4, "p_riven_3": 3, "u_riven_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_arcane_art", "escling_arcane_art", "escling_arcane_art"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_rosaluna",
    name: "The Midnight Bloom",
    description: "A Lunaea Beast deck of moon-roses that gives by night and takes by day, blooming the Malarosa when the tide is highest.",
    ilk: "Lunaea",
    color: "#6e2440",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_lunaea_l1: 3, ex_tome_lunaea_l2: 2, ex_tome_lunaea_l3: 1,
      "rosaluna_beb": 5, "rosaluna_champion": 3, "rosaluna_lord": 3,
    },
    luxmoire: { "p_rosaluna_1": 5, "p_rosaluna_2": 4, "p_rosaluna_3": 3, "u_rosaluna_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_lunaea", "ex_escling_lunaea", "ex_escling_lunaea"],
    curios: [], prophecy: null,
  },


  {
    id: "exdeck_skuryz",
    name: "The Gutter's Wager",
    description: "A Phantom Beast swarm that strikes at Anima and flips gutter-luck coins for the big payoffs.",
    ilk: "Phantom",
    color: "#3d2b1f",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_boots_wing_walking: 1, tome_omnis_corruption: 1, tome_blood_money: 1, tome_temptation_manifested: 1,
      "skuryz_beb": 5, "skuryz_champion": 3, "skuryz_lord": 3,
    },
    luxmoire: { "p_skuryz_1": 5, "p_skuryz_2": 4, "p_skuryz_3": 3, "u_skuryz_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_phantom", "escling_phantom", "escling_phantom"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_slijj",
    name: "The Long Interment",
    description: "A Dark Art Humanoid blight deck that plants rot and lets the Lyf-drain tick across the Turns.",
    ilk: "Dark Art",
    color: "#2f4426",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_darkart_l1: 3, ex_tome_darkart_l2: 2, ex_tome_darkart_l3: 1,
      "slijj_beb": 5, "slijj_champion": 3, "slijj_lord": 3,
    },
    luxmoire: { "p_slijj_1": 5, "p_slijj_2": 4, "p_slijj_3": 3, "u_slijj_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_darkart", "ex_escling_darkart", "ex_escling_darkart"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_trunaran",
    name: "The Unmoved Mountain",
    description: "A Phantom Beast fortress deck that stacks Lyf until nothing on the other side of the board matters.",
    ilk: "Phantom",
    color: "#4a4a52",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_boots_wing_walking: 1, tome_omnis_corruption: 1, tome_blood_money: 1, tome_temptation_manifested: 1,
      "trunaran_beb": 5, "trunaran_champion": 3, "trunaran_lord": 3,
    },
    luxmoire: { "p_trunaran_1": 5, "p_trunaran_2": 4, "p_trunaran_3": 3, "u_trunaran_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_phantom", "escling_phantom", "escling_phantom"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_tzhahws",
    name: "The Captain's Hoard",
    description: "An Avarice Humanoid plunder deck that banks stolen cards beneath its Souls and cashes the hoard in all at once.",
    ilk: "Avarice",
    color: "#6b4d1f",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_avarice_l1: 3, ex_tome_avarice_l2: 2, ex_tome_avarice_l3: 1,
      "tzhahws_beb": 5, "tzhahws_champion": 3, "tzhahws_lord": 3,
    },
    luxmoire: { "p_tzhahws_1": 5, "p_tzhahws_2": 4, "p_tzhahws_3": 3, "u_tzhahws_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_avarice", "ex_escling_avarice", "ex_escling_avarice"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_ursaeva",
    name: "The Winter That Endured",
    description: "A Phantom Beast guardian deck that soaks every blow on one great Lyf wall while the den survives behind it.",
    ilk: "Phantom",
    color: "#1f3a4d",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_boots_wing_walking: 1, tome_omnis_corruption: 1, tome_blood_money: 1, tome_temptation_manifested: 1,
      "ursaeva_beb": 5, "ursaeva_champion": 3, "ursaeva_lord": 3,
    },
    luxmoire: { "p_ursaeva_1": 5, "p_ursaeva_2": 4, "p_ursaeva_3": 3, "u_ursaeva_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_phantom", "escling_phantom", "escling_phantom"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_valtorix",
    name: "The Sun-Bleached Stampede",
    description: "A Dark Art Beast deck that rides down one target at a time with blight that keeps trampling for Turns after the charge.",
    ilk: "Dark Art",
    color: "#7a6a3a",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_darkart_l1: 3, ex_tome_darkart_l2: 2, ex_tome_darkart_l3: 1,
      "valtorix_beb": 5, "valtorix_champion": 3, "valtorix_lord": 3,
    },
    luxmoire: { "p_valtorix_1": 5, "p_valtorix_2": 4, "p_valtorix_3": 3, "u_valtorix_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_darkart", "ex_escling_darkart", "ex_escling_darkart"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_velkor",
    name: "The Bank of Rolling Grey",
    description: "An Arcane Art Beast wardweave deck of living mist that answers every Tome and Impera before it lands.",
    ilk: "Arcane Art",
    color: "#37474f",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_kyriums_wardglass: 3, tome_archons_rebuke: 2, tome_great_librarys_seal: 1,
      "velkor_beb": 5, "velkor_champion": 3, "velkor_lord": 3,
    },
    luxmoire: { "p_velkor_1": 5, "p_velkor_2": 4, "p_velkor_3": 3, "u_velkor_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_arcane_art", "escling_arcane_art", "escling_arcane_art"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_vyren",
    name: "The Serpent Beneath the Moons",
    description: "A Lunaea Beast tide deck that cycles the hand in great draws now and pays the Necropolis its due later.",
    ilk: "Lunaea",
    color: "#2b2b5e",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_lunaea_l1: 3, ex_tome_lunaea_l2: 2, ex_tome_lunaea_l3: 1,
      "vyren_beb": 5, "vyren_champion": 3, "vyren_lord": 3,
    },
    luxmoire: { "p_vyren_1": 5, "p_vyren_2": 4, "p_vyren_3": 3, "u_vyren_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_lunaea", "ex_escling_lunaea", "ex_escling_lunaea"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_xolibrix",
    name: "The Blur That Heals",
    description: "A Phantom Beast healer deck whose hummingbird spirits restore Anima and Lyf faster than the adversary can dim them.",
    ilk: "Phantom",
    color: "#155e4b",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_boots_wing_walking: 1, tome_omnis_corruption: 1, tome_blood_money: 1, tome_temptation_manifested: 1,
      "xolibrix_beb": 5, "xolibrix_champion": 3, "xolibrix_lord": 3,
    },
    luxmoire: { "p_xolibrix_1": 5, "p_xolibrix_2": 4, "p_xolibrix_3": 3, "u_xolibrix_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_phantom", "escling_phantom", "escling_phantom"],
    curios: [], prophecy: null,
  },


  {
    id: "exdeck_zriidians",
    name: "The Counterwheel Turns",
    description: "A Humanoid Astrae deck that tilts every being on the board at once, then wins the standstill it created.",
    ilk: "Astrae",
    color: "#1c2e6b",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_astrae_l1: 3, ex_tome_astrae_l2: 2, ex_tome_astrae_l3: 1,
      "zriidians_beb": 5, "zriidians_champion": 3, "zriidians_lord": 3,
    },
    luxmoire: { "p_zriidians_1": 5, "p_zriidians_2": 4, "p_zriidians_3": 3, "u_zriidians_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_astrae", "ex_escling_astrae", "ex_escling_astrae"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_zylaraeon",
    name: "Ruin the Writer",
    description: "A Humanoid Avarice deck that banks Khao like a devil hoarding evidence, then spends it all on one accusation.",
    ilk: "Avarice",
    color: "#5c1a1a",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_avarice_l1: 3, ex_tome_avarice_l2: 2, ex_tome_avarice_l3: 1,
      "zylaraeon_beb": 5, "zylaraeon_champion": 3, "zylaraeon_lord": 3,
    },
    luxmoire: { "p_zylaraeon_1": 5, "p_zylaraeon_2": 4, "p_zylaraeon_3": 3, "u_zylaraeon_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_avarice", "ex_escling_avarice", "ex_escling_avarice"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_dg0_nihil",
    name: "Mercy's Blank Page",
    description: "A Machine N'ihil deck that heals by erasing — high-Lyf walls behind hand-stripping, silencing mercy.",
    ilk: "N'ihil",
    color: "#3a3f4a",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_nihil_l1: 3, ex_tome_nihil_l2: 2, ex_tome_nihil_l3: 1,
      "dg0_nihil_beb": 5, "dg0_nihil_champion": 3, "dg0_nihil_lord": 3,
    },
    luxmoire: { "p_dg0_nihil_1": 5, "p_dg0_nihil_2": 4, "p_dg0_nihil_3": 3, "u_dg0_nihil_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_nihil", "ex_escling_nihil", "ex_escling_nihil"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_z4z_omnia",
    name: "The Turned Blade",
    description: "A Machine O'mnia deck that pays Anima to steal strikes and beings, destroying nothing it can convert.",
    ilk: "O'mnia",
    color: "#4a1258",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_omnia_l1: 3, ex_tome_omnia_l2: 2, ex_tome_omnia_l3: 1,
      "z4z_omnia_beb": 5, "z4z_omnia_champion": 3, "z4z_omnia_lord": 3,
    },
    luxmoire: { "p_z4z_omnia_1": 5, "p_z4z_omnia_2": 4, "p_z4z_omnia_3": 3, "u_z4z_omnia_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_omnia", "ex_escling_omnia", "ex_escling_omnia"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_antz_avarice",
    name: "The Hoard Beneath the Hill",
    description: "A Machine Avarice deck of hoard-colony Antdroids that bank Anima and Khao until the last day, then open the reservoir.",
    ilk: "Avarice",
    color: "#6b4a12",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_avarice_l1: 3, ex_tome_avarice_l2: 2, ex_tome_avarice_l3: 1,
      "antz_avarice_beb": 5, "antz_avarice_champion": 3, "antz_avarice_lord": 3,
    },
    luxmoire: { "p_antz_avarice_1": 5, "p_antz_avarice_2": 4, "p_antz_avarice_3": 3, "u_antz_avarice_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_avarice", "ex_escling_avarice", "ex_escling_avarice"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_nyfaux_arcane",
    name: "The Ninth Ward",
    description: "A Beast Arcane Art deck of warded foxes that answer every adversary Tome and Impera the instant it is played.",
    ilk: "Arcane Art",
    color: "#0f4d43",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_kyriums_wardglass: 3, tome_archons_rebuke: 2, tome_great_librarys_seal: 1,
      "nyfaux_arcane_beb": 5, "nyfaux_arcane_champion": 3, "nyfaux_arcane_lord": 3,
    },
    luxmoire: { "p_nyfaux_arcane_1": 5, "p_nyfaux_arcane_2": 4, "p_nyfaux_arcane_3": 3, "u_nyfaux_arcane_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_arcane_art", "escling_arcane_art", "escling_arcane_art"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_nyfaux_gambling",
    name: "The House Always",
    description: "A Beast Shadow Gambling deck of coin-flipping foxes that wager Anima on loaded odds and let the House collect.",
    ilk: "Shadow Gambling",
    color: "#7a1245",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_temptation_manifested: 1, tome_casino_royale: 1, tome_snake_eyes: 1,
      "nyfaux_gambling_beb": 5, "nyfaux_gambling_champion": 3, "nyfaux_gambling_lord": 3,
    },
    luxmoire: { "p_nyfaux_gambling_1": 5, "p_nyfaux_gambling_2": 4, "p_nyfaux_gambling_3": 3, "u_nyfaux_gambling_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_shadowgambling", "ex_escling_shadowgambling", "ex_escling_shadowgambling"],
    curios: [], prophecy: null,
  },


  {
    id: "exdeck_h_phantom",
    name: "The Unquiet Dead of H-27",
    description: "Humanoid Phantom spirits that slip Intangible and strike the adversary's Anima directly.",
    ilk: "Phantom",
    color: "#2b1d45",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_boots_wing_walking: 1, tome_omnis_corruption: 1, tome_blood_money: 1, tome_temptation_manifested: 1,
      "h_phantom_beb": 5, "h_phantom_champion": 3, "h_phantom_lord": 3,
    },
    luxmoire: { "p_h_phantom_1": 5, "p_h_phantom_2": 4, "p_h_phantom_3": 3, "u_h_phantom_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_phantom", "escling_phantom", "escling_phantom"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_h_time",
    name: "The Appointed Hour",
    description: "Humanoid Time-keepers that stall, slow the adversary, and Resurrect on schedule.",
    ilk: "Time",
    color: "#1b3a3f",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_boots_wing_walking: 1, tome_omnis_corruption: 1, tome_blood_money: 1, tome_temptation_manifested: 1,
      "h_time_beb": 5, "h_time_champion": 3, "h_time_lord": 3,
    },
    luxmoire: { "p_h_time_1": 5, "p_h_time_2": 4, "p_h_time_3": 3, "u_h_time_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_time", "escling_time", "escling_time"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_h_dragon",
    name: "Century Rain",
    description: "Humanoid Dragon-blooded aggressors that trade everything for raw Lyt and falling damage.",
    ilk: "Dragon",
    color: "#4a1f14",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_boots_wing_walking: 1, tome_omnis_corruption: 1, tome_blood_money: 1, tome_temptation_manifested: 1,
      "h_dragon_beb": 5, "h_dragon_champion": 3, "h_dragon_lord": 3,
    },
    luxmoire: { "p_h_dragon_1": 5, "p_h_dragon_2": 4, "p_h_dragon_3": 3, "u_h_dragon_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_dragon", "escling_dragon", "escling_dragon"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_h_nihil",
    name: "The Finishing School",
    description: "Humanoid N'ihil erasers that strip the adversary's hand and silence their beings.",
    ilk: "N'ihil",
    color: "#14141c",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_nihil_l1: 3, ex_tome_nihil_l2: 2, ex_tome_nihil_l3: 1,
      "h_nihil_beb": 5, "h_nihil_champion": 3, "h_nihil_lord": 3,
    },
    luxmoire: { "p_h_nihil_1": 5, "p_h_nihil_2": 4, "p_h_nihil_3": 3, "u_h_nihil_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_nihil", "ex_escling_nihil", "ex_escling_nihil"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_h_omnia",
    name: "Borrowed Light",
    description: "Humanoid O'mnia siphons that steal Lyt, Lyf, and whole beings rather than ruin them.",
    ilk: "O'mnia",
    color: "#3d1430",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_omnia_l1: 3, ex_tome_omnia_l2: 2, ex_tome_omnia_l3: 1,
      "h_omnia_beb": 5, "h_omnia_champion": 3, "h_omnia_lord": 3,
    },
    luxmoire: { "p_h_omnia_1": 5, "p_h_omnia_2": 4, "p_h_omnia_3": 3, "u_h_omnia_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_omnia", "ex_escling_omnia", "ex_escling_omnia"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_h_avarice",
    name: "The Ledger of Ages",
    description: "Humanoid Avarice bankers that stack Souls and delayed draws for guaranteed compound payoffs.",
    ilk: "Avarice",
    color: "#4a3a10",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_avarice_l1: 3, ex_tome_avarice_l2: 2, ex_tome_avarice_l3: 1,
      "h_avarice_beb": 5, "h_avarice_champion": 3, "h_avarice_lord": 3,
    },
    luxmoire: { "p_h_avarice_1": 5, "p_h_avarice_2": 4, "p_h_avarice_3": 3, "u_h_avarice_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_avarice", "ex_escling_avarice", "ex_escling_avarice"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_h_solaea",
    name: "Noon Without End",
    description: "Humanoid Solaea sun-cultists that stack standing Lyt auras into one blinding push.",
    ilk: "Solaea",
    color: "#6b3410",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_solaea_l1: 3, ex_tome_solaea_l2: 2, ex_tome_solaea_l3: 1,
      "h_solaea_beb": 5, "h_solaea_champion": 3, "h_solaea_lord": 3,
    },
    luxmoire: { "p_h_solaea_1": 5, "p_h_solaea_2": 4, "p_h_solaea_3": 3, "u_h_solaea_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_solaea", "ex_escling_solaea", "ex_escling_solaea"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_h_lunaea",
    name: "The Gibbous Tide",
    description: "Humanoid Lunaea moon-sworn that cycle the hand in tides, giving now and taking later.",
    ilk: "Lunaea",
    color: "#16255a",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_lunaea_l1: 3, ex_tome_lunaea_l2: 2, ex_tome_lunaea_l3: 1,
      "h_lunaea_beb": 5, "h_lunaea_champion": 3, "h_lunaea_lord": 3,
    },
    luxmoire: { "p_h_lunaea_1": 5, "p_h_lunaea_2": 4, "p_h_lunaea_3": 3, "u_h_lunaea_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_lunaea", "ex_escling_lunaea", "ex_escling_lunaea"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_h_darkart",
    name: "The Long Cooling",
    description: "Humanoid Dark Art blighters that lay slow curses and win by ticking ruin over many Turns.",
    ilk: "Dark Art",
    color: "#1f2e1a",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_darkart_l1: 3, ex_tome_darkart_l2: 2, ex_tome_darkart_l3: 1,
      "h_darkart_beb": 5, "h_darkart_champion": 3, "h_darkart_lord": 3,
    },
    luxmoire: { "p_h_darkart_1": 5, "p_h_darkart_2": 4, "p_h_darkart_3": 3, "u_h_darkart_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_darkart", "ex_escling_darkart", "ex_escling_darkart"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_h_arcane",
    name: "The Ring Closes",
    description: "Humanoid Arcane Art wardweavers that answer every adversary Tome and Impera in flight.",
    ilk: "Arcane Art",
    color: "#123a5c",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_kyriums_wardglass: 3, tome_archons_rebuke: 2, tome_great_librarys_seal: 1,
      "h_arcane_beb": 5, "h_arcane_champion": 3, "h_arcane_lord": 3,
    },
    luxmoire: { "p_h_arcane_1": 5, "p_h_arcane_2": 4, "p_h_arcane_3": 3, "u_h_arcane_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_arcane_art", "escling_arcane_art", "escling_arcane_art"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_h_gambling",
    name: "The House Always Rebuilds",
    description: "Humanoid Shadow Gambling sharps that wager Anima on coins and dice the shadows underwrite.",
    ilk: "Shadow Gambling",
    color: "#3f0f1e",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_temptation_manifested: 1, tome_nevermore: 1, tome_casino_royale: 1,
      "h_gambling_beb": 5, "h_gambling_champion": 3, "h_gambling_lord": 3,
    },
    luxmoire: { "p_h_gambling_1": 5, "p_h_gambling_2": 4, "p_h_gambling_3": 3, "u_h_gambling_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_shadowgambling", "ex_escling_shadowgambling", "ex_escling_shadowgambling"],
    curios: [], prophecy: null,
  },


  // ── BATCH H — the missing second-Class lines (caP'n, 2026-09-03) ──

  {
    id: "exdeck_abyssia_lunaea",
    name: "Abyssia Under the Moon-Tide",
    description: "A Beast Lunaea deck that cycles the hand like a tide — drawing deep now and paying it back on the ebb.",
    ilk: "Lunaea",
    color: "#1B2A4A",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_lunaea_l1: 3, ex_tome_lunaea_l2: 2, ex_tome_lunaea_l3: 1,
      "abyssia_lunaea_beb": 5, "abyssia_lunaea_champion": 3, "abyssia_lunaea_lord": 3,
    },
    luxmoire: { "p_abyssia_lunaea_1": 5, "p_abyssia_lunaea_2": 4, "p_abyssia_lunaea_3": 3, "u_abyssia_lunaea_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_lunaea", "ex_escling_lunaea", "ex_escling_lunaea"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_aealix_lunaea",
    name: "Aealix Moonlit Nothing",
    description: "A Humanoid Lunaea deck that trades the hand away and back, borrowing power from what is not there.",
    ilk: "Lunaea",
    color: "#2C2255",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_lunaea_l1: 3, ex_tome_lunaea_l2: 2, ex_tome_lunaea_l3: 1,
      "aealix_lunaea_beb": 5, "aealix_lunaea_champion": 3, "aealix_lunaea_lord": 3,
    },
    luxmoire: { "p_aealix_lunaea_1": 5, "p_aealix_lunaea_2": 4, "p_aealix_lunaea_3": 3, "u_aealix_lunaea_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_lunaea", "ex_escling_lunaea", "ex_escling_lunaea"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_aeolix_solaea",
    name: "Aeolix, Facing Forward",
    description: "A Humanoid Solaea deck of standing auras and Lyt sermons from the faith that finally turned around.",
    ilk: "Solaea",
    color: "#7A3803",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_solaea_l1: 3, ex_tome_solaea_l2: 2, ex_tome_solaea_l3: 1,
      "aeolix_solaea_beb": 5, "aeolix_solaea_champion": 3, "aeolix_solaea_lord": 3,
    },
    luxmoire: { "p_aeolix_solaea_1": 5, "p_aeolix_solaea_2": 4, "p_aeolix_solaea_3": 3, "u_aeolix_solaea_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_solaea", "ex_escling_solaea", "ex_escling_solaea"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_aeternavis_solaea",
    name: "Aeternavis at Full Radiance",
    description: "A Beast Solaea deck that swings hard with Lyt buffs and burns back from the Necropolis like the phoenix it is.",
    ilk: "Solaea",
    color: "#8A4B08",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_solaea_l1: 3, ex_tome_solaea_l2: 2, ex_tome_solaea_l3: 1,
      "aeternavis_solaea_beb": 5, "aeternavis_solaea_champion": 3, "aeternavis_solaea_lord": 3,
    },
    luxmoire: { "p_aeternavis_solaea_1": 5, "p_aeternavis_solaea_2": 4, "p_aeternavis_solaea_3": 3, "u_aeternavis_solaea_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_solaea", "ex_escling_solaea", "ex_escling_solaea"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_auroraeth_solaea",
    name: "Auroraeth's True Noon",
    description: "A Beast Solaea deck of team-wide Lyt auras that peaks when the meridian rose stands at zenith.",
    ilk: "Solaea",
    color: "#6B4A0F",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_solaea_l1: 3, ex_tome_solaea_l2: 2, ex_tome_solaea_l3: 1,
      "auroraeth_solaea_beb": 5, "auroraeth_solaea_champion": 3, "auroraeth_solaea_lord": 3,
    },
    luxmoire: { "p_auroraeth_solaea_1": 5, "p_auroraeth_solaea_2": 4, "p_auroraeth_solaea_3": 3, "u_auroraeth_solaea_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_solaea", "ex_escling_solaea", "ex_escling_solaea"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_calypsis_solaea",
    name: "Calypsis on Golden Water",
    description: "A Humanoid Solaea deck that pairs Lyt buffs with small Anima recoveries — sun on the sea, and the sea on your side.",
    ilk: "Solaea",
    color: "#0E5A5A",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_solaea_l1: 3, ex_tome_solaea_l2: 2, ex_tome_solaea_l3: 1,
      "calypsis_solaea_beb": 5, "calypsis_solaea_champion": 3, "calypsis_solaea_lord": 3,
    },
    luxmoire: { "p_calypsis_solaea_1": 5, "p_calypsis_solaea_2": 4, "p_calypsis_solaea_3": 3, "u_calypsis_solaea_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_solaea", "ex_escling_solaea", "ex_escling_solaea"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_carcharion_arcane",
    name: "Carcharion Wardwater",
    description: "A Beast Arcane Art deck that answers every adversary Tome and Impera with warded teeth.",
    ilk: "Arcane Art",
    color: "#123C69",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_kyriums_wardglass: 3, tome_archons_rebuke: 2, tome_great_librarys_seal: 1,
      "carcharion_arcane_beb": 5, "carcharion_arcane_champion": 3, "carcharion_arcane_lord": 3,
    },
    luxmoire: { "p_carcharion_arcane_1": 5, "p_carcharion_arcane_2": 4, "p_carcharion_arcane_3": 3, "u_carcharion_arcane_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_arcane_art", "escling_arcane_art", "escling_arcane_art"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_carnifex_darkart",
    name: "Carnifex Rotting Vespers",
    description: "A Humanoid Dark Art deck that sings blight over multiple Turns until nothing on the other side stands.",
    ilk: "Dark Art",
    color: "#4A0E12",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_darkart_l1: 3, ex_tome_darkart_l2: 2, ex_tome_darkart_l3: 1,
      "carnifex_darkart_beb": 5, "carnifex_darkart_champion": 3, "carnifex_darkart_lord": 3,
    },
    luxmoire: { "p_carnifex_darkart_1": 5, "p_carnifex_darkart_2": 4, "p_carnifex_darkart_3": 3, "u_carnifex_darkart_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_darkart", "ex_escling_darkart", "ex_escling_darkart"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_devorix_avarice",
    name: "Devorix, Hoard With Teeth",
    description: "A Beast Avarice deck that banks Khao and Souls for guaranteed, compounding payoffs.",
    ilk: "Avarice",
    color: "#5C4708",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_avarice_l1: 3, ex_tome_avarice_l2: 2, ex_tome_avarice_l3: 1,
      "devorix_avarice_beb": 5, "devorix_avarice_champion": 3, "devorix_avarice_lord": 3,
    },
    luxmoire: { "p_devorix_avarice_1": 5, "p_devorix_avarice_2": 4, "p_devorix_avarice_3": 3, "u_devorix_avarice_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_avarice", "ex_escling_avarice", "ex_escling_avarice"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_embearaea_solaea",
    name: "Embearaea Daylight Fuse",
    description: "A Beast Solaea deck of explosive Lyt spikes that pays in Anima for every festive detonation.",
    ilk: "Solaea",
    color: "#7B2D0E",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_solaea_l1: 3, ex_tome_solaea_l2: 2, ex_tome_solaea_l3: 1,
      "embearaea_solaea_beb": 5, "embearaea_solaea_champion": 3, "embearaea_solaea_lord": 3,
    },
    luxmoire: { "p_embearaea_solaea_1": 5, "p_embearaea_solaea_2": 4, "p_embearaea_solaea_3": 3, "u_embearaea_solaea_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_solaea", "ex_escling_solaea", "ex_escling_solaea"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_geulaoin_omnia",
    name: "Geulaoin's Stolen Flame",
    description: "A Beast O'mnia deck that steals Lyt and dead spells rather than destroying anything it could keep.",
    ilk: "O'mnia",
    color: "#6E1423",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_omnia_l1: 3, ex_tome_omnia_l2: 2, ex_tome_omnia_l3: 1,
      "geulaoin_omnia_beb": 5, "geulaoin_omnia_champion": 3, "geulaoin_omnia_lord": 3,
    },
    luxmoire: { "p_geulaoin_omnia_1": 5, "p_geulaoin_omnia_2": 4, "p_geulaoin_omnia_3": 3, "u_geulaoin_omnia_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_omnia", "ex_escling_omnia", "ex_escling_omnia"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_gorgxonox_darkart",
    name: "Gorgxonox Walking Blight",
    description: "A Beast Dark Art deck that grinds every adversary being down a Turn at a time, and pays its own Anima toll to do it.",
    ilk: "Dark Art",
    color: "#331D2C",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_darkart_l1: 3, ex_tome_darkart_l2: 2, ex_tome_darkart_l3: 1,
      "gorgxonox_darkart_beb": 5, "gorgxonox_darkart_champion": 3, "gorgxonox_darkart_lord": 3,
    },
    luxmoire: { "p_gorgxonox_darkart_1": 5, "p_gorgxonox_darkart_2": 4, "p_gorgxonox_darkart_3": 3, "u_gorgxonox_darkart_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_darkart", "ex_escling_darkart", "ex_escling_darkart"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_kraalok_time",
    name: "Kraalok, Given Forever",
    description: "A Beast Time deck of delayed detonations — everything it plays says soon, and means it.",
    ilk: "Time",
    color: "#0B3D2E",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_boots_wing_walking: 1, tome_omnis_corruption: 1, tome_blood_money: 1, tome_temptation_manifested: 1,
      "kraalok_time_beb": 5, "kraalok_time_champion": 3, "kraalok_time_lord": 3,
    },
    luxmoire: { "p_kraalok_time_1": 5, "p_kraalok_time_2": 4, "p_kraalok_time_3": 3, "u_kraalok_time_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_time", "escling_time", "escling_time"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_lyraei_nihil",
    name: "Lyraei's Blank Benediction",
    description: "A Humanoid N'ihil deck that heals its Warlock by gently un-writing the adversary's hand and options.",
    ilk: "N'ihil",
    color: "#3E4E5E",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_nihil_l1: 3, ex_tome_nihil_l2: 2, ex_tome_nihil_l3: 1,
      "lyraei_nihil_beb": 5, "lyraei_nihil_champion": 3, "lyraei_nihil_lord": 3,
    },
    luxmoire: { "p_lyraei_nihil_1": 5, "p_lyraei_nihil_2": 4, "p_lyraei_nihil_3": 3, "u_lyraei_nihil_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_nihil", "ex_escling_nihil", "ex_escling_nihil"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_lyrixi_time",
    name: "Lyrixi Universal Tempo",
    description: "A Beast Time deck that bends the order of Turns themselves, trading tempo for extra time on the clock.",
    ilk: "Time",
    color: "#24123D",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_boots_wing_walking: 1, tome_omnis_corruption: 1, tome_blood_money: 1, tome_temptation_manifested: 1,
      "lyrixi_time_beb": 5, "lyrixi_time_champion": 3, "lyrixi_time_lord": 3,
    },
    luxmoire: { "p_lyrixi_time_1": 5, "p_lyrixi_time_2": 4, "p_lyrixi_time_3": 3, "u_lyrixi_time_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_time", "escling_time", "escling_time"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_malumora_omnia",
    name: "Malumora's Borrowed Grimoire",
    description: "A Humanoid O'mnia deck that never casts its own spells — it lifts the adversary's, mid-casting or from their Necropolis.",
    ilk: "O'mnia",
    color: "#43122E",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_omnia_l1: 3, ex_tome_omnia_l2: 2, ex_tome_omnia_l3: 1,
      "malumora_omnia_beb": 5, "malumora_omnia_champion": 3, "malumora_omnia_lord": 3,
    },
    luxmoire: { "p_malumora_omnia_1": 5, "p_malumora_omnia_2": 4, "p_malumora_omnia_3": 3, "u_malumora_omnia_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_omnia", "ex_escling_omnia", "ex_escling_omnia"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_mygzori_time",
    name: "Mygzori Across the Loop",
    description: "A Humanoid Time deck of slow-burning faith — Anima restored on schedule and beings returned from every point of the loop.",
    ilk: "Time",
    color: "#14432F",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_boots_wing_walking: 1, tome_omnis_corruption: 1, tome_blood_money: 1, tome_temptation_manifested: 1,
      "mygzori_time_beb": 5, "mygzori_time_champion": 3, "mygzori_time_lord": 3,
    },
    luxmoire: { "p_mygzori_time_1": 5, "p_mygzori_time_2": 4, "p_mygzori_time_3": 3, "u_mygzori_time_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_time", "escling_time", "escling_time"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_noxura_gambling",
    name: "Noxura All-In on the Author",
    description: "A Humanoid Shadow Gambling deck that bets its cards, its Lyt, and finally its Anima on the writer's ending.",
    ilk: "Shadow Gambling",
    color: "#2E1A47",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_temptation_manifested: 1, tome_nevermore: 1, tome_casino_royale: 1,
      "noxura_gambling_beb": 5, "noxura_gambling_champion": 3, "noxura_gambling_lord": 3,
    },
    luxmoire: { "p_noxura_gambling_1": 5, "p_noxura_gambling_2": 4, "p_noxura_gambling_3": 3, "u_noxura_gambling_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_shadowgambling", "ex_escling_shadowgambling", "ex_escling_shadowgambling"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_rapaxi_nihil",
    name: "Rapaxi, Hunger That Erases",
    description: "A Beast N'ihil deck that removes cards from the duel entirely — no Necropolis, no record, no city.",
    ilk: "N'ihil",
    color: "#521E12",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_nihil_l1: 3, ex_tome_nihil_l2: 2, ex_tome_nihil_l3: 1,
      "rapaxi_nihil_beb": 5, "rapaxi_nihil_champion": 3, "rapaxi_nihil_lord": 3,
    },
    luxmoire: { "p_rapaxi_nihil_1": 5, "p_rapaxi_nihil_2": 4, "p_rapaxi_nihil_3": 3, "u_rapaxi_nihil_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_nihil", "ex_escling_nihil", "ex_escling_nihil"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_riven_astrae",
    name: "Astraea Starlit Rivens",
    description: "A Humanoid Astrae deck whose constellations touch every being in play, friend and foe alike.",
    ilk: "Astrae",
    color: "#1D1B4F",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_astrae_l1: 3, ex_tome_astrae_l2: 2, ex_tome_astrae_l3: 1,
      "riven_astrae_beb": 5, "riven_astrae_champion": 3, "riven_astrae_lord": 3,
    },
    luxmoire: { "p_riven_astrae_1": 5, "p_riven_astrae_2": 4, "p_riven_astrae_3": 3, "u_riven_astrae_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_astrae", "ex_escling_astrae", "ex_escling_astrae"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_rosaluna_time",
    name: "Rosaluna Bloom of Ages",
    description: "A Beast Time deck that stalls behind slow-growing Lyf until the long bloom becomes untouchable.",
    ilk: "Time",
    color: "#3A1A3F",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_boots_wing_walking: 1, tome_omnis_corruption: 1, tome_blood_money: 1, tome_temptation_manifested: 1,
      "rosaluna_time_beb": 5, "rosaluna_time_champion": 3, "rosaluna_time_lord": 3,
    },
    luxmoire: { "p_rosaluna_time_1": 5, "p_rosaluna_time_2": 4, "p_rosaluna_time_3": 3, "u_rosaluna_time_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_time", "escling_time", "escling_time"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_skuryz_gambling",
    name: "Skuryz Gutter Luck",
    description: "A Beast Shadow Gambling deck built on terrible odds and one long shot that buys the whole city.",
    ilk: "Shadow Gambling",
    color: "#3B3320",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_snake_eyes: 1, tome_gambits_lucky_cat: 1, tome_casino_royale: 1,
      "skuryz_gambling_beb": 5, "skuryz_gambling_champion": 3, "skuryz_gambling_lord": 3,
    },
    luxmoire: { "p_skuryz_gambling_1": 5, "p_skuryz_gambling_2": 4, "p_skuryz_gambling_3": 3, "u_skuryz_gambling_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_shadowgambling", "ex_escling_shadowgambling", "ex_escling_shadowgambling"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_valtorix_solaea",
    name: "Valtorix, the Radiant Remains",
    description: "A Beast Solaea deck that hits with sun-bleached Lyt and keeps riding back out of the Necropolis at noon.",
    ilk: "Solaea",
    color: "#665A10",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_solaea_l1: 3, ex_tome_solaea_l2: 2, ex_tome_solaea_l3: 1,
      "valtorix_solaea_beb": 5, "valtorix_solaea_champion": 3, "valtorix_solaea_lord": 3,
    },
    luxmoire: { "p_valtorix_solaea_1": 5, "p_valtorix_solaea_2": 4, "p_valtorix_solaea_3": 3, "u_valtorix_solaea_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_solaea", "ex_escling_solaea", "ex_escling_solaea"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_vyren_nihil",
    name: "Vyren, the Word Undone",
    description: "A Beast N'ihil deck that un-says the adversary's hand and effects one sentence at a time.",
    ilk: "N'ihil",
    color: "#1F3B33",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_nihil_l1: 3, ex_tome_nihil_l2: 2, ex_tome_nihil_l3: 1,
      "vyren_nihil_beb": 5, "vyren_nihil_champion": 3, "vyren_nihil_lord": 3,
    },
    luxmoire: { "p_vyren_nihil_1": 5, "p_vyren_nihil_2": 4, "p_vyren_nihil_3": 3, "u_vyren_nihil_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_nihil", "ex_escling_nihil", "ex_escling_nihil"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_vyren_phantom",
    name: "Vyren Among the Spirits",
    description: "A Beast Phantom deck of anima-strike serpents that slip the veil and bite the Warlock directly.",
    ilk: "Phantom",
    color: "#2F4858",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_boots_wing_walking: 1, tome_omnis_corruption: 1, tome_blood_money: 1, tome_temptation_manifested: 1,
      "vyren_phantom_beb": 5, "vyren_phantom_champion": 3, "vyren_phantom_lord": 3,
    },
    luxmoire: { "p_vyren_phantom_1": 5, "p_vyren_phantom_2": 4, "p_vyren_phantom_3": 3, "u_vyren_phantom_lord": 1, "aff_awoken": 1 },
    esclings: ["escling_phantom", "escling_phantom", "escling_phantom"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_zriidians_omnia",
    name: "Zriidians Great Conversion",
    description: "A Humanoid O'mnia deck that converts adversary beings and spells to its side instead of ruining them.",
    ilk: "O'mnia",
    color: "#123A4F",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      ex_tome_omnia_l1: 3, ex_tome_omnia_l2: 2, ex_tome_omnia_l3: 1,
      "zriidians_omnia_beb": 5, "zriidians_omnia_champion": 3, "zriidians_omnia_lord": 3,
    },
    luxmoire: { "p_zriidians_omnia_1": 5, "p_zriidians_omnia_2": 4, "p_zriidians_omnia_3": 3, "u_zriidians_omnia_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_omnia", "ex_escling_omnia", "ex_escling_omnia"],
    curios: [], prophecy: null,
  },
  {
    id: "exdeck_zylaraeon_gambling",
    name: "Zylaraeon's Contrary Book",
    description: "A Humanoid Shadow Gambling deck that takes the far side of every wager, including the writer's.",
    ilk: "Shadow Gambling",
    color: "#4B0F35",
    experimental: true,
    grimoire: {
      ...SHARED_BASE,
      tome_nevermore: 1, tome_snake_eyes: 1, tome_gambits_lucky_cat: 1,
      "zylaraeon_gambling_beb": 5, "zylaraeon_gambling_champion": 3, "zylaraeon_gambling_lord": 3,
    },
    luxmoire: { "p_zylaraeon_gambling_1": 5, "p_zylaraeon_gambling_2": 4, "p_zylaraeon_gambling_3": 3, "u_zylaraeon_gambling_lord": 1, "aff_awoken": 1 },
    esclings: ["ex_escling_shadowgambling", "ex_escling_shadowgambling", "ex_escling_shadowgambling"],
    curios: [], prophecy: null,
  },

];

// ── THE WHOLE POCKET, FLAT — what the Fauxdex renders ─────────
export const ALL_EXPERIMENTAL_CARDS = [
  ...EX_ESCENDANTS, ...EX_TOMES, ...EX_IMPERA,
  ...EX_UHLTIS, ...EX_ESCLINGS,
];

// Read-only helpers for the Fauxdex (mirrors cards.js accessors).
export const getExImperaForBeing = (id) => EX_IMPERA.filter(p => p.owner === id);
export const getExUhltiForBeing = (id) => EX_UHLTIS.find(u => u.owner === id) || null;
export const getExLine = (card) =>
  EX_ESCENDANTS.filter(e => e.school === card.school && e.ilk === card.ilk &&
    e.id.replace(/_(beb|champion|lord)$/, '') === card.id.replace(/_(beb|champion|lord)$/, ''))
    .sort((a, b) => a.level - b.level);
